import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  type AdminStore,
  type Decision,
  type Distribution,
  type DistributionStatus,
  type Invite,
  type InviteStatus,
  type PoolTag,
  type Stage,
  applicationDecision,
  dayOf,
  decisionLabels,
  decisionOf,
  decisionStyles,
  distributionStatuses,
  distributionStyles,
  emptyPoolTag,
  emptyAdmin,
  loadAdmin,
  logActivity,
  matchScore,
  ratingOf,
  ratingStyle,
  categoriesOf,
  categoryStyle,
  salesCategories,
  salaryBands,
  moneyFits,
  


  saveAdmin,
  scoreStyle,
  stageOf,
  stageStyles,
  stages,
  timeOf,
} from "@/components/site/admin";
import {
  type Application,
  type EmployerJob,
  type EmployerStore,
  emptyStore,
  initialsOf,
  loadEmployer,
  saveEmployer,
} from "@/components/site/employer";
import { Combobox, MultiCombobox } from "@/components/site/Combobox";
import type { PoolPerson } from "@/components/site/TalentPool";
import { experienceLevelOptions, lastSeenLabel, profileRecords, salesTypeOptions } from "@/components/site/candidate-access";
import { assessmentDone, type CandidateDirectoryEntry } from "@/components/site/showcase";
import { employerAccountStatus, employerCategoryOptions, type EmployerRecord } from "@/components/site/employer-crm";
import { type AutoApplyAdminCandidate, type AutoStatus, type AutoSubmission } from "@/components/site/autoapply";
import { adminLogout, getAdminSession } from "@/lib/api-client";
import {
  type CreateJobInput,
  createEmployer,
  createJob,
  deleteJob,
  fetchAdminCandidates,
  fetchApplications,
  fetchAutoApplyCandidates,
  fetchEmployers,
  fetchInvites,
  fetchJobs,
  removeAutoApplySubmission,
  runAutoApplyMatch,
  updateAutoApplySubmission,
  updateInvite as apiUpdateInvite,
  createInvite as apiCreateInvite,
  updateEmployer,
  updateJob,
  updateApplication,
  deleteApplication,
  deleteAdminCandidate,
  resetCandidatePassword,
  fetchCandidateDecisions,
  setCandidateDecision,
  fetchDistributions,
  createDistribution,
  deleteDistribution,
  sendTestEmail,
} from "@/lib/admin-api";

export const Route = createFileRoute("/admin/{-$tab}")({
  head: () => {
    const t = "Admin console | CLOSDD";
    const d =
      "Review candidates, approve roles, match salespeople to vacancies and track every candidate put forward on CLOSDD.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  beforeLoad: ({ params }) => {
    if (params.tab && !tabIds.includes(params.tab as Tab)) throw redirect({ to: "/admin/{-$tab}" });
  },
  component: Admin,
});

import { jobTags, tagGroups, toneClass } from "@/components/site/tags";
import { PaginationBar, paginate, usePagination } from "@/components/site/PaginationBar";
import { formatAdvert } from "@/components/site/jobs";
import { TagRow } from "@/components/site/TagPill";
import { Invitations } from "@/components/site/Invitations";
import { ExclusiveRoles } from "@/components/site/ExclusiveRoles";
import { TalentPool } from "@/components/site/TalentPool";
import { AutoApplyAdmin } from "@/components/site/AutoApplyAdmin";
import { CandidateAccessAdmin } from "@/components/site/CandidateAccessAdmin";
import { EmployersAdmin } from "@/components/site/EmployersAdmin";
import { JobCreateForm, compactJobInput } from "@/components/site/JobCreateForm";
import { CandidateCreateForm } from "@/components/site/CandidateCreateForm";
import { CandidateProfilePage, type ProfileRow } from "@/components/site/CandidateProfilePage";

const panel = "rounded-xl border border-[#E3E3DE] bg-white";

/** Mirrors the real admin shell (header + nav rail + panel) so the console never flashes blank — or worse, looks like it fell through to another page — while the initial API calls are in flight. */
function AdminSkeleton() {
  const bar = "animate-pulse rounded-[4px] bg-[#1C3683]";
  const card = "animate-pulse rounded-xl border border-[#E3E3DE] bg-white";
  return (
    <div className="cl flex min-h-screen flex-wrap items-stretch bg-[#F3F3F0]" aria-busy="true" aria-label="Loading">
      <aside className="flex flex-[1_1_232px] flex-col gap-7 bg-[#0B2160] px-4 py-6 lg:max-w-[260px]">
        <span className="flex min-h-11 items-center gap-2.5 px-2 text-[24px] font-extrabold tracking-[-0.03em] text-white">
          <span aria-hidden className="block h-[11px] w-[11px] rounded-full bg-[#FF5A3C]" />
          closdd
          <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#FF5A3C]">Admin</span>
        </span>
        <nav className="flex flex-col gap-1">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex min-h-11 items-center px-3">
              <div className={`h-4 w-28 ${bar}`} />
            </div>
          ))}
        </nav>
      </aside>
      <main className="min-w-0 flex-[999_1_560px] px-5 pb-14 pt-8 lg:px-10">
        <div className="mx-auto max-w-[1160px]">
          <div className="h-4 w-24 animate-pulse rounded-[4px] bg-[#E3E3DE]" />
          <div className="mt-3 h-11 w-[min(520px,100%)] animate-pulse rounded-[4px] bg-[#E3E3DE]" />
          <div className="mt-8 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`min-h-[168px] ${card}`} />
            ))}
          </div>
          <div className={`mt-6 h-72 ${card}`} />
        </div>
      </main>
    </div>
  );
}

const label = "font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#5A5A60]";
const input =
  "w-full min-h-11 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 py-2 text-[15px] text-[#0B2160] outline-none transition focus:border-[#0B2160] focus:ring-2 focus:ring-[#FF5A3C]/40";
const btn = "inline-flex min-h-11 items-center rounded-[4px] px-4 text-[15px] font-extrabold transition";
const btnGhost = `${btn} border-2 border-[#0B2160] bg-white text-[#0B2160] hover:bg-[#0B2160]/[0.05]`;
const btnSolid = `${btn} bg-[#0B2160] text-white hover:bg-[#142C75]`;

type Tab =
  | "dashboard"
  | "candidates"
  | "jobs"
  | "exclusive"
  | "invites"
  | "pool"
  | "applications"
  | "matching"
  | "autoapply"
  | "employers"
  | "access"
  | "distribution"
  | "activity"
  | "leads"
  | "payments"
  | "settings";

const tabIds: Tab[] = [
  "dashboard", "candidates", "jobs", "exclusive", "invites", "pool", "applications", "matching",
  "autoapply", "employers", "access", "distribution", "activity", "leads", "payments", "settings",
];

type CandidateRecord = {
  key: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  years: string;
  role: string;
  cvName: string;
  hasPitch: boolean;
  audioUrl?: string | undefined;
  voiceAnswers: number;
  tags: string[];
  appliedJobIds: string[];
  /** Latest of their applications and their last profile save, for newest-first ordering. */
  activeAt: string;
};

const navIcons: Record<Tab, string> = {
  leads: "",
  payments: "",
  settings: "",
  dashboard: "M4 13h7V4H4zM13 20h7v-9h-7zM4 20h7v-4H4zM13 8h7V4h-7z",
  candidates: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8",
  jobs: "M3 7h18v12H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  exclusive: "M12 3l2.6 5.4 5.9.8-4.3 4.1 1.1 5.8L12 16.4 6.7 19.1l1.1-5.8L3.5 9.2l5.9-.8z",
  invites: "M3 6h18v12H3zM3 7l9 6 9-6",
  pool: "M12 3v5M12 8l-6 4M12 8l6 4M4 12h4v4H4zM10 16h4v4h-4zM16 12h4v4h-4z",
  applications: "M8 3h8l4 4v14H4V3zM14 3v5h5",
  matching: "M4 7h5l3 10h8M4 17h5M17 4l3 3-3 3",
  autoapply: "M12 3l1.8 4.2 4.6.4-3.5 3 1 4.5L12 12.8 8.1 15.1l1-4.5-3.5-3 4.6-.4zM6 20h12",
  employers: "M3 21V8l7-4 7 4v13M9 21v-6h4v6",
  access: "M7 11V8a5 5 0 0 1 10 0M5 11h14v10H5z",
  distribution: "M4 12h10M14 8l4 4-4 4M4 6h6M4 18h6",
  activity: "M3 12h4l3 8 4-16 3 8h4",
};

function NavIcon({ tab }: { tab: Tab }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d={navIcons[tab]} />
    </svg>
  );
}

function Pill({ text, tone }: { text: string; tone: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-[3px] text-[12px] font-extrabold ${tone}`}>
      {text}
    </span>
  );
}

function Score({ value }: { value: number }) {
  return (
    <span className={`grid h-9 w-14 shrink-0 place-items-center rounded-md text-[13px] font-bold ${scoreStyle(value)}`}>
      {value}%
    </span>
  );
}

function formatDate(d: Date | null | undefined) {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function Stat({ value, label: text, accent }: { value: number; label: string; accent?: boolean }) {
  return (
    <div className={`${panel} relative overflow-hidden px-5 py-5`}>
      <span
        className={`absolute inset-y-0 left-0 w-[3px] ${accent ? "bg-tag-amber" : "bg-purple-strong/70"}`}
        aria-hidden
      />
      <p className="font-serif text-[22px] leading-none tracking-[-0.02em] text-ink">{value}</p>
      <p className={`mt-2.5 ${label}`}>{text}</p>
    </div>
  );
}

function Empty({ children }: { children: string }) {
  return <p className="px-6 py-12 text-[14px] text-ink/50">{children}</p>;
}

function Chips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: T[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition ${
            value === o
              ? "border-purple-strong bg-purple-strong text-white"
              : "border-purple-strong/15 text-ink/60 hover:border-purple-strong/30 hover:text-ink"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

/** Compact pitch player for dense admin tables. */
/**
 * Actually plays the candidate's recorded pitch via a real <audio> element —
 * this used to only ever animate a fake progress bar to a fixed "0:60" with
 * no audio source at all, regardless of `has`, so nothing was ever audible.
 * `url` is optional even when `has` is true for applications recorded
 * before AddAudioUrlToApplicationsTable backfilled it from the candidate's
 * live showcase profile — that only catches a pitch that still exists
 * there, so a since-deleted/re-recorded one can still show this state.
 */
function PitchPlayer({ has, url }: { has: boolean; url?: string | undefined }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  if (!has) {
    return (
      <div className="flex h-8 w-full max-w-[120px] items-center rounded-md border border-dashed border-purple-strong/15 px-2.5 text-[11px] text-ink/40">
        No pitch
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className="flex h-8 w-full max-w-[120px] items-center rounded-md border border-dashed border-purple-strong/15 px-2.5 text-[11px] text-ink/40"
        title="No playable recording on file for this application — ask the candidate to re-save their pitch."
      >
        Unavailable
      </div>
    );
  }

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="flex h-8 w-full max-w-[120px] items-center gap-2 rounded-lg border border-purple-strong/12 bg-[#fbfcfe] px-2">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => {
          const d = e.currentTarget.duration || 0;
          setProgress(d ? e.currentTarget.currentTime / d : 0);
        }}
      />
      <button
        type="button"
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (playing) el.pause();
          else void el.play();
        }}
        aria-label={playing ? "Pause pitch" : "Play pitch"}
        className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-purple-strong text-white"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          {playing ? <path d="M6 4h4v16H6zM14 4h4v16h-4z" /> : <path d="M7 4l13 8-13 8z" />}
        </svg>
      </button>
      <span className="h-1 flex-1 overflow-hidden rounded-full bg-ink/10">
        <span
          className="block h-full rounded-full bg-purple-strong transition-[width]"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </span>
      <span className="shrink-0 text-[10px] font-semibold text-ink/45">{duration ? fmt(duration) : "0:60"}</span>
    </div>
  );
}

function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const [admin, setAdmin] = useState<AdminStore>(emptyAdmin);
  const [employer, setEmployer] = useState<EmployerStore>(emptyStore);
  const { tab: tabParam } = Route.useParams();
  const tab: Tab = tabIds.includes(tabParam as Tab) ? (tabParam as Tab) : "dashboard";
  const setTab = (next: Tab) => {
    void navigate({ to: "/admin/{-$tab}", params: { tab: next === "dashboard" ? undefined : next } });
  };
  const [query, setQuery] = useState("");
  const [matchCandidate, setMatchCandidate] = useState<string>("");
  const [matchJob, setMatchJob] = useState<string>("");
  const [history, setHistory] = useState<{ tab: Tab; matchCandidate: string; matchJob: string }[]>([]);
  /** Jobs' "Post a job" view brings its own page header, so the shell's one steps aside. */
  const [jobFullPage, setJobFullPage] = useState(false);
  const [creatingCandidate, setCreatingCandidate] = useState(false);
  const [autoApplyCandidates, setAutoApplyCandidates] = useState<AutoApplyAdminCandidate[]>([]);
  /** Dashboard window, in days. 0 means all time. */
  const [range, setRange] = useState(7);
  /** Employers (CRM) and invites now come from the API, not localStorage —
   * separate state since they're no longer part of the AdminStore blob. */
  const [crmEmployers, setCrmEmployers] = useState<EmployerRecord[]>([]);
  /** Sidebar "Show demo data" switch. Off by default and remembered per browser, like the design. */
  const [showDemo, setShowDemo] = useState(() => {
    try {
      return window.localStorage.getItem("closddShowDemo") === "1";
    } catch {
      return false;
    }
  });
  const toggleShowDemo = () => {
    const next = !showDemo;
    try {
      window.localStorage.setItem("closddShowDemo", next ? "1" : "0");
    } catch {
      // Private mode etc. — the switch still works for this visit.
    }
    setShowDemo(next);
  };
  const [invites, setInvites] = useState<Invite[]>([]);
  /** Which candidate keys already have a distribution — powers "ready to distribute". */
  const [distributedKeys, setDistributedKeys] = useState<Set<string>>(new Set());
  /** Real candidate directory — replaces the old hardcoded salesDatabase. */
  const [candidateDirectory, setCandidateDirectory] = useState<CandidateDirectoryEntry[]>([]);

  // Defined above the effect below (and above the `!ready` early return
  // further down) because loadAdminConsole's closure needs it — a `const`
  // declared after an early return a render can take is never assigned in
  // that render's scope, which throws a "before initialization" error the
  // moment a since-registered effect from that render calls it.
  const employerName = (j?: EmployerJob) => (!j ? "Employer" : j.anonymous ? "Anonymous employer" : j.company || "Employer");

  useEffect(() => {
    let cancelled = false;

    getAdminSession().then((user) => {
      if (cancelled) return;
      if (!user) {
        navigate({ to: "/login" });
        return;
      }
      loadAdminConsole()
        .then(() => {
          if (!cancelled) setLoadError("");
        })
        .catch((err) => {
          // The session is already confirmed valid above — a failed data
          // fetch here is transient, not a logged-out state, so show a
          // retry instead of leaving the skeleton spinning forever.
          console.error("[admin] loadAdminConsole failed:", err);
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : "We couldn't load the admin console. Try again.");
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [navigate, retryCount]);

  async function loadAdminConsole() {
    const [jobs, applications, employers, fetchedInvites, candidateDecisions, distributionRows, directory, autoApply] = await Promise.all([
      fetchJobs(),
      fetchApplications(),
      fetchEmployers(),
      fetchInvites(),
      fetchCandidateDecisions(),
      fetchDistributions(),
      fetchAdminCandidates(),
      fetchAutoApplyCandidates(),
    ]);
    // jobTitle/employer aren't stored on the distribution row itself — join
    // them in from the jobs list we just fetched, same as every local
    // distribute/assign action already does.
    const jobsById = new Map(jobs.map((j) => [j.id, j] as const));
    const serverDistributionsRaw: Distribution[] = distributionRows.map((d) => ({
      ...d,
      jobTitle: jobsById.get(d.jobId)?.title ?? "",
      employer: employerName(jobsById.get(d.jobId)),
    }));
    // Every server-side distribution row is created together with the
    // application it clones (Api\Admin\Distributions::create()), so a row
    // with no matching application left is a leftover from that
    // application being deleted some other way (e.g. the "Company" column's
    // remove, before it also cleaned up this row) — stale, and worth
    // dropping here too so counts (like the "Assign to client role"
    // dropdown's) self-heal on reload instead of staying inflated forever.
    const appPairs = new Set(applications.map((a) => `${(a.email || a.name).toLowerCase()}::${a.jobId}`));
    const serverDistributions = serverDistributionsRaw.filter((d) =>
      appPairs.has(`${d.candidateKey}::${d.jobId}`),
    );
    // jobs/applications/candidates decisions and distributions now come from
    // the API, not the localStorage blob — everything else on AdminStore
    // (stages, pool, ratings, notes, saved, activity, invites) still does,
    // since those tabs aren't reachable from the admin nav.
    const local = loadAdmin();
    // "Assign to client role" (assignMany) persists to the server, but
    // "Distribute" (talent pool) and "Put forward" (candidates) still only
    // write to this local blob — so the server list is authoritative where
    // it has a row, and any local-only rows (candidateKey+jobId the server
    // doesn't know about) are kept alongside it rather than dropped, or
    // those two actions would stop surviving a page reload.
    const serverKeys = new Set(serverDistributions.map((d) => `${d.candidateKey}::${d.jobId}`));
    const localOnly = local.distributions.filter((d) => !serverKeys.has(`${d.candidateKey}::${d.jobId}`));
    const distributions = [...serverDistributions, ...localOnly];
    setAdmin({
      ...local,
      jobs: Object.fromEntries(jobs.map((j) => [j.id, j.decision ?? "Pending"])),
      applications: Object.fromEntries(applications.map((a) => [a.id, a.decision ?? "Pending"])),
      candidates: candidateDecisions,
      distributions,
    });
    setDistributedKeys(new Set(distributions.map((d) => d.candidateKey)));
    setEmployer({ ...emptyStore, jobs, applications });
    setCrmEmployers(employers);
    setInvites(fetchedInvites);
    setCandidateDirectory(directory);
    setAutoApplyCandidates(autoApply);
    setReady(true);
  }

  const update = (patch: Partial<AdminStore>) => setAdmin(saveAdmin(patch));
  /** Legacy localStorage path — only the unreachable "exclusive" tab still uses this. */
  const updateEmployerLegacy = (patch: Partial<EmployerStore>) => saveEmployer(patch, "sourced");
  const act = (message: string, patch: Partial<AdminStore> = {}) =>
    setAdmin(saveAdmin({ ...patch, activity: logActivity({ ...loadAdmin(), ...patch }, message) }));

  const jobById = useMemo(
    () => new Map(employer.jobs.map((j) => [j.id, j] as const)),
    [employer.jobs],
  );

  const candidates: CandidateRecord[] = useMemo(() => {
    const map = new Map<string, CandidateRecord>();
    for (const a of employer.applications) {
      const key = (a.email || a.name).toLowerCase();
      const job = jobById.get(a.jobId);
      const existing = map.get(key);
      if (existing) {
        existing.appliedJobIds.push(a.jobId);
        existing.voiceAnswers = Math.max(existing.voiceAnswers, a.voiceAnswers);
        existing.hasPitch = existing.hasPitch || a.hasPitch;
        existing.audioUrl = existing.audioUrl || a.audioUrl;
        if ((a.appliedAt || "") > existing.activeAt) existing.activeAt = a.appliedAt || "";
        for (const t of job?.tags ?? []) if (!existing.tags.includes(t)) existing.tags.push(t);
        continue;
      }
      map.set(key, {
        key,
        name: a.name,
        email: a.email,
        phone: a.phone,
        location: a.location,
        years: a.years,
        role: job?.title ?? "Sales",
        cvName: a.cvName,
        hasPitch: a.hasPitch,
        audioUrl: a.audioUrl,
        voiceAnswers: a.voiceAnswers,
        tags: [...(job?.tags ?? [])],
        appliedJobIds: [a.jobId],
        activeAt: a.appliedAt || "",
      });
    }
    // Accounts with no application yet (a fresh signup, or a profile created
    // here in admin) only exist in the directory. Without this they never
    // showed up in Candidates until they applied for a role.
    for (const d of candidateDirectory) {
      const key = (d.email || d.name).toLowerCase();
      const sc = d.showcase;
      const existing = map.get(key);
      if (existing) {
        existing.cvName = existing.cvName || d.cvName || "";
        existing.hasPitch = existing.hasPitch || Boolean(sc.audioUrl);
        existing.audioUrl = existing.audioUrl || sc.audioUrl || undefined;
        if ((d.savedAt || "") > existing.activeAt) existing.activeAt = d.savedAt || "";
        continue;
      }
      map.set(key, {
        key,
        name: d.name,
        email: d.email,
        phone: d.phone,
        location: sc.location || d.location || "",
        years: sc.yearsSelling,
        role: sc.targetRole || d.role || "Sales",
        cvName: d.cvName ?? "",
        hasPitch: Boolean(sc.audioUrl),
        audioUrl: sc.audioUrl || undefined,
        voiceAnswers: assessmentDone(sc),
        tags: [...sc.skillTags],
        appliedJobIds: [],
        activeAt: d.savedAt || "",
      });
    }
    // Newest first, so a fresh signup or a profile just made here is on page 1.
    const time = (iso: string) => new Date(iso).getTime() || 0;
    return [...map.values()].sort((a, b) => time(b.activeAt) - time(a.activeAt));
  }, [employer.applications, jobById, candidateDirectory]);

  const candidateByKey = useMemo(() => new Map(candidates.map((c) => [c.key, c] as const)), [candidates]);

  if (loadError) {
    return (
      <div className="cl grid min-h-screen place-items-center bg-[#F3F3F0] px-6">
        <div className="grid max-w-[420px] gap-4 text-center">
          <p className="text-[17px] text-[#3A3A40]">{loadError}</p>
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="mx-auto min-h-11 rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready) return <AdminSkeleton />;

  const q = query.trim().toLowerCase();

  // Demo/sample data never counts towards any number here. A job is demo when
  // it's flagged itself or sits under an employer flagged demo; lists only
  // show demo rows while Show demo data is on.
  const demoEmployers = crmEmployers.filter((e) => e.demo);
  const demoCompanyNames = new Set(demoEmployers.map((e) => e.company.trim().toLowerCase()));
  const demoEmployerJobIds = new Set(demoEmployers.flatMap((e) => e.jobIds));
  const demoJobIds = new Set(
    employer.jobs
      .filter((j) => j.demo || demoEmployerJobIds.has(j.id) || demoCompanyNames.has((j.company || "").trim().toLowerCase()))
      .map((j) => j.id),
  );
  const realApplications = employer.applications.filter((a) => !demoJobIds.has(a.jobId));
  // A candidate profile's own demo flag only ever meant "made in admin" (now
  // createdByAdmin), so it doesn't hide anyone. Only applicants whose every
  // application is to a demo role count as demo.
  const realCandidates = candidates.filter(
    (c) => !c.appliedJobIds.length || c.appliedJobIds.some((id) => !demoJobIds.has(id)),
  );
  const realEmployers = crmEmployers.filter((e) => !e.demo);

  const exclusiveJobs = employer.jobs.filter((j) => j.exclusive);
  const boardJobs = employer.jobs.filter((j) => !j.exclusive);
  const realBoardJobs = boardJobs.filter((j) => !demoJobIds.has(j.id));
  const pendingJobs = employer.jobs.filter((j) => !demoJobIds.has(j.id) && decisionOf(admin.jobs, j.id) === "Pending").length;
  const pendingApps = realApplications.filter(
    (a) => applicationDecision(admin, a.id, (a.email || a.name).toLowerCase()) === "Pending",
  ).length;
  const pendingCandidates = realCandidates.filter((c) => decisionOf(admin.candidates, c.key) === "Pending").length;
  const approvedCandidates = (showDemo ? candidates : realCandidates).filter((c) => decisionOf(admin.candidates, c.key) === "Approved");
  const readyToDistribute = realCandidates.filter(
    (c) => decisionOf(admin.candidates, c.key) === "Approved" && !distributedKeys.has(c.key),
  ).length;

  // Dashboard figures. "New" means inside the selected window.
  const weekAgo = range ? Date.now() - range * 86_400_000 : 0;
  const isNew = (iso?: string | null) => Boolean(iso && new Date(iso).getTime() >= weekAgo);
  const approvedJobs = realBoardJobs.filter((j) => decisionOf(admin.jobs, j.id) === "Approved").length;
  const rejectedJobs = realBoardJobs.filter((j) => decisionOf(admin.jobs, j.id) === "Rejected").length;
  const newJobs = realBoardJobs.filter((j) => isNew(j.createdAt)).length;
  const liveJobs = realBoardJobs.filter((j) => !j.status || j.status === "Live").length;
  const newApplications = realApplications.filter((a) => isNew(a.appliedAt)).length;
  const approvedApplications = realApplications.filter(
    (a) => applicationDecision(admin, a.id, (a.email || a.name).toLowerCase()) === "Approved",
  ).length;
  const rejectedApplications = realApplications.filter(
    (a) => applicationDecision(admin, a.id, (a.email || a.name).toLowerCase()) === "Rejected",
  ).length;
  const newCandidates = realCandidates.filter((c) =>
    realApplications.some((a) => (a.email || a.name).toLowerCase() === c.key && isNew(a.appliedAt)),
  ).length;
  const employerCount = realEmployers.length;
  const employerStatuses = realEmployers.map((e) => ({
    hasAccount: Boolean(e.accountCreatedAt),
    isActive: Boolean(e.loggedIn),
  }));
  const employersSignedUp = employerStatuses.filter((s) => s.hasAccount).length;
  const employersActive = employerStatuses.filter((s) => s.isActive).length;
  const employersProspect = employerCount - employersSignedUp;
  const employersHiring = new Set(
    realBoardJobs
      .filter((j) => !j.status || j.status === "Live")
      .map((j) => (j.company || "").trim().toLowerCase())
      .filter(Boolean),
  ).size;

  const rangeWord = range === 0 ? "all time" : range === 7 ? "this week" : `in ${range} days`;

  const employersToFollow = crmEmployers.filter((e) => {
    if (e.demo) return false;
    const added = new Date(e.addedAt).getTime();
    const addedDays = Number.isNaN(added) ? 0 : Math.floor((Date.now() - added) / 86_400_000);
    return !e.contactedAt || (!e.accountCreatedAt && addedDays > 3);
  }).length;

  const dashboardCards: DashboardCard[] = [
    {
      value: pendingJobs,
      title: "Jobs to approve",
      hint: "Check, edit, then approve or reject",
      tone: "navy",
      links: [{ label: "Review jobs →", tab: "jobs" }],
    },
    {
      value: employersToFollow,
      title: "Employers to follow up",
      hint: "Need an invitation, a follow-up after 3 days, or re-engaging",
      tone: "coral",
      links: [{ label: "See who to chase →", tab: "employers" }],
    },
    {
      value: pendingApps + pendingCandidates,
      title: "Candidates to review",
      hint: "Direct applications and new profiles waiting for a decision",
      tone: "plain",
      links: [
        { label: `${pendingApps} application${pendingApps === 1 ? "" : "s"} →`, tab: "applications" },
        { label: `${pendingCandidates} profile${pendingCandidates === 1 ? "" : "s"} →`, tab: "candidates" },
      ],
    },
    {
      value: 0,
      title: "New leads to pick up",
      hint: "Certified hire, audits, Academy calls and contact forms with no owner yet",
      tone: "plain",
      links: [{ label: "Open leads →", tab: "leads" }],
    },
  ];

  const jobTitle = (id: string) => jobById.get(id)?.title ?? "Role removed";

  /** Contact email of the company that posted a role, so admins can search by it. */
  const employerEmail = (j: EmployerJob) => {
    const company = (j.company || "").trim().toLowerCase();
    const record = crmEmployers.find((e) => e.company.trim().toLowerCase() === company);
    return record?.email ?? "";
  };

  /**
   * Live edit of a role from the jobs manager. JobEditor below batches its
   * own keystrokes and only calls this once typing pauses, so it's safe to
   * apply the patch to local state and persist it right away here.
   */
  const editJob = (id: string, patch: Partial<EmployerJob>) => {
    setEmployer((prev) => ({ ...prev, jobs: prev.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)) }));
    updateJob(id, patch).then((updated) => {
      setEmployer((prev) => ({ ...prev, jobs: prev.jobs.map((j) => (j.id === id ? updated : j)) }));
    });
  };

  /** Creates one role (from the manual form or one row of a CSV import) and wires it into local state. */
  const createJobAdmin = async (input: CreateJobInput) => {
    const job = await createJob(input);
    setEmployer((prev) => ({ ...prev, jobs: [job, ...prev.jobs] }));
    act(`Created role ${job.title}`, { jobs: { ...admin.jobs, [job.id]: job.decision ?? "Pending" } });
    return job;
  };

  const removeJob = (job: EmployerJob) => {
    deleteJob(job.id).then(() => {
      const jobs = { ...admin.jobs };
      delete jobs[job.id];
      act(`Removed role ${job.title}`, { jobs });
      setEmployer((prev) => ({
        ...prev,
        jobs: prev.jobs.filter((j) => j.id !== job.id),
        applications: prev.applications.filter((a) => a.jobId !== job.id),
      }));
    });
  };

  /** Deletes a batch of roles in one go, then updates local state once every request has settled. */
  const removeJobs = async (jobsToRemove: EmployerJob[]) => {
    const results = await Promise.allSettled(jobsToRemove.map((j) => deleteJob(j.id)));
    const removedIds = new Set(jobsToRemove.filter((_, i) => results[i]?.status === "fulfilled").map((j) => j.id));
    if (!removedIds.size) return removedIds;
    const jobs = { ...admin.jobs };
    for (const id of removedIds) delete jobs[id];
    const removedTitles = jobsToRemove.filter((j) => removedIds.has(j.id)).map((j) => j.title);
    act(`Removed ${removedIds.size} role${removedIds.size === 1 ? "" : "s"}: ${removedTitles.join(", ")}`, { jobs });
    setEmployer((prev) => ({
      ...prev,
      jobs: prev.jobs.filter((j) => !removedIds.has(j.id)),
      applications: prev.applications.filter((a) => !removedIds.has(a.jobId)),
    }));
    return removedIds;
  };

  /** Approves or rejects a batch of roles in one go, persisting each to the API then updating local state once. */
  const decideJobs = async (jobsToDecide: EmployerJob[], d: Decision) => {
    if (!jobsToDecide.length) return;
    const results = await Promise.allSettled(jobsToDecide.map((j) => updateJob(j.id, { decision: d })));
    const decidedIds = new Set(jobsToDecide.filter((_, i) => results[i]?.status === "fulfilled").map((j) => j.id));
    if (!decidedIds.size) return;
    const jobs = { ...admin.jobs };
    for (const id of decidedIds) jobs[id] = d;
    const decidedTitles = jobsToDecide.filter((j) => decidedIds.has(j.id)).map((j) => j.title);
    const verb = d === "Approved" ? "Approved" : d === "Rejected" ? "Rejected" : "Reset";
    act(`${verb} ${decidedIds.size} role${decidedIds.size === 1 ? "" : "s"}: ${decidedTitles.join(", ")}`, { jobs });
  };


  // Funnel applicants: everyone who applied to an invite only role. They get
  // graded here, then split across the campaigns that fit their money.
  const exclusiveIds = new Set(exclusiveJobs.map((j) => j.id));
  const poolPeople: PoolPerson[] = employer.applications
    .filter((a) => exclusiveIds.has(a.jobId))
    .map((a) => {
      const key = (a.email || a.name).toLowerCase();
      return {
        key,
        name: a.name,
        email: a.email,
        location: a.location,
        years: a.years,
        funnelJobId: a.jobId,
        funnelTitle: jobTitle(a.jobId),
        hasPitch: a.hasPitch,
        voiceAnswers: a.voiceAnswers,
        sentTo: admin.distributions.filter((d) => d.candidateKey === key).map((d) => d.jobTitle),
      };
    })
    .filter((p, i, all) => all.findIndex((x) => x.key === p.key) === i);

  const unsortedPool = poolPeople.filter((p) => (admin.pool[p.key] ?? emptyPoolTag).grade === "Unsorted").length;

  const tagPool = (key: string, patch: Partial<PoolTag>) =>
    update({ pool: { ...admin.pool, [key]: { ...emptyPoolTag, ...admin.pool[key], ...patch } } });

  /** Send a batch of funnel people to one campaign in a single move. */
  const distributeMany = (keys: string[], jobId: string) => {
    const now = new Date().toISOString();
    const created: Distribution[] = keys
      .filter((key) => !admin.distributions.some((d) => d.candidateKey === key && d.jobId === jobId))
      .map((key) => ({
        id: `dist-${key}-${jobId}`,
        candidateKey: key,
        candidateName: poolPeople.find((p) => p.key === key)?.name ?? key,
        jobId,
        jobTitle: jobTitle(jobId),
        employer: employerName(jobById.get(jobId)),
        status: "Sent" as DistributionStatus,
        sentAt: now,
      }));
    if (!created.length) return;
    const stagePatch = { ...admin.stages };
    for (const d of created) stagePatch[d.candidateKey] = "Put forward";
    act(
      `Distributed ${created.length} ${created.length === 1 ? "person" : "people"} from the talent pool to ${jobTitle(jobId)}`,
      { distributions: [...created, ...admin.distributions], stages: stagePatch },
    );
  };

  const decideCandidate = (c: CandidateRecord, d: Decision) => {
    setCandidateDecision(c.key, d);
    const stagePatch: Record<string, Stage> = {
      ...admin.stages,
      [c.key]: d === "Approved" ? "Approved" : d === "Rejected" ? "Not approved" : "New",
    };
    act(`${d === "Approved" ? "Approved" : d === "Rejected" ? "Did not approve" : "Reset"} candidate ${c.name}`, {
      candidates: { ...admin.candidates, [c.key]: d },
      stages: stagePatch,
    });
  };

  /**
   * Permanently removes a candidate. With an account, the API deletes the
   * login, profile, applications and put-forwards in one go; an applicant who
   * never made an account only exists as applications, so those are deleted.
   */
  const deleteCandidate = async (c: CandidateRecord): Promise<boolean> => {
    if (!window.confirm(`Delete ${c.name}? This permanently removes their account, profile, pitch and applications. This cannot be undone.`)) {
      return false;
    }
    const entry = candidateDirectory.find((d) => (d.email || d.name).toLowerCase() === c.key);
    const apps = employer.applications.filter((a) => appKeyOf(a) === c.key);
    try {
      if (entry) await deleteAdminCandidate(entry.id);
      else
        await Promise.all([
          ...apps.map((a) => deleteApplication(a.id)),
          ...admin.distributions.filter((d) => d.candidateKey === c.key).map((d) => deleteDistribution(d.candidateKey, d.jobId)),
        ]);
    } catch (err) {
      window.alert(`Couldn't delete ${c.name}: ${err instanceof Error ? err.message : "try again."}`);
      return false;
    }
    setEmployer((prev) => ({ ...prev, applications: prev.applications.filter((a) => appKeyOf(a) !== c.key) }));
    if (entry) setCandidateDirectory((prev) => prev.filter((d) => d.id !== entry.id));
    act(`Deleted candidate ${c.name}`, {
      distributions: admin.distributions.filter((d) => d.candidateKey !== c.key),
    });
    return true;
  };

  const putForward = (c: CandidateRecord, jobIds: string[]) => {
    const now = new Date().toISOString();
    const created: Distribution[] = jobIds
      .filter((id) => !admin.distributions.some((d) => d.candidateKey === c.key && d.jobId === id))
      .map((id) => ({
        id: `dist-${c.key}-${id}`,
        candidateKey: c.key,
        candidateName: c.name,
        jobId: id,
        jobTitle: jobTitle(id),
        employer: employerName(jobById.get(id)),
        status: "Sent" as DistributionStatus,
        sentAt: now,
      }));
    if (!created.length) return;
    act(
      `Put ${c.name} forward to ${created.map((d) => `${d.jobTitle} at ${d.employer}`).join(", ")}`,
      {
        distributions: [...created, ...admin.distributions],
        stages: { ...admin.stages, [c.key]: "Put forward" },
      },
    );
  };

  /**
   * Assign a batch of reviewed candidates onto any client role in one move.
   * Each assignment is a real POST — it clones the candidate's most recent
   * application onto the target job, pre-approved, so it's an application
   * the employer actually sees, not just local bookkeeping.
   */
  const assignMany = async (list: { key: string; name: string }[], jobIds: string[]) => {
    const pairs = jobIds.flatMap((jobId) =>
      list
        .filter((p) => !admin.distributions.some((d) => d.candidateKey === p.key && d.jobId === jobId))
        .map((p) => ({ ...p, jobId })),
    );
    if (!pairs.length) return;

    const now = new Date().toISOString();
    const created: Distribution[] = [];
    const newApps: Application[] = [];
    const failed: string[] = [];

    for (const p of pairs) {
      try {
        const app = await createDistribution({ candidateKey: p.key, candidateName: p.name, jobId: p.jobId });
        newApps.push(app);
        created.push({
          id: `dist-${p.key}-${p.jobId}`,
          candidateKey: p.key,
          candidateName: p.name,
          jobId: p.jobId,
          jobTitle: jobTitle(p.jobId),
          employer: employerName(jobById.get(p.jobId)),
          status: "Sent" as DistributionStatus,
          sentAt: now,
        });
      } catch {
        failed.push(`${p.name} → ${jobTitle(p.jobId)}`);
      }
    }

    if (newApps.length) {
      setEmployer((prev) => ({ ...prev, applications: [...newApps, ...prev.applications] }));
    }
    if (created.length) {
      const stages = { ...admin.stages };
      for (const d of created) stages[d.candidateKey] = "Put forward";
      const roleLabel =
        jobIds.length === 1
          ? `${jobTitle(jobIds[0]!)} at ${employerName(jobById.get(jobIds[0]!))}`
          : `${jobIds.length} roles`;
      act(
        `Assigned ${list.length} candidate${list.length === 1 ? "" : "s"} to ${roleLabel}`,
        { distributions: [...created, ...admin.distributions], stages },
      );
    }
    if (failed.length) {
      window.alert(`Could not assign: ${failed.join(", ")}. They may have no application on file to distribute from.`);
    }
  };

  /**
   * "Unassign" — reverses one candidate/role assignment made via
   * Distribution. The application it may have created is left in place.
   * Some distributions (e.g. from "Put forward") only ever existed in
   * local admin state, never as a real row server-side — the local
   * removal below still applies even if the server has nothing to delete,
   * so the button always does what it visibly promises.
   */
  /** Drops any applications the backend deleted (assignment-created, never a real self-application) from already-loaded state. */
  const dropRemovedApplications = (ids: string[]) => {
    if (!ids.length) return;
    const removed = new Set(ids);
    setEmployer((prev) => ({ ...prev, applications: prev.applications.filter((a) => !removed.has(a.id)) }));
  };

  /**
   * Explicit "sever this candidate from this employer" action, usable on
   * any application whether or not it has a distributions row. The
   * Distribution view's Company column asks for confirmation and passes
   * the exact application id(s) to delete; this runs the deletes and drops
   * them from already-loaded state.
   *
   * The backend also prunes any distributions row for the same
   * candidate+role (Applications::delete()) — mirrored here so the
   * "Assign to client role" dropdown's count updates immediately instead
   * of only on the next full reload. Without this, a candidate removed
   * this way (rather than via the "Sent" column's own Unassign) kept
   * counting as assigned forever, since nothing else here ever re-checked
   * distributions rows against whether their application still exists.
   */
  const removeApplications = (ids: string[]) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    const pairs = new Set(
      employer.applications
        .filter((a) => idSet.has(a.id))
        .map((a) => `${(a.email || a.name).toLowerCase()}::${a.jobId}`),
    );
    Promise.allSettled(ids.map((id) => deleteApplication(id))).then(() => {
      dropRemovedApplications(ids);
      if (pairs.size) {
        update({
          distributions: admin.distributions.filter((d) => !pairs.has(`${d.candidateKey}::${d.jobId}`)),
        });
      }
    });
  };

  const unassignDistribution = (d: Distribution) => {
    const removeLocally = (removedApplicationIds: string[] = []) => {
      act(`Unassigned ${d.candidateName} from ${d.jobTitle} at ${d.employer}`, {
        // Match by candidateKey+jobId, not just this row's id — a stale
        // local-only row can end up alongside a server-backed one for the
        // same candidate+role (different ids, same pair), and leaving
        // either behind keeps that candidate counted as assigned in the
        // "Assign to client role" dropdown even after this unassign.
        distributions: admin.distributions.filter(
          (x) => !(x.candidateKey === d.candidateKey && x.jobId === d.jobId),
        ),
      });
      dropRemovedApplications(removedApplicationIds);
    };
    deleteDistribution(d.candidateKey, d.jobId)
      .then((r) => removeLocally(r.removedApplicationIds))
      .catch(() => removeLocally());
  };

  /**
   * Bulk "Unassign" — removes existing role assignments for each of the
   * given candidates. When `company` is given (the Distribution list's
   * active company filter), only that company's assignments are removed —
   * a candidate assigned to several roles stays assigned to the others.
   * "All" (or omitted) removes every assignment, same as before.
   */
  const unassignManyDistributions = async (candidateKeys: string[], company?: string) => {
    const keys = new Set(candidateKeys);
    const toRemove = admin.distributions.filter(
      (d) => keys.has(d.candidateKey) && (!company || company === "All" || d.employer === company),
    );
    if (!toRemove.length) return;

    const results = await Promise.allSettled(toRemove.map((d) => deleteDistribution(d.candidateKey, d.jobId)));

    const removedIds = new Set(toRemove.map((d) => d.id));
    const scope = company && company !== "All" ? ` from ${company}` : "";
    act(
      `Unassigned ${toRemove.length} role assignment${toRemove.length === 1 ? "" : "s"} for ${keys.size} candidate${keys.size === 1 ? "" : "s"}${scope}`,
      { distributions: admin.distributions.filter((x) => !removedIds.has(x.id)) },
    );
    dropRemovedApplications(
      results.flatMap((r) => (r.status === "fulfilled" ? r.value.removedApplicationIds : [])),
    );
  };

  /**
   * Prunes jobs deleted elsewhere (e.g. an employer delete cascade on the
   * Employers tab) out of this page's own state — Jobs, Applications and
   * Distribution all read from employer.jobs/employer.applications/
   * admin.distributions, which don't otherwise know those roles are gone
   * until the next full reload.
   */
  const removeJobsEverywhere = (jobIds: string[]) => {
    const ids = new Set(jobIds);
    setEmployer((prev) => ({
      ...prev,
      jobs: prev.jobs.filter((j) => !ids.has(j.id)),
      applications: prev.applications.filter((a) => !ids.has(a.jobId)),
    }));
    update({ distributions: admin.distributions.filter((d) => !ids.has(d.jobId)) });
  };


  const setDistributionStatus = (id: string, status: DistributionStatus) => {
    const d = admin.distributions.find((x) => x.id === id);
    if (!d) return;
    const stagePatch = { ...admin.stages };
    if (status === "Interview") stagePatch[d.candidateKey] = "Interviewed";
    if (status === "Hired") stagePatch[d.candidateKey] = "Hired";
    act(`${d.candidateName} marked ${status.toLowerCase()} for ${d.jobTitle} at ${d.employer}`, {
      distributions: admin.distributions.map((x) => (x.id === id ? { ...x, status } : x)),
      stages: stagePatch,
    });
  };

  const pushHistory = () => setHistory((h) => [...h.slice(-9), { tab, matchCandidate, matchJob }]);

  const goTab = (next: Tab, opts?: { candidate?: string; job?: string }) => {
    pushHistory();
    setMatchCandidate(opts?.candidate ?? "");
    setMatchJob(opts?.job ?? "");
    setTab(next);
    setQuery("");
  };

  const goBack = () => {
    setHistory((h) => {
      const prev = h[h.length - 1];
      if (!prev) return h;
      setTab(prev.tab);
      setMatchCandidate(prev.matchCandidate);
      setMatchJob(prev.matchJob);
      setQuery("");
      return h.slice(0, -1);
    });
  };

  const openMatchForCandidate = (key: string) => {
    goTab("matching", { candidate: key });
  };

  const autoPending = autoApplyCandidates.reduce(
    (n, c) => n + c.submissions.filter((s) => s.status === "Pending Review").length,
    0,
  );

  const patchAutoSubmission = (id: string, patch: Partial<AutoSubmission>) => {
    setAutoApplyCandidates((prev) =>
      prev.map((c) => ({ ...c, submissions: c.submissions.map((s) => (s.id === id ? { ...s, ...patch } : s)) })),
    );
  };

  const setAutoStatus = (s: AutoSubmission, status: AutoStatus) => {
    patchAutoSubmission(s.id, { status, submittedAt: status === "Submitted" ? new Date().toISOString() : (s.submittedAt ?? null) });
    void updateAutoApplySubmission(s.id, status);
    act(`${status} for ${s.jobTitle} at ${s.company} (auto apply)`);
  };

  const matchAuto = async (key: string) => {
    const fresh = await runAutoApplyMatch(key);
    if (fresh.length) {
      setAutoApplyCandidates((prev) => prev.map((c) => (c.key === key ? { ...c, submissions: [...fresh, ...c.submissions] } : c)));
      act(`Matched a candidate to ${fresh.length} role${fresh.length === 1 ? "" : "s"} (auto apply)`);
    }
  };

  const removeAuto = (s: AutoSubmission) => {
    setAutoApplyCandidates((prev) => prev.map((c) => ({ ...c, submissions: c.submissions.filter((x) => x.id !== s.id) })));
    void removeAutoApplySubmission(s.id);
  };
  const openMatchForJob = (id: string) => {
    goTab("matching", { job: id });
  };

  const navLabels: Record<string, string> = {
    dashboard: "Dashboard",
    invites: "Invitations",
    employers: "Employers",
    applications: "Applications",
    jobs: "Jobs",
    access: "Candidate access",
    matching: "Matching",
    candidates: "Candidates",
    distribution: "Distribution",
    autoapply: "Auto apply",
    activity: "Activity log",
  };


  const navItems: { id: Tab; label: string; count: number; tone?: "coral" | "navy"; divider?: boolean }[] = [
    { id: "dashboard", label: "Dashboard", count: 0 },
    { id: "jobs", label: "Jobs", count: pendingJobs, tone: "coral" },
    { id: "applications", label: "Applications", count: 0 },
    { id: "candidates", label: "Candidates", count: 0 },
    { id: "employers", label: "Employers", count: realEmployers.length, tone: "navy" },
    { id: "access", label: "Database access", count: 0 },
    { id: "leads", label: "Leads", count: 0, tone: "coral", divider: true },
    { id: "payments", label: "Payments", count: 0 },
    { id: "activity", label: "Audit log", count: 0 },
  ];

  const pageHeadlines: Partial<Record<Tab, string>> = {
    jobs: "Every role on Closdd.",
    applications: "Review direct applications.",
    candidates: `${realCandidates.length} salespeople on Closdd.`,
    employers: "Real clients, first contact to paying.",
    access: "Database access",
    activity: "Every admin action, in order.",
    leads: "Leads and audit bookings.",
    payments: "Payments and refunds.",
    settings: "Settings.",
    invites: "Invitations",
  };

  const titles: Record<Tab, { h: string; p: string; search: string }> = {
    dashboard: { h: "Today", p: "Everything waiting for you this morning.", search: "" },
    candidates: {
      h: "Candidates",
      p: "Filter the whole pool, tick the people you want and put them forward to any client role. They then show up in Applications for that role.",
      search: "Search name, role or location",
    },
    jobs: { h: "Jobs", p: "Jobs created by Closdd go live straight away. Jobs employers post wait here for approval.", search: "Search role, ref or company" },
    exclusive: {
      h: "Exclusive roles",
      p: "Roles you run yourself. They never appear on the job board: you invite the people you want to apply.",
      search: "Search role, company or ref",
    },
    invites: {
      h: "Invitations",
      p: "Invite a salesperson to apply for an existing CLOSDD role. CLOSDD creates the invitation and the secure link, then tracks it.",
      search: "Search name or role",
    },
    pool: {
      h: "Talent pool",
      p: "Everyone who applied to your funnel roles. Grade them, note what they sell and what they want, then split them across the campaigns that fit.",
      search: "Search name, location or experience",
    },
    applications: { h: "Applications", p: "People who applied directly to a role. Hear the pitch, then approve or reject. Employers only ever see approved candidates; rejected stays internal and the candidate is never told.", search: "Search name or role" },
    matching: { h: "Matching", p: "Match a candidate to roles, or a role to candidates, then put forward.", search: "" },
    autoapply: {
      h: "Auto apply",
      p: "Candidates who gave CLOSDD permission to apply for them. Review each match, then submit.",
      search: "Search candidate, role or location",
    },
    employers: {
      h: "Employers",
      p: "Stages update themselves: Account created → Invited when you email the invitation → Followed up when you email them again → Active when they set a password → Paid when they unlock a role. 30 days with no activity moves them to Inactive. Each row tells you the one thing to do next. Click an employer to open their page. Demo employers only show when Show demo data is on, and never count towards these numbers.",
      search: "",
    },
    access: {
      h: "Database access",
      p: "Choose which employers can browse live candidate profiles, and approve the candidate details they request.",
      search: "Search name, sector, category or location",
    },

    distribution: { h: "Distribution", p: "Who has been put forward where, and what happened next.", search: "Search candidate, role or employer" },
    leads: {
      h: "Leads",
      p: "Certified hire, audits, Academy calls and contact forms, with an owner on each.",
      search: "",
    },
    payments: {
      h: "Payments",
      p: "Unlocks, refunds and the money-back guarantee, role by role.",
      search: "",
    },
    settings: {
      h: "Settings",
      p: "Admin tools for the console.",
      search: "",
    },
    activity: {
      h: "Audit log",
      p: "Approvals, rejections, edits, put forward and remove, invitations, refunds, ratings and badges. Entries are written automatically and can't be edited or deleted.",
      search: "Search the log",
    },
  };

  /* ------------------------------------------------------------------ *
   * Dashboard as its own system: every row below carries the detail and
   * the actions an admin needs, so nothing has to be opened elsewhere.
   * ------------------------------------------------------------------ */
  const profiles = profileRecords(candidateDirectory);
  const inWindow = (days: number) => (range === 0 ? true : days <= range);
  const dayText = (iso?: string | null) => {
    if (!iso) return "Not recorded";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return String(iso);
    return lastSeenLabel(Math.floor((Date.now() - t) / 86_400_000));
  };
  /** Exact stamp, e.g. "12 Aug 2026, 09:35". */
  const stampText = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const daysSince = (iso?: string | null) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
  };

  const decideApplication = (a: Application, d: Decision) => {
    const key = appKeyOf(a);
    updateApplication(a.id, { decision: d });
    setCandidateDecision(key, d);
    act(`${d === "Approved" ? "Approved" : d === "Rejected" ? "Marked not approved" : "Reset"}: ${a.name} for ${jobTitle(a.jobId)}`, {
      applications: { ...admin.applications, [a.id]: d },
      candidates: { ...admin.candidates, [key]: d },
      stages: { ...admin.stages, [key]: d === "Approved" ? "Approved" : d === "Rejected" ? "Not approved" : "New" },
    });
  };

  const rateKey = (key: string, name: string, n: number) => {
    const ratings = { ...admin.ratings };
    if (n) ratings[key] = n;
    else delete ratings[key];
    act(n ? `Rated ${name} ${n}/10` : `Cleared rating for ${name}`, { ratings });
  };

  const profileByName = new Map(profiles.map((r) => [r.name.trim().toLowerCase(), r] as const));

  // Candidates: sign-in date and time, profile completion, and approve or
  // reject right here.
  const feedCandidates: FeedRow[] = candidates
    .map((c) => {
      const p = profileByName.get(c.name.trim().toLowerCase());
      const apps = employer.applications.filter((a) => (a.email || a.name).toLowerCase() === c.key);
      const latest = apps
        .map((a) => a.appliedAt)
        .sort((x, y) => (new Date(y).getTime() || 0) - (new Date(x).getTime() || 0))[0];
      const decision = decisionOf(admin.candidates, c.key);
      const rating = ratingOf(admin.ratings, c.key);
      return { c, p, apps, latest, decision, rating };
    })
    .filter((r) => (range === 0 ? true : isNew(r.latest) || (r.p ? inWindow(r.p.lastActiveDays) : false)))
    .sort((a, b) => {
      const at = a.p ? a.p.lastActiveDays : 999;
      const bt = b.p ? b.p.lastActiveDays : 999;
      return at - bt;
    })
    .map(({ c, p, apps, latest, decision, rating }) => ({
      id: c.key,
      title: c.name,
      sub: `${c.role} · ${c.location || "UK"} · ${apps.length} application${apps.length === 1 ? "" : "s"}`,
      extra: p
        ? `Last signed in ${stampText(p.lastActiveAt)} · Profile ${p.completion}% complete`
        : `Last applied ${stampText(latest)} · No digital profile yet`,
      when: p ? lastSeenLabel(p.lastActiveDays) : dayText(latest),
      flag: decision === "Pending" ? "Awaiting" : decision === "Approved" ? "Approved" : "Not approved",
      ok: decision === "Approved",
      search: `${c.name} ${c.email} ${c.role} ${c.location}`.toLowerCase(),
      meta: [
        { label: "Email", value: c.email || "—" },
        { label: "Phone", value: c.phone || "—" },
        { label: "Experience", value: c.years || "—" },
        { label: "Signed in", value: p ? stampText(p.lastActiveAt) : "Not recorded" },
        { label: "Profile", value: p ? `${p.completion}% complete` : "Not started" },
        { label: "Outstanding", value: p && p.missing.length ? p.missing.join(", ") : "Nothing outstanding" },
        { label: "60 second pitch", value: c.hasPitch ? "Recorded" : "Not recorded" },
        { label: "CV", value: c.cvName || "None uploaded" },
        { label: "Applied to", value: apps.map((a) => jobTitle(a.jobId)).join(", ") || "—" },
        {
          label: "Put forward",
          value:
            admin.distributions
              .filter((d) => d.candidateKey === c.key)
              .map((d) => `${d.jobTitle} (${d.employer})`)
              .join(", ") || "Not yet",
        },
      ],
      rating: { value: rating, onChange: (n: number) => rateKey(c.key, c.name, n) },
      actions: [
        { label: "Approve", run: () => decideCandidate(c, "Approved"), tone: "primary" as const, active: decision === "Approved" },
        { label: "Not approved", run: () => decideCandidate(c, "Rejected"), tone: "danger" as const, active: decision === "Rejected" },
        { label: "Reset", run: () => decideCandidate(c, "Pending"), tone: "plain" as const, active: decision === "Pending" },
      ],
    }));

  // Applications: approve, reject and rate in place, with the pitch and CV
  // state on the row.
  const feedApplications: FeedRow[] = [...(showDemo ? employer.applications : realApplications)]
    .filter((a) => (range === 0 ? true : isNew(a.appliedAt)))
    .sort((a, b) => (new Date(b.appliedAt).getTime() || 0) - (new Date(a.appliedAt).getTime() || 0))
    .map((a) => {
      const job = jobById.get(a.jobId);
      const key = appKeyOf(a);
      const decision = applicationDecision(admin, a.id, key);
      return {
        id: a.id,
        title: a.name,
        sub: `${jobTitle(a.jobId)} · ${employerName(job)}`,
        extra: `Applied ${stampText(a.appliedAt)} · ${a.hasPitch ? "Pitch recorded" : "No pitch yet"} · ${
          a.cvName ? "CV uploaded" : "No CV"
        }`,
        when: dayText(a.appliedAt),
        flag: decision === "Pending" ? "Awaiting review" : decision === "Approved" ? "Approved" : "Not approved",
        ok: decision === "Approved",
        search: `${a.name} ${a.email} ${jobTitle(a.jobId)} ${job?.company ?? ""}`.toLowerCase(),
        meta: [
          { label: "Email", value: a.email || "—" },
          { label: "Phone", value: a.phone || "—" },
          { label: "Location", value: a.location || "—" },
          { label: "Experience", value: a.years || "—" },
          { label: "Applied", value: stampText(a.appliedAt) },
          { label: "Role", value: `${jobTitle(a.jobId)} at ${employerName(job)}` },
          { label: "CV", value: a.cvName || "None uploaded" },
          { label: "Voice answers", value: `${a.voiceAnswers} of 3` },
        ],
        pitch: a.hasPitch,
        rating: { value: ratingOf(admin.ratings, key), onChange: (n: number) => rateKey(key, a.name, n) },
        actions: [
          { label: "Approve", run: () => decideApplication(a, "Approved"), tone: "primary" as const, active: decision === "Approved" },
          { label: "Not approved", run: () => decideApplication(a, "Rejected"), tone: "danger" as const, active: decision === "Rejected" },
          { label: "Reset", run: () => decideApplication(a, "Pending"), tone: "plain" as const, active: decision === "Pending" },
        ],
      };
    });

  // Jobs: approve, reject, pause, close or reopen a role from the dashboard.
  const feedJobs: FeedRow[] = [...(showDemo ? boardJobs : realBoardJobs)]
    .filter((j) => (range === 0 ? true : isNew(j.createdAt)))
    .sort((a, b) => (new Date(b.createdAt).getTime() || 0) - (new Date(a.createdAt).getTime() || 0))
    .map((j) => {
      const apps = employer.applications.filter((a) => a.jobId === j.id);
      const decision = decisionOf(admin.jobs, j.id);
      const setStatus = (status: EmployerJob["status"]) => {
        editJob(j.id, { status });
        act(`Set ${j.title} to ${status.toLowerCase()}`);
      };
      return {
        id: j.id,
        title: j.title,
        sub: `${employerName(j)} · ${j.location || "UK"} · ${apps.length} application${apps.length === 1 ? "" : "s"}`,
        extra: `Posted ${stampText(j.createdAt)} · Status ${j.status} · ${decision}`,
        when: dayText(j.createdAt),
        flag: decision === "Pending" ? "Awaiting approval" : decision,
        ok: decision === "Approved" && j.status === "Live",
        search: `${j.title} ${j.company} ${j.ref ?? ""} ${j.location}`.toLowerCase(),
        meta: [
          { label: "Company", value: j.company || "—" },
          { label: "Reference", value: j.ref || "—" },
          { label: "Posted", value: stampText(j.createdAt) },
          { label: "Status", value: j.status },
          { label: "Approval", value: decision },
          { label: "Pay", value: j.pay || "—" },
          { label: "Contract", value: `${j.contract || "—"} · ${j.payStructure || "—"}` },
          { label: "Applications", value: String(apps.length) },
          { label: "Applicants", value: apps.map((a) => a.name).join(", ") || "None yet" },
          { label: "Tags", value: (j.tags ?? []).join(", ") || "—" },
        ],
        actions: [
          { label: "Approve", run: () => { updateJob(j.id, { decision: "Approved" }); act(`Approved role ${j.title}`, { jobs: { ...admin.jobs, [j.id]: "Approved" } }); }, tone: "primary" as const, active: decision === "Approved" },
          { label: "Reject", run: () => { updateJob(j.id, { decision: "Rejected" }); act(`Rejected role ${j.title}`, { jobs: { ...admin.jobs, [j.id]: "Rejected" } }); }, tone: "danger" as const, active: decision === "Rejected" },
          { label: "Set live", run: () => setStatus("Live"), tone: "plain" as const, active: j.status === "Live" },
          { label: "Pause", run: () => setStatus("Paused"), tone: "plain" as const, active: j.status === "Paused" },
          { label: "Close", run: () => setStatus("Closed"), tone: "plain" as const, active: j.status === "Closed" },
        ],
      };
    });

  // Employers: signup date, contact lag, last login and whether they have
  // posted, with contact logging on the row.
  const feedEmployers: FeedRow[] = [...(showDemo ? crmEmployers : realEmployers)]
    .filter((e) => {
      const stamp = e.lastActiveAt || e.accountCreatedAt || e.contactedAt || e.addedAt;
      return range === 0 ? true : isNew(stamp);
    })
    .sort((a, b) => {
      const at = new Date(a.lastActiveAt || a.accountCreatedAt || a.addedAt).getTime() || 0;
      const bt = new Date(b.lastActiveAt || b.accountCreatedAt || b.addedAt).getTime() || 0;
      return bt - at;
    })
    .map((e) => {
      const addedDays = daysSince(e.addedAt);
      const contactDays = daysSince(e.contactedAt);
      const responseDays =
        addedDays !== null && contactDays !== null ? Math.max(0, addedDays - contactDays) : null;
      const contactBit = e.contactedAt
        ? `Contacted ${stampText(e.contactedAt)}${
            responseDays !== null
              ? ` (${responseDays === 0 ? "same day" : `${responseDays} day${responseDays === 1 ? "" : "s"} after signup`})`
              : ""
          }`
        : `Not contacted yet${addedDays !== null ? ` · added ${addedDays} day${addedDays === 1 ? "" : "s"} ago` : ""}`;
      const posted = boardJobs.filter(
        (j) => (j.company || "").trim().toLowerCase() === e.company.trim().toLowerCase(),
      );
      const needsChase = !e.contactedAt || (!e.accountCreatedAt && (addedDays ?? 0) > 3);
      const logContact = (iso: string | null) => {
        updateEmployer(e.id, { contactedAt: iso }).then((updated) => {
          setCrmEmployers((prev) => prev.map((x) => (x.id === e.id ? updated : x)));
        });
        act(iso ? `Logged contact with ${e.company}` : `Cleared contact record for ${e.company}`);
      };
      return {
        id: e.id,
        title: e.company,
        sub: `${e.contactName || e.email} · ${posted.length} role${posted.length === 1 ? "" : "s"} posted · ${
          e.accountCreatedAt ? `Signed up ${stampText(e.accountCreatedAt)}` : "No account yet"
        }`,
        extra: `${contactBit} · Last login ${e.lastActiveAt ? stampText(e.lastActiveAt) : "never"}`,
        when: dayText(e.lastActiveAt || e.accountCreatedAt || e.addedAt),
        flag: needsChase ? "Chase up" : e.accountCreatedAt ? "Signed up" : "In progress",
        ok: !needsChase && Boolean(e.accountCreatedAt),
        search: `${e.company} ${e.email} ${e.contactName ?? ""}`.toLowerCase(),
        meta: [
          { label: "Contact", value: e.contactName || "—" },
          { label: "Email", value: e.email || "—" },
          { label: "Phone", value: e.phone || "—" },
          { label: "Added", value: stampText(e.addedAt) },
          { label: "Signed up", value: e.accountCreatedAt ? stampText(e.accountCreatedAt) : "No account yet" },
          { label: "Contacted", value: e.contactedAt ? stampText(e.contactedAt) : "Not contacted" },
          { label: "Last login", value: e.lastActiveAt ? stampText(e.lastActiveAt) : "Never" },
          { label: "Posted an advert", value: posted.length ? "Yes" : "Not yet" },
          { label: "Roles", value: posted.map((j) => `${j.title} (${j.status})`).join(", ") || "None" },
          {
            label: "Applications received",
            value: String(
              employer.applications.filter((a) => posted.some((j) => j.id === a.jobId)).length,
            ),
          },
        ],
        actions: [
          { label: "Log contact today", run: () => logContact(new Date().toISOString()), tone: "primary" as const },
          { label: "Clear contact", run: () => logContact(null), tone: "plain" as const, active: !e.contactedAt },
        ],
      };
    });

  const dashboardFeeds = [
    {
      key: "candidates",
      label: "Candidates",
      rows: feedCandidates,
      empty: "No candidate activity in this window.",
      hint: "Sign-in date and time, profile completion, approvals and ratings.",
    },
    {
      key: "applications",
      label: "Applications",
      rows: feedApplications,
      empty: "No applications in this window.",
      hint: "Review the pitch and CV, then approve or reject on the spot.",
    },
    {
      key: "jobs",
      label: "Jobs",
      rows: feedJobs,
      empty: "No roles posted in this window.",
      hint: "Approve, reject, pause or close any advert from here.",
    },
    {
      key: "employers",
      label: "Employers",
      rows: feedEmployers,
      empty: "No employer activity in this window.",
      hint: "Signup date, how fast we made contact, logins and adverts posted.",
    },
  ];


  return (
    <div className="cl flex min-h-screen flex-wrap items-stretch bg-[#F3F3F0]">
      <aside
        aria-label="Admin"
        className="flex flex-[1_1_232px] flex-col gap-7 bg-[#0B2160] px-4 py-6 text-[#D6D6DB] lg:sticky lg:top-0 lg:h-screen lg:max-w-[260px] lg:self-start"
      >
        <Link
          to="/"
          className="flex min-h-11 items-center gap-2.5 px-2 text-[24px] font-extrabold tracking-[-0.03em] text-white"
        >
          <span aria-hidden className="block h-[11px] w-[11px] rounded-full bg-[#FF5A3C]" />
          closdd
          <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#FF5A3C]">Admin</span>
        </Link>
        <nav aria-label="Admin sections" className="flex flex-col gap-1 text-[15px] font-semibold">
          {navItems.map((item) => (
            <Fragment key={item.id}>
              {item.divider ? <div role="separator" aria-hidden className="mx-3 my-2.5 h-px bg-[#2A4393]" /> : null}
            <button
              type="button"
              aria-current={tab === item.id ? "page" : undefined}
              onClick={() => goTab(item.id)}
              className={`flex min-h-11 items-center gap-2.5 rounded-md px-3 text-left transition ${
                tab === item.id ? "bg-[#1C3683] text-white" : "hover:bg-[#1C3683]/60 hover:text-white"
              }`}
            >
              <span className="flex-1">{item.label}</span>
              {item.count > 0 ? (
                <span
                  className={`min-w-6 rounded-full px-2 py-0.5 text-center text-[13px] ${
                    item.tone === "navy" ? "bg-[#2A4393] font-bold text-white" : "bg-[#FF5A3C] font-extrabold text-[#0B2160]"
                  }`}
                >
                  {item.count}
                </span>
              ) : null}
            </button>
            </Fragment>
          ))}
        </nav>
        <button
          type="button"
          role="switch"
          aria-checked={showDemo}
          onClick={toggleShowDemo}
          className="flex min-h-11 items-center justify-between gap-2.5 rounded-md border border-dashed border-[#2A4393] bg-transparent px-3 text-left text-[14px] font-semibold text-[#D6D6DB]"
        >
          Show demo data
          <span
            aria-hidden
            className={`relative inline-block h-[22px] w-[38px] flex-none rounded-full ${showDemo ? "bg-[#FF5A3C]" : "bg-[#2A4393]"}`}
          >
            <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white ${showDemo ? "left-[19px]" : "left-[3px]"}`} />
          </span>
        </button>
        <div className="mt-auto flex flex-col gap-0.5 border-t border-[#2A4393] px-3 pt-4 text-[14px]">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A9A9B0]">Signed in as</div>
          <div className="font-bold text-white">CLOSDD admin</div>
          <div className="flex gap-4">
            <button type="button" onClick={() => goTab("settings")} className="inline-flex min-h-10 items-center text-white underline underline-offset-[3px]">
              Settings
            </button>
            <button
              type="button"
              disabled={loggingOut}
              onClick={() => {
                setLoggingOut(true);
                saveAdmin({ loggedIn: false });
                void adminLogout().then(() => navigate({ to: "/login" }));
              }}
              className="inline-flex min-h-10 items-center text-white underline underline-offset-[3px] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loggingOut ? "Logging out…" : "Log out"}
            </button>
          </div>
        </div>
      </aside>

        <main className="grid min-w-0 flex-[999_1_560px] content-start gap-4 px-5 pb-14 pt-8 lg:px-10">
          {history.length > 0 ? (
            <button
              type="button"
              onClick={goBack}
              className="justify-self-start rounded-md border border-purple-strong/15 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-purple-strong transition hover:bg-purple-strong/[0.06]"
            >
              ← Back to {navLabels[history[history.length - 1]!.tab] ?? "previous page"}
            </button>
          ) : null}
          {tab === "dashboard" ? (
            <Dashboard
              cards={dashboardCards}
              onGo={(t) => goTab(t)}
            />
          ) : (
            <div className="mx-auto w-full max-w-[1160px]">
              {tab === "candidates" && creatingCandidate ? (
                <CandidateCreateForm
                  onBack={() => setCreatingCandidate(false)}
                  onCreated={() => setRetryCount((n) => n + 1)}
                />
              ) : null}
              {((tab === "jobs" || tab === "employers" || tab === "candidates") && jobFullPage) || (tab === "candidates" && creatingCandidate) ? null : (
              <div className="mb-7 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                <div>
                  <p className="font-mono text-[13px] uppercase tracking-[0.12em] text-[#D6361C]">{titles[tab].h}</p>
                  <h1 className="mt-2.5 text-[clamp(32px,3.6vw,46px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">
                    {pageHeadlines[tab] ?? titles[tab].h}
                  </h1>
                  <p className="mt-2.5 max-w-[640px] text-[17px] text-[#3A3A40]">{titles[tab].p}</p>
                </div>
                {tab === "candidates" ? (
                  <button type="button" onClick={() => setCreatingCandidate(true)} className="inline-flex min-h-12 items-center rounded-[4px] bg-[#FF5A3C] px-[22px] text-[15px] font-extrabold text-[#0B2160]">
                    + Create candidate profile
                  </button>
                ) : null}
              </div>
              )}
            <section className={tab === "access" || tab === "activity" || tab === "employers" || tab === "candidates" || (tab === "jobs" && jobFullPage) ? "" : panel}>

              {tab === "candidates" && !creatingCandidate ? (
                <CandidatesTab
                  onFullPage={setJobFullPage}
                  candidates={(showDemo ? candidates : realCandidates).filter(
                    (c) => !q || `${c.name} ${c.email} ${c.role} ${c.location}`.toLowerCase().includes(q),
                  )}
                  admin={admin}
                  jobTitle={jobTitle}
                  distributionsFor={(key) => admin.distributions.filter((d) => d.candidateKey === key)}
                  onDecide={decideCandidate}
                  onDelete={deleteCandidate}
                  onMatch={openMatchForCandidate}
                  assignJobs={employer.jobs}
                  crmEmployers={crmEmployers}
                  employerName={employerName}
                  onAssign={assignMany}
                  onUnassign={unassignDistribution}
                  onRate={(key, name, n) => {
                    const ratings = { ...admin.ratings };
                    if (n) ratings[key] = n;
                    else delete ratings[key];
                    act(n ? `Rated ${name} ${n}/10` : `Cleared rating for ${name}`, { ratings });
                  }}
                  onCreate={() => goTab("access")}
                  lastLoginAt={(c) =>
                    candidateDirectory.find((d) => (d.email || d.name).toLowerCase() === c.key)?.lastLoginAt ?? null
                  }
                  createdByClosdd={(c) =>
                    Boolean(candidateDirectory.find((d) => (d.email || d.name).toLowerCase() === c.key)?.createdByAdmin)
                  }
                  entryOf={(c) => candidateDirectory.find((d) => (d.email || d.name).toLowerCase() === c.key) ?? null}
                  applicationsOf={(c) => employer.applications.filter((a) => appKeyOf(a) === c.key)}
                  onProfileChanged={async (message, done) => {
                    act(message);
                    const list = await fetchAdminCandidates();
                    setCandidateDirectory(list);
                    done?.();
                  }}
                  onResetPassword={async (c, password) => {
                    const entry = candidateDirectory.find((d) => (d.email || d.name).toLowerCase() === c.key);
                    if (!entry) throw new Error("This candidate has no account to reset.");
                    await resetCandidatePassword(entry.id, password);
                    act(`Reset password for ${c.name}`);
                  }}
                />
              ) : null}

              {tab === "jobs" ? (
                <JobsTab
                  onFullPage={setJobFullPage}
                  jobs={boardJobs}
                  showDemo={showDemo}
                  admin={admin}
                  crmEmployers={crmEmployers}
                  employerName={employerName}
                  employerEmail={employerEmail}
                  recruiter={employer.account?.contactName ?? "Direct employer"}
                  applicationsFor={(id) => employer.applications.filter((a) => a.jobId === id)}
                  qualifiedFor={(id) =>
                    employer.applications.filter(
                      (a) => a.jobId === id && decisionOf(admin.applications, a.id) === "Approved",
                    ).length
                  }
                  onDecide={(j, d, featured, reason) => {
                    updateJob(j.id, {
                      decision: d,
                      ...(featured === undefined ? {} : { featured }),
                      ...(d === "Rejected" ? { decisionReason: reason ?? "" } : {}),
                    }).then((updated) => {
                      if (featured !== undefined) {
                        setEmployer((prev) => ({ ...prev, jobs: prev.jobs.map((x) => (x.id === j.id ? updated : x)) }));
                      }
                    });
                    act(`${d === "Approved" ? (featured ? "Approved and featured" : "Approved") : d === "Rejected" ? "Rejected" : "Reset"} role ${j.title}`, {
                      jobs: { ...admin.jobs, [j.id]: d },
                    });
                  }}
                  onHidden={(ids, hidden) => {
                    void Promise.all(ids.map((id) => updateJob(id, { hidden }))).then((updated) => {
                      const byId = new Map(updated.map((u) => [u.id, u] as const));
                      setEmployer((prev) => ({ ...prev, jobs: prev.jobs.map((j) => byId.get(j.id) ?? j) }));
                    });
                    act(`${hidden ? "Hid" : "Showed"} ${ids.length} role${ids.length === 1 ? "" : "s"} ${hidden ? "from" : "on"} the website`);
                  }}
                  onStatus={(id, status) =>
                    updateJob(id, { status }).then((updated) => {
                      setEmployer((prev) => ({ ...prev, jobs: prev.jobs.map((j) => (j.id === id ? updated : j)) }));
                    })
                  }
                  onEdit={editJob}
                  onRemove={removeJob}
                  onRemoveMany={removeJobs}
                  onDecideMany={decideJobs}
                  onFindCandidates={openMatchForJob}
                  onCreate={createJobAdmin}
                />
              ) : null}

              {tab === "exclusive" ? (
                <ExclusiveRoles
                  jobs={exclusiveJobs}
                  invites={admin.invites}
                  query={query}
                  onCreate={(job) => {
                    updateEmployerLegacy({ jobs: [job, ...loadEmployer("sourced").jobs] });
                    act(`Created exclusive role ${job.title} (${job.ref})`, {
                      jobs: { ...admin.jobs, [job.id]: "Approved" },
                    });
                  }}
                  onDelete={(job) => {
                    updateEmployerLegacy({ jobs: loadEmployer("sourced").jobs.filter((j) => j.id !== job.id) });
                    act(`Deleted exclusive role ${job.title}`, {
                      invites: admin.invites.filter((iv) => iv.jobId !== job.id),
                    });
                  }}
                  onInvite={(jobId, name, email) => {
                    const job = jobById.get(jobId);
                    const invite = {
                      id: `inv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                      jobId,
                      jobTitle: job?.title ?? "Exclusive role",
                      name,
                      email,
                      status: "Invited" as InviteStatus,
                      sentAt: new Date().toISOString(),
                    };
                    act(`Invited ${name || email} to apply for ${invite.jobTitle}`, {
                      invites: [invite, ...admin.invites],
                    });
                  }}
                  onInviteStatus={(id, status) => {
                    const iv = admin.invites.find((x) => x.id === id);
                    act(`${iv?.name || iv?.email || "Invite"} marked ${status.toLowerCase()} for ${iv?.jobTitle ?? "role"}`, {
                      invites: admin.invites.map((x) => (x.id === id ? { ...x, status } : x)),
                    });
                  }}
                  onRemoveInvite={(id) =>
                    update({ invites: admin.invites.filter((x) => x.id !== id) })
                  }
                />
              ) : null}

              {tab === "invites" ? (
                <Invitations
                  jobs={employer.jobs}
                  invites={invites}
                  applications={employer.applications.map((a) => ({ jobId: a.jobId, email: a.email }))}
                  query={query}
                  onSend={(invite) => {
                    apiCreateInvite(invite).then((created) => {
                      setInvites((prev) => [created, ...prev]);
                      act(`Invited ${invite.firstName || invite.email} to apply for ${invite.jobTitle}`);
                    });
                  }}
                  onResend={(invite) => {
                    const at = new Date().toISOString();
                    apiUpdateInvite(invite.id, { status: "Sent", sentAt: at, lastActivityAt: at }).then((updated) => {
                      setInvites((prev) => prev.map((x) => (x.id === invite.id ? updated : x)));
                      act(`Resent invitation to ${invite.email} for ${invite.jobTitle}`);
                    });
                  }}
                  onCancel={(id) => {
                    const iv = invites.find((x) => x.id === id);
                    apiUpdateInvite(id, { status: "Cancelled", lastActivityAt: new Date().toISOString() }).then((updated) => {
                      setInvites((prev) => prev.map((x) => (x.id === id ? updated : x)));
                      act(`Cancelled invitation to ${iv?.email ?? "candidate"}`);
                    });
                  }}
                />
              ) : null}

              {tab === "pool" ? (
                <TalentPool
                  people={poolPeople}
                  funnelJobs={exclusiveJobs}
                  targetJobs={boardJobs.filter((j) => j.status === "Live")}
                  pool={admin.pool}
                  query={query}
                  onTag={tagPool}
                  onDistribute={distributeMany}
                />
              ) : null}

              {tab === "applications" ? (
                <ApplicationsTab
                  // CLOSDD puts candidates forward pre-approved, so only direct applications need review here.
                  apps={(showDemo ? employer.applications : realApplications).filter(
                    (a) =>
                      !admin.distributions.some((d) => d.candidateKey === appKeyOf(a) && d.jobId === a.jobId) &&
                      (!q || `${a.name} ${a.email} ${jobTitle(a.jobId)}`.toLowerCase().includes(q)),
                  )}
                  directory={candidateDirectory}
                  admin={admin}
                  crmEmployers={crmEmployers}
                  jobTitle={jobTitle}
                  jobById={jobById}
                  employerName={employerName}
                  assignJobs={employer.jobs}
                  distributions={admin.distributions}
                  onAssign={assignMany}
                  onUnassign={unassignDistribution}
                  onUnassignMany={unassignManyDistributions}
                  onRemoveApplications={removeApplications}
                  onDecide={(a, d) => {
                    const key = appKeyOf(a);
                    updateApplication(a.id, { decision: d });
                    setCandidateDecision(key, d);
                    act(
                      `${d === "Approved" ? "Approved" : "Marked not approved"}: ${a.name} for ${jobTitle(a.jobId)}`,
                      {
                        applications: { ...admin.applications, [a.id]: d },
                        // The candidate decision carries over to future
                        // applications until an admin changes it.
                        candidates: { ...admin.candidates, [key]: d },
                        stages: { ...admin.stages, [key]: d === "Approved" ? "Approved" : "Not approved" },
                      },
                    );
                  }}
                  onSalary={(key, name, min, max) => {
                    const tag = { ...emptyPoolTag, ...admin.pool[key], min, max };
                    act(
                      max ? `Set salary expectation for ${name}: ${min}k to ${max}k` : `Cleared salary expectation for ${name}`,
                      { pool: { ...admin.pool, [key]: tag } },
                    );
                  }}
                  onRate={(key, name, n) => {
                    const ratings = { ...admin.ratings };
                    if (n) ratings[key] = n;
                    else delete ratings[key];
                    act(n ? `Rated ${name} ${n}/10` : `Cleared rating for ${name}`, { ratings });
                  }}

                  onDecideMany={(list, d) => {
                    if (!list.length) return;
                    const applications = { ...admin.applications };
                    const candidates = { ...admin.candidates };
                    const stages = { ...admin.stages };
                    list.forEach((a) => {
                      const key = appKeyOf(a);
                      applications[a.id] = d;
                      candidates[key] = d;
                      stages[key] = d === "Approved" ? "Approved" : "Not approved";
                      updateApplication(a.id, { decision: d });
                      setCandidateDecision(key, d);
                    });
                    act(
                      `${d === "Approved" ? "Approved" : "Marked not approved"} ${list.length} application${
                        list.length === 1 ? "" : "s"
                      } in bulk`,
                      { applications, candidates, stages },
                    );
                  }}
                />

              ) : null}

              {tab === "matching" ? (
                <MatchingTab
                  candidates={candidates}
                  approved={approvedCandidates}
                  jobs={employer.jobs}
                  candidateKey={matchCandidate}
                  jobId={matchJob}
                  setCandidateKey={(k) => {
                    setMatchCandidate(k);
                    setMatchJob("");
                  }}
                  setJobId={(id) => {
                    setMatchJob(id);
                    setMatchCandidate("");
                  }}
                  employerName={employerName}
                  distributions={admin.distributions}
                  onPutForward={putForward}
                  candidateByKey={candidateByKey}
                />
              ) : null}

              {tab === "autoapply" ? (
                <AutoApplyAdmin
                  candidates={autoApplyCandidates}
                  query={query}
                  onStatus={setAutoStatus}
                  onMatch={matchAuto}
                  onRemove={removeAuto}
                />
              ) : null}

              {tab === "employers" ? (
                <EmployersAdmin admin={admin} showDemo={showDemo} onLog={(m) => act(m)} onJobsRemoved={removeJobsEverywhere} onFullPage={setJobFullPage} onAddCandidates={() => goTab("candidates")} />
              ) : null}


              {tab === "access" ? <CandidateAccessAdmin query={query} /> : null}

              {tab === "distribution" ? (
                <DistributionTab
                  rows={admin.distributions.filter(
                    (d) => !q || `${d.candidateName} ${d.jobTitle} ${d.employer}`.toLowerCase().includes(q),
                  )}
                  onStatus={setDistributionStatus}
                  q={q}
                />
              ) : null}

              {tab === "leads" || tab === "payments" ? (
                <div className="rounded-xl border border-[#E3E3DE] bg-white px-6 py-10 text-center">
                  <p className="text-[18px] font-extrabold text-[#0B2160]">
                    {tab === "leads" ? "No leads yet." : "No payments to show yet."}
                  </p>
                  <p className="mx-auto mt-1.5 max-w-[520px] text-[15px] text-[#3A3A40]">
                    {tab === "leads"
                      ? "Certified-hire requests, audit bookings, Academy calls and contact forms will land here once they are connected to the console."
                      : "Role unlocks, refunds and the money-back guarantee will list here once payments are connected to the console."}
                  </p>
                </div>
              ) : null}

              {tab === "settings" ? <EmailDeliveryTest /> : null}

              {tab === "activity" ? (
                <ActivityTab
                  entries={admin.activity.filter((e) => !q || e.message.toLowerCase().includes(q))}
                />
              ) : null}
            </section>
            </div>
          )}
        </main>
    </div>
  );
}

type DashboardCard = {
  value: number;
  title: string;
  hint: string;
  tone: "navy" | "coral" | "plain";
  links: { label: string; tab: Tab }[];
};

type FeedAction = { label: string; run: () => void; tone?: "primary" | "danger" | "plain"; active?: boolean };

type FeedRow = {
  id: string;
  title: string;
  sub: string;
  extra?: string;
  when: string;
  flag: string;
  ok: boolean;
  search: string;
  meta: { label: string; value: string }[];
  actions: FeedAction[];
  rating?: { value: number; onChange: (n: number) => void };
  pitch?: boolean;
};

type DashboardFeed = { key: string; label: string; rows: FeedRow[]; empty: string; hint: string };

/** Strips the HTML CI4's Email::printDebugger() wraps its output in, for plain display. */
function stripDebugHtml(s: string) {
  return s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?[^>]+>/g, "")
    .trim();
}

/**
 * Sends a real diagnostic email through the live mailer, on demand — so an
 * admin can confirm SMTP is actually configured and working (mail server,
 * credentials, port) without needing to trigger a real password reset or
 * employer email just to find out whether it arrives. See Api\Admin\
 * EmailTest::send() — unlike every real transactional email, this surfaces
 * the exact SMTP failure instead of only logging it server-side.
 */
function EmailDeliveryTest() {
  const [to, setTo] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  const run = () => {
    setStatus("sending");
    setMessage("");
    sendTestEmail(to.trim() || undefined)
      .then((res) => {
        setStatus("ok");
        setMessage(
          `Sent to ${res.to}. If it doesn't arrive within a minute or two, check spam first.`,
        );
      })
      .catch((err: Error) => {
        setStatus("error");
        setMessage(stripDebugHtml(err.message));
      });
  };

  return (
    <div className={`${panel} px-5 py-4`}>
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <p className={label}>Email delivery</p>
          <p className="mt-1 text-[12px] leading-snug text-ink/50">
            Sends a real test email through the live mailer — the fastest way to confirm SMTP is
            actually working.
          </p>
        </div>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com (optional)"
          className="h-9 w-[210px] rounded-md border border-purple-strong/15 bg-white px-3 text-[12px] text-ink outline-none focus:border-purple-mid"
        />
        <button
          type="button"
          disabled={status === "sending"}
          onClick={run}
          className="inline-flex h-9 items-center rounded-md bg-purple-strong px-3.5 text-[13px] font-semibold text-white transition hover:bg-purple-mid disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "sending" ? "Sending…" : "Send test email"}
        </button>
      </div>
      {message ? (
        <p
          className={`mt-2.5 whitespace-pre-wrap text-[12px] font-semibold leading-snug ${
            status === "error" ? "text-tag-red" : "text-tag-green"
          }`}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The dashboard is its own console: candidates, applications, jobs and
 * employers are all tracked and actioned here, without leaving the page.
 */
function Dashboard({
  cards,
  onGo,
}: {
  cards: DashboardCard[];
  onGo: (tab: Tab) => void;
}) {
  const outstanding = cards.reduce((n, c) => n + c.value, 0);
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
        <div>
          <p className="font-mono text-[13px] uppercase tracking-[0.12em] text-[#D6361C]">Dashboard</p>
          <h1 className="mt-2.5 text-[clamp(32px,3.6vw,46px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">
            {outstanding
              ? `${outstanding} ${outstanding === 1 ? "thing needs" : "things need"} a decision.`
              : "Nothing is waiting on you."}
          </h1>
          <p className="mt-2.5 max-w-[600px] text-[17px] text-[#3A3A40]">
            Only what is waiting on you. Totals live on each screen.
          </p>
        </div>
      </div>

      <section aria-labelledby="needs" className="grid gap-3">
        <h2 id="needs" className="font-mono text-[12px] font-medium uppercase tracking-[0.12em] text-[#3A3A40]">
          Needs a decision
        </h2>
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
          {cards.map((c) => {
            const navy = c.tone === "navy";
            const box = navy
              ? "bg-[#0B2160] text-white"
              : c.tone === "coral"
                ? "border-2 border-[#FF5A3C] bg-[#FFF1EC] text-[#0B2160]"
                : "border border-[#E3E3DE] bg-white text-[#0B2160]";
            return (
              <div key={c.title} className={`flex min-h-[168px] flex-col gap-1.5 rounded-xl px-6 py-[22px] ${box}`}>
                <span className="text-[52px] font-extrabold leading-none tracking-[-0.04em]">{c.value}</span>
                <span className="text-[17px] font-extrabold">{c.title}</span>
                <span className={`text-[14px] ${navy ? "text-[#C9C9CF]" : "text-[#3A3A40]"}`}>{c.hint}</span>
                <span className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 text-[14px] font-extrabold">
                  {c.links.map((l) => (
                    <button
                      key={l.label}
                      type="button"
                      onClick={() => onGo(l.tab)}
                      className={`text-left ${navy ? "text-[#FF5A3C]" : "text-[#D6361C]"} hover:underline`}
                    >
                      {l.label}
                    </button>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}




function Row({ children }: { children: React.ReactNode }) {
  return <div className="px-6 py-5">{children}</div>;
}

/** Multi-select dropdown used by the Candidates filter panel (design: "Filters" card). */
function FilterGroup({
  label: text,
  options,
  value,
  onChange,
  open,
  onToggle,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
  open: boolean;
  onToggle: () => void;
}) {
  const names = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  return (
    <div className="relative min-w-0">
      <span className="mb-1 block text-[13px] font-semibold text-[#0B2160]">{text}</span>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${text}: ${names.length ? names.join(", ") : "any"}`}
        className={`flex min-h-11 w-full items-center gap-2 rounded-[4px] bg-white py-0 pl-3 pr-2.5 text-[15px] text-[#0B2160] ${
          names.length ? "border-2 border-[#0B2160] font-bold" : "border border-[#BDBDB5]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-left">{names.length ? names.join(", ") : "Any"}</span>
        {names.length > 1 ? (
          <span className="min-w-[22px] rounded-full bg-[#0B2160] px-[7px] py-px text-center text-[12px] font-extrabold text-white">
            {names.length}
          </span>
        ) : null}
        <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          role="group"
          aria-label={text}
          className="absolute left-0 top-full z-20 mt-1 max-h-[320px] w-max min-w-full max-w-[280px] overflow-y-auto rounded-lg border border-[#BDBDB5] bg-white p-1.5 shadow-[0_12px_28px_rgba(11,33,96,0.14)]"
        >
          {options.length ? (
            options.map((o) => {
              const on = value.includes(o.value);
              return (
                <label
                  key={o.value}
                  className={`flex min-h-10 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-[15px] ${on ? "bg-[#EEF1F8] font-bold" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])}
                    className="h-[18px] w-[18px] shrink-0 accent-[#0B2160]"
                  />
                  {o.label}
                </label>
              );
            })
          ) : (
            <p className="px-2.5 py-2 text-[14px] text-[#5A5A60]">Nothing to filter yet.</p>
          )}
          <div className="mt-1 flex justify-between gap-2 border-t border-[#EFEFEA] px-1.5 pb-0.5 pt-1.5">
            <button type="button" onClick={() => onChange([])} className="min-h-9 px-1.5 text-[14px] font-bold text-[#0B2160] underline underline-offset-[3px]">
              Clear
            </button>
            <button type="button" onClick={onToggle} className="min-h-9 rounded-[4px] bg-[#0B2160] px-3.5 text-[14px] font-extrabold text-white">
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CandidatesTab({
  onFullPage,
  candidates,
  admin,
  jobTitle,
  distributionsFor,
  onDecide,
  onDelete,
  onMatch,
  assignJobs,
  crmEmployers,
  employerName,
  onAssign,
  onUnassign,
  onRate,
  onCreate,
  lastLoginAt,
  createdByClosdd,
  entryOf,
  applicationsOf,
  onProfileChanged,
  onResetPassword,
}: {
  onFullPage: (on: boolean) => void;
  candidates: CandidateRecord[];
  admin: AdminStore;
  jobTitle: (id: string) => string;
  distributionsFor: (key: string) => Distribution[];
  onDecide: (c: CandidateRecord, d: Decision) => void;
  onDelete: (c: CandidateRecord) => Promise<boolean>;
  onMatch: (key: string) => void;
  assignJobs: EmployerJob[];
  crmEmployers: EmployerRecord[];
  employerName: (j: EmployerJob | undefined) => string;
  onAssign: (people: { key: string; name: string }[], jobIds: string[]) => void;
  onUnassign: (d: Distribution) => void;
  onRate: (key: string, name: string, n: number) => void;
  onCreate: () => void;
  lastLoginAt: (c: CandidateRecord) => string | null;
  createdByClosdd: (c: CandidateRecord) => boolean;
  /** Their account, or null when they only ever applied. */
  entryOf: (c: CandidateRecord) => CandidateDirectoryEntry | null;
  applicationsOf: (c: CandidateRecord) => Application[];
  /** Logs the change and reloads the directory; `done` runs with the new data in place. */
  onProfileChanged: (message: string, done?: () => void) => Promise<void>;
  onResetPassword: (c: CandidateRecord, password: string) => Promise<void>;
}) {
  const [pwFor, setPwFor] = useState<CandidateRecord | null>(null);
  const [pw, setPw] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwDone, setPwDone] = useState(false);
  const openReset = (c: CandidateRecord) => {
    setPwFor(c);
    setPw("");
    setPwShow(false);
    setPwError("");
    setPwDone(false);
  };
  const submitReset = async () => {
    if (!pwFor) return;
    if (pw.length < 8) return setPwError("The new password needs at least 8 characters.");
    setPwBusy(true);
    setPwError("");
    try {
      await onResetPassword(pwFor, pw);
      setPwDone(true);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Couldn't reset the password. Try again.");
    } finally {
      setPwBusy(false);
    }
  };
  const [needClosdd, setNeedClosdd] = useState(false);
  const [fwdKey, setFwdKey] = useState("");
  const [tab, setTab] = useState<"all" | "approved" | "complete" | "incomplete" | "pitch" | "week">("all");
  const [rowMenu, setRowMenu] = useState("");
  const [filter, setFilter] = useState<Stage | "All">("All");
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<"put" | "remove">("put");
  const [clientQ, setClientQ] = useState("");
  const [clientSector, setClientSector] = useState("All");
  const [clientLoc, setClientLoc] = useState("All");
  const [clientRole, setClientRole] = useState("All");
  const [chosenJobs, setChosenJobs] = useState<string[]>([]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [sentNote, setSentNote] = useState("");
  const [pageKey, setPageKey] = useState("");
  const [pageMode, setPageMode] = useState<"view" | "edit">("view");
  useEffect(() => {
    onFullPage(Boolean(pageKey));
    return () => onFullPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);
  const openPage = (key: string, m: "view" | "edit") => {
    setPageMode(m);
    setPageKey(key);
  };
  const [open, setOpen] = useState<string>("");
  const [q, setQ] = useState("");
  const [sets, setSets] = useState<Record<string, string[]>>({});
  const [openGroup, setOpenGroup] = useState("");
  const [moreFilters, setMoreFilters] = useState(false);
  const [needPitch, setNeedPitch] = useState(false);
  const [needCv, setNeedCv] = useState(false);
  const [needAssess, setNeedAssess] = useState(false);

  const pick = (k: string) => sets[k] ?? [];
  const setPick = (k: string, v: string[]) => setSets((prev) => ({ ...prev, [k]: v }));
  const uniq = (xs: string[]) => Array.from(new Set(xs.map((x) => x.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const toOpts = (xs: string[]) => xs.map((x) => ({ value: x, label: x }));
  const forwardedTo = (c: CandidateRecord) => distributionsFor(c.key).map((d) => d.employer);

  const groupDefs: { key: string; label: string; options: { value: string; label: string }[]; main: boolean }[] = [
    { key: "loc", label: "Location", options: toOpts(uniq(candidates.map((c) => c.location))), main: true },
    {
      key: "role",
      label: "Role type",
      options: [...toOpts(uniq(candidates.map((c) => c.role))), { value: "__none", label: "Not set" }],
      main: true,
    },
    {
      key: "rate",
      label: "Your rating",
      main: true,
      options: [
        { value: "5", label: "5 stars" },
        { value: "4", label: "4 stars" },
        { value: "3", label: "3 stars" },
        { value: "low", label: "1 to 2 stars" },
        { value: "0", label: "Not rated" },
      ],
    },
    { key: "exp", label: "Experience", options: toOpts(uniq(candidates.map((c) => c.years))), main: false },
    {
      key: "fwd",
      label: "Put forward to",
      main: false,
      options: [
        { value: "none", label: "Not put forward yet" },
        ...toOpts(uniq(candidates.flatMap((c) => forwardedTo(c)))),
      ],
    },
  ];

  const ratingBucket = (n: number) => (n >= 3 ? String(n) : n >= 1 ? "low" : "0");
  const needle = q.trim().toLowerCase();
  const rows = candidates.filter((c) => {
    if (filter !== "All" && stageOf(admin.stages, c.key) !== filter) return false;
    if (needle && ![c.name, c.email, c.role, c.location, ...c.tags].join(" ").toLowerCase().includes(needle)) return false;
    if (pick("role").length && !pick("role").includes(c.role.trim() || "__none")) return false;
    if (pick("loc").length && !pick("loc").includes(c.location)) return false;
    if (pick("exp").length && !pick("exp").includes(c.years)) return false;
    if (pick("rate").length && !pick("rate").includes(ratingBucket(ratingOf(admin.ratings, c.key)))) return false;
    if (pick("fwd").length) {
      const fwd = forwardedTo(c);
      if (!pick("fwd").some((v) => (v === "none" ? fwd.length === 0 : fwd.includes(v)))) return false;
    }
    if (needPitch && !c.hasPitch) return false;
    if (needCv && !c.cvName) return false;
    if (needAssess && c.voiceAnswers < 3) return false;
    if (needClosdd && !createdByClosdd(c)) return false;
    return true;
  });

  const chips = groupDefs.flatMap((g) =>
    pick(g.key).map((v) => ({ g: g.key, v, text: `${g.label}: ${g.options.find((o) => o.value === v)?.label ?? v}` })),
  );
  const hasFilters = Boolean(needle) || chips.length > 0 || needPitch || needCv || needAssess || needClosdd;
  const clearAll = () => {
    setQ("");
    setSets({});
    setNeedPitch(false);
    setNeedCv(false);
    setNeedAssess(false);
    setNeedClosdd(false);
  };
  const moreActive = groupDefs.filter((g) => !g.main && pick(g.key).length).length;


  /** Profile completeness, derived from what a candidate record actually holds. */
  const profileOf = (c: CandidateRecord) => {
    const checks: [string, boolean][] = [
      ["name", Boolean(c.name)],
      ["email", Boolean(c.email)],
      ["phone", Boolean(c.phone)],
      ["location", Boolean(c.location)],
      ["role", Boolean(c.role)],
      ["experience", Boolean(c.years)],
      ["CV", Boolean(c.cvName)],
      ["pitch", c.hasPitch],
      ["assessment", c.voiceAnswers >= 3],
    ];
    const done = checks.filter(([, ok]) => ok).length;
    return { pct: Math.round((done / checks.length) * 100), missing: checks.filter(([, ok]) => !ok).map(([k]) => k) };
  };
  const weekAgo = Date.now() - 7 * 86_400_000;
  const tabMatch = (c: CandidateRecord, k: typeof tab) => {
    if (k === "all") return true;
    if (k === "approved") return decisionOf(admin.candidates, c.key) === "Approved";
    if (k === "complete") return profileOf(c).pct === 100;
    if (k === "incomplete") return profileOf(c).pct < 100;
    if (k === "pitch") return c.hasPitch;
    const t = new Date(lastLoginAt(c) ?? "").getTime();
    return !Number.isNaN(t) && t >= weekAgo;
  };
  const tabCount = (k: typeof tab) => candidates.filter((c) => tabMatch(c, k)).length;
  const shown = rows.filter((c) => tabMatch(c, tab));

  const { page, setPage, pageSize, setPageSize, totalPages, resetPage } = usePagination(shown.length, 20);
  useEffect(() => {
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filter, q, sets, needPitch, needCv, needAssess, needClosdd, pageSize]);
  const pageRows = paginate(shown, pageSize, page);
  const allPicked = pageRows.length > 0 && pageRows.every((c) => picked.includes(c.key));
  const toggleAllPicked = () =>
    setPicked((prev) =>
      allPicked
        ? prev.filter((k) => !pageRows.some((c) => c.key === k))
        : Array.from(new Set([...prev, ...pageRows.map((c) => c.key)])),
    );

  const recordOf = (j: EmployerJob) =>
    crmEmployers.find((e) => e.company.trim().toLowerCase() === employerName(j).trim().toLowerCase());
  const sectorOf = (j: EmployerJob) => recordOf(j)?.category ?? "";
  const clientBadge = (j: EmployerJob) =>
    j.unlocked
      ? { text: "Paid", cls: "bg-[#0B2160] text-white" }
      : recordOf(j)?.accountCreatedAt
        ? { text: "Active", cls: "bg-[#DDF2E3] text-[#1F6B3A]" }
        : { text: "Prospect", cls: "bg-[#FFE9A8] text-[#6B4A00]" };
  const putForwardCount = (jobId: string) => admin.distributions.filter((d) => d.jobId === jobId).length;
  const realJobs = assignJobs.filter((j) => !j.demo);
  const uniqSorted = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const clients = realJobs
    .filter((j) => clientSector === "All" || sectorOf(j) === clientSector)
    .filter((j) => clientLoc === "All" || j.location === clientLoc)
    .filter((j) => clientRole === "All" || j.title === clientRole)
    .filter((j) => {
      const needle2 = clientQ.trim().toLowerCase();
      return !needle2 || `${employerName(j)} ${j.title} ${j.location}`.toLowerCase().includes(needle2);
    })
    .sort((x, y) => employerName(x).localeCompare(employerName(y)) || x.title.localeCompare(y.title));
  const pickedPeople = candidates.filter((c) => picked.includes(c.key));
  const assignedTo = (jobId: string) => pickedPeople.filter((c) => distributionsFor(c.key).some((d) => d.jobId === jobId)).length;
  const toggleJob = (id: string) => setChosenJobs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const sendLabel =
    mode === "put"
      ? chosenJobs.length
        ? `Put ${picked.length} forward to ${chosenJobs.length} role${chosenJobs.length === 1 ? "" : "s"}`
        : "Choose a role to put them forward to"
      : chosenJobs.length
        ? `Remove ${picked.length} from ${chosenJobs.length} role${chosenJobs.length === 1 ? "" : "s"}`
        : "Choose a role to remove them from";
  const runPut = () => {
    if (!chosenJobs.length || !pickedPeople.length) return;
    onAssign(pickedPeople.map((c) => ({ key: c.key, name: c.name })), chosenJobs);
    setSentNote(`Put ${pickedPeople.length} candidate${pickedPeople.length === 1 ? "" : "s"} forward to ${chosenJobs.length} role${chosenJobs.length === 1 ? "" : "s"}. They now show in Applications for each role.`);
    setPicked([]);
    setChosenJobs([]);
  };
  const runRemove = () => {
    let n = 0;
    for (const c of pickedPeople)
      for (const d of distributionsFor(c.key)) if (chosenJobs.includes(d.jobId)) {
        onUnassign(d);
        n++;
      }
    setSentNote(n ? `Removed ${n} assignment${n === 1 ? "" : "s"}.` : "Nothing to remove for those roles.");
    setConfirmRemove(false);
    setPicked([]);
    setChosenJobs([]);
  };

  const tabBtn = (on: boolean) =>
    `min-h-11 rounded-full px-4 text-[14px] font-bold transition ${
      on ? "border-2 border-[#0B2160] bg-[#0B2160] text-white" : "border border-[#BDBDB5] bg-white text-[#0B2160] hover:border-[#0B2160]"
    }`;

  const pwDialog = pwFor ? (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#0B2160]/40 p-4" onClick={() => !pwBusy && setPwFor(null)}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-h"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] rounded-xl bg-white p-6 shadow-[0_24px_48px_rgba(11,33,96,0.24)]"
      >
        <h2 id="pw-h" className="text-[22px] font-extrabold tracking-[-0.02em] text-[#0B2160]">Reset password</h2>
        {pwDone ? (
          <>
            <p className="mt-2 text-[16px] text-[#3A3A40]">
              {pwFor.name}'s password is changed. Nothing is emailed, so send it to them yourself.
            </p>
            <button type="button" onClick={() => setPwFor(null)} className="mt-5 min-h-12 rounded-[4px] bg-[#0B2160] px-6 text-[15px] font-extrabold text-white">
              Done
            </button>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitReset();
            }}
          >
            <p className="mt-2 text-[16px] text-[#3A3A40]">
              Set a new password for {pwFor.name} ({pwFor.email}). Nothing is emailed; tell them the new password yourself.
            </p>
            <label htmlFor="pw-new" className="mt-4 block text-[14px] font-semibold text-[#0B2160]">New password</label>
            <div className="mt-1.5 flex gap-2">
              <input
                id="pw-new"
                type={pwShow ? "text" : "password"}
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus
                className="min-h-12 min-w-0 flex-1 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none focus:border-[#0B2160]"
              />
              <button type="button" onClick={() => setPwShow((v) => !v)} className="min-h-12 rounded-[4px] border border-[#BDBDB5] px-3.5 text-[14px] font-bold text-[#0B2160]">
                {pwShow ? "Hide" : "Show"}
              </button>
            </div>
            {pwError ? <p role="alert" className="mt-2 text-[14px] font-semibold text-[#A62B12]">{pwError}</p> : null}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button type="submit" disabled={pwBusy} className="min-h-12 rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160] disabled:opacity-60">
                {pwBusy ? "Saving…" : "Set password"}
              </button>
              <button type="button" onClick={() => setPwFor(null)} className="min-h-12 px-2 text-[15px] font-bold text-[#0B2160] underline underline-offset-[3px]">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
) : null;

  const pageCand = pageKey ? candidates.find((c) => c.key === pageKey) : undefined;
  if (pageCand) {
    const c = pageCand;
    const entry = entryOf(c);
    const apps = applicationsOf(c);
    const shortDay = (iso: string) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    };
    const roleAt = (jobId: string, title: string, company?: string) => {
      const name = company ?? employerName(assignJobs.find((j) => j.id === jobId));
      return name ? `${title} · ${name}` : title;
    };
    const activity: ProfileRow[] = [
      ...apps.map((a) => ({
        id: `app-${a.id}`,
        title: roleAt(a.jobId, jobTitle(a.jobId)),
        detail: `Applied ${shortDay(a.appliedAt)} · ${a.status.toLowerCase()}`,
      })),
      ...distributionsFor(c.key).map((d) => ({
        id: `dist-${d.id}`,
        title: roleAt(d.jobId, d.jobTitle, d.employer),
        detail: `Put forward by Closdd ${shortDay(d.sentAt)} · ${d.status === "Viewed" ? "heard by employer" : d.status === "Sent" ? "new" : d.status.toLowerCase()}`,
        onRemove: () => onUnassign(d),
      })),
    ];
    const historyWords = (m: string) =>
      m.startsWith("Approved candidate")
        ? "Profile approved by Closdd admin"
        : m.startsWith("Did not approve candidate")
          ? "Profile rejected by Closdd admin"
          : m.startsWith("Reset candidate")
            ? "Profile moved back to waiting by Closdd admin"
            : m;
    const history = [
      ...admin.activity
        .filter((a) => a.message.includes(c.name))
        .map((a) => ({ id: a.id, text: `${historyWords(a.message)} · ${shortDay(a.at)}` })),
      ...(entry?.createdAt
        ? [{ id: "created", text: `Profile created by ${entry.createdByAdmin ? "Closdd" : "candidate"} · ${shortDay(entry.createdAt)}` }]
        : []),
    ];
    return (
      <>
        {pwDialog}
        <CandidateProfilePage
          person={c}
          entry={entry}
          cvUrl={apps.find((a) => a.cvUrl)?.cvUrl ?? ""}
          decision={decisionOf(admin.candidates, c.key)}
          rating={ratingOf(admin.ratings, c.key)}
          completePct={profileOf(c).pct}
          lastLoginAt={lastLoginAt(c)}
          activity={activity}
          history={history}
          mode={pageMode}
          onMode={setPageMode}
          onBack={() => setPageKey("")}
          onDecide={(d) => onDecide(c, d)}
          onRate={(n) => onRate(c.key, c.name, n)}
          onDelete={() => void onDelete(c).then((gone) => gone && setPageKey(""))}
          onResetPassword={() => openReset(c)}
          onPutForward={() => onMatch(c.key)}
          onChanged={(message, email) => onProfileChanged(message, email ? () => setPageKey(email.toLowerCase()) : undefined)}
        />
      </>
    );
  }

  const fwdCand = fwdKey ? candidates.find((c) => c.key === fwdKey) : undefined;
  const fwdRows = fwdCand ? distributionsFor(fwdCand.key) : [];
  const fwdByEmployer = Array.from(new Set(fwdRows.map((d) => d.employer))).map((emp) => ({
    emp,
    rows: fwdRows.filter((d) => d.employer === emp),
  }));
  const fwdStatusLabel = (st: Distribution["status"]) => (st === "Sent" ? "New" : st === "Viewed" ? "Heard" : st);

  return (
    <>
      {pwDialog}
      {fwdCand ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-[#0B2160]/40 p-4"
          onClick={() => setFwdKey("")}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Put forward to, ${fwdCand.name}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-xl bg-white p-6 shadow-[0_24px_60px_rgba(11,33,96,0.3)]"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#FF5A3C]">Put forward to</p>
                <h2 className="mt-1 text-[22px] font-extrabold tracking-[-0.01em] text-[#0B2160]">{fwdCand.name}</h2>
                <p className="mt-1 text-[14px] text-[#3A3A40]">
                  {fwdByEmployer.length} employer{fwdByEmployer.length === 1 ? "" : "s"} · {fwdRows.length} role{fwdRows.length === 1 ? "" : "s"}
                </p>
              </div>
              <button type="button" aria-label="Close" onClick={() => setFwdKey("")} className="h-10 w-10 shrink-0 rounded-full border border-[#BDBDB5] text-[18px] font-extrabold text-[#0B2160]">×</button>
            </div>
            <div className="mt-4 grid gap-3">
              {fwdByEmployer.length ? (
                fwdByEmployer.map((g) => (
                  <div key={g.emp} className="rounded-lg border border-[#E3E3DE] px-4 py-3">
                    <p className="border-b border-[#EFEFEA] pb-2 text-[15px] font-extrabold text-[#0B2160]">{g.emp}</p>
                    {g.rows.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-3 py-2 text-[14px] text-[#3A3A40]">
                        <span className="min-w-0">{d.jobTitle}</span>
                        <span className="flex shrink-0 items-center gap-3">
                          <Pill text={fwdStatusLabel(d.status)} tone={distributionStyles[d.status]} />
                          <button type="button" onClick={() => onUnassign(d)} className="text-[13px] font-extrabold text-[#A62B12]">Remove</button>
                        </span>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <p className="text-[14px] text-[#3A3A40]">Not put forward to any role yet.</p>
              )}
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={() => setFwdKey("")} className="min-h-11 rounded-[4px] bg-[#0B2160] px-6 text-[15px] font-extrabold text-white">Done</button>
            </div>
          </div>
        </div>
      ) : null}
      <div role="group" aria-label="Show" className="flex flex-wrap gap-2">
        {([["all", "All"], ["approved", "Approved"], ["complete", "Profile complete"], ["incomplete", "Incomplete"], ["pitch", "Pitch recorded"], ["week", "Signed in this week"]] as const).map(([k, l]) => (
          <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)} className={tabBtn(tab === k)}>
            {l} <span className="opacity-75">{tabCount(k)}</span>
          </button>
        ))}
      </div>

      <section aria-label="Filters" className="mt-3.5 rounded-xl border border-[#E3E3DE] bg-white px-5 py-[18px]">
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(170px,100%),1fr))]">
          <div className="[grid-column:1/-1]">
            <label htmlFor="cd-q" className="mb-1 block text-[13px] font-semibold text-[#0B2160]">
              Search
            </label>
            <input
              id="cd-q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email, skill or past employer"
              className="min-h-11 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none placeholder:text-[#7A7F99] focus:border-[#0B2160]"
            />
          </div>
          {groupDefs
            .filter((g) => moreFilters || g.main)
            .map((g) => (
              <FilterGroup
                key={g.key}
                label={g.label}
                options={g.options}
                value={pick(g.key)}
                onChange={(v) => setPick(g.key, v)}
                open={openGroup === g.key}
                onToggle={() => setOpenGroup(openGroup === g.key ? "" : g.key)}
              />
            ))}
          <div className="flex min-w-0 items-end">
            <button
              type="button"
              onClick={() => setMoreFilters((v) => !v)}
              aria-expanded={moreFilters}
              className="min-h-11 w-full rounded-[4px] border border-dashed border-[#0B2160] bg-white px-3 text-[14px] font-extrabold text-[#0B2160]"
            >
              {moreFilters ? "Fewer filters" : `More filters${moreActive ? ` (${moreActive})` : ""}`}
            </button>
          </div>
        </div>
        {chips.length ? (
          <ul aria-label="Active filters" className="mt-3 flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <li
                key={`${c.g}-${c.v}`}
                className="inline-flex items-center gap-0.5 rounded-full bg-[#E6EBF7] py-0.5 pl-3 pr-0.5 text-[13px] font-bold text-[#0B2160]"
              >
                {c.text}
                <button
                  type="button"
                  aria-label={`Remove ${c.text}`}
                  onClick={() => setPick(c.g, pick(c.g).filter((x) => x !== c.v))}
                  className="h-[30px] w-[30px] rounded-full text-[16px] leading-none"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {(
            [
              ["Pitch recorded", needPitch, setNeedPitch],
              ["CV uploaded", needCv, setNeedCv],
              ["Assessment done", needAssess, setNeedAssess],
              ["Created by Closdd", needClosdd, setNeedClosdd],
            ] as [string, boolean, (v: boolean) => void][]
          ).map(([text, on, set]) => (
            <label key={text} className="flex min-h-10 cursor-pointer items-center gap-2.5 text-[15px] font-semibold text-[#0B2160]">
              <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} className="h-5 w-5 accent-[#0B2160]" />
              {text}
            </label>
          ))}
          <span className="ml-auto text-[14px] text-[#3A3A40]">
            <strong className="text-[#0B2160]">{shown.length}</strong> candidates match
          </span>
          {hasFilters ? (
            <button type="button" onClick={clearAll} className="min-h-10 px-1.5 text-[14px] font-bold text-[#0B2160] underline underline-offset-[3px]">
              Clear filters
            </button>
          ) : null}
        </div>
      </section>

      {picked.length ? (
        <section aria-label="Put forward" className="mt-3.5 rounded-xl bg-[#0B2160] px-[22px] py-5 text-white">
          <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
              <div className="text-[20px] font-extrabold tracking-[-0.01em]">{picked.length} selected</div>
              <div role="group" aria-label="Action" className="inline-flex gap-1 rounded-full bg-[#142C75] p-1">
                {([["put", "Put forward"], ["remove", "Remove from"]] as const).map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={mode === k} onClick={() => { setMode(k); setConfirmRemove(false); }} className={`min-h-10 rounded-full px-4 text-[14px] font-extrabold ${mode === k ? "bg-white text-[#0B2160]" : "text-white"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => { setPicked([]); setChosenJobs([]); }} className="min-h-10 px-1.5 text-[14px] font-bold text-white underline underline-offset-[3px]">
              Clear selection
            </button>
          </div>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[#FF5A3C]">
            {mode === "put" ? "Pick the client roles to put them forward to" : "Pick the roles to remove them from"}
          </div>
          {chosenJobs.length ? (
            <ul aria-label="Chosen clients" className="mt-2 flex flex-wrap gap-2">
              {chosenJobs.map((id) => {
                const j = assignJobs.find((x) => x.id === id);
                return (
                  <li key={id} className="inline-flex items-center gap-0.5 rounded-full bg-[#FF5A3C] py-0.5 pl-3.5 pr-0.5 text-[14px] font-extrabold text-[#0B2160]">
                    {j ? `${j.title} · ${employerName(j)}` : id}
                    <button type="button" aria-label="Remove" onClick={() => toggleJob(id)} className="h-9 w-9 rounded-full text-[18px] leading-none">
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="mt-2.5 rounded-[10px] bg-white p-3.5 text-[#0B2160]">
            <label htmlFor="pf-q" className="mb-1 block text-[13px] font-semibold">
              Find a client
            </label>
            <input id="pf-q" type="search" value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Type an employer or role name" autoComplete="off" className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none focus:border-[#0B2160]" />
            <div className="mt-3 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(170px,100%),1fr))]">
              {([
                ["Sector", clientSector, setClientSector, "All sectors", uniqSorted(realJobs.map(sectorOf))],
                ["Location", clientLoc, setClientLoc, "Anywhere", uniqSorted(realJobs.map((j) => j.location))],
                ["Hiring for", clientRole, setClientRole, "Any role", uniqSorted(realJobs.map((j) => j.title))],
              ] as [string, string, (v: string) => void, string, string[]][]).map(([l, v, set, all, opts]) => (
                <label key={l} className="grid gap-1 text-[13px] font-semibold text-[#0B2160]">
                  {l}
                  <select value={v} onChange={(e) => set(e.target.value)} className="min-h-11 rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] font-normal text-[#0B2160] outline-none focus:border-[#0B2160]">
                    <option value="All">{all}</option>
                    {opts.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="mt-2.5 text-[13px] text-[#3A3A40]">
              <strong className="text-[#0B2160]">{clients.length}</strong> of {realJobs.length} clients shown
            </div>
            <ul aria-label="Matching clients" className="mt-2 max-h-[300px] overflow-y-auto rounded-lg border border-[#E3E3DE]">
              {clients.map((j) => {
                const on = chosenJobs.includes(j.id);
                const already = assignedTo(j.id);
                return (
                  <li key={j.id} className="border-b border-[#EFEFEA] last:border-b-0">
                    <label className={`flex cursor-pointer items-center gap-3 px-3.5 py-2.5 ${on ? "bg-[#EEF1F8]" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleJob(j.id)} className="h-5 w-5 shrink-0 accent-[#0B2160]" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-extrabold">{employerName(j)}</span>
                        <span className="block text-[14px] text-[#3A3A40]">{j.title}</span>
                        {putForwardCount(j.id) ? (
                          <span className="mt-0.5 mr-1.5 inline-flex rounded-full bg-[#DCE6FF] px-2 py-px text-[12px] font-extrabold text-[#0B2160]">
                            {putForwardCount(j.id)} candidate{putForwardCount(j.id) === 1 ? "" : "s"} put forward
                          </span>
                        ) : null}
                        {already ? <span className="mt-0.5 inline-flex rounded-full bg-[#DDF2E3] px-2 py-px text-[12px] font-extrabold text-[#1F6B3A]">{already} of {picked.length} already there</span> : null}
                      </span>
                      <span className="grid shrink-0 justify-items-end gap-1 text-right text-[13px] text-[#3A3A40]">
                        <span className={`rounded-full px-2.5 py-px text-[12px] font-extrabold ${clientBadge(j).cls}`}>{clientBadge(j).text}</span>
                        <span>{[sectorOf(j), j.location].filter(Boolean).join(" · ") || "—"}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
              {!clients.length ? <li className="px-3.5 py-4 text-[15px] text-[#3A3A40]">No clients match.</li> : null}
            </ul>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {mode === "put" ? (
              <button type="button" onClick={runPut} disabled={!chosenJobs.length} className={`min-h-[52px] rounded-[4px] px-6 text-[16px] font-extrabold ${chosenJobs.length ? "bg-[#FF5A3C] text-[#0B2160]" : "cursor-not-allowed bg-[#2A4393] text-[#8A8FB5]"}`}>
                {chosenJobs.length ? sendLabel : "Choose at least one role"}
              </button>
            ) : confirmRemove ? (
              <span className="flex flex-wrap items-center gap-2.5">
                <span className="text-[15px]">They come off those roles and out of Applications for them.</span>
                <button type="button" onClick={runRemove} className="min-h-[52px] rounded-[4px] bg-[#FF5A3C] px-[22px] text-[16px] font-extrabold text-[#0B2160]">Yes, remove</button>
                <button type="button" onClick={() => setConfirmRemove(false)} className="min-h-[52px] px-2.5 text-[15px] font-bold text-white underline underline-offset-[3px]">Cancel</button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)} disabled={!chosenJobs.length} className={`min-h-[52px] rounded-[4px] px-6 text-[16px] font-extrabold ${chosenJobs.length ? "border-2 border-white text-white" : "cursor-not-allowed border-2 border-[#2A4393] text-[#8A8FB5]"}`}>
                {sendLabel}
              </button>
            )}
          </div>
        </section>
      ) : null}

      {sentNote ? (
        <div role="status" className="mt-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-[10px] bg-[#DDF2E3] px-[18px] py-3.5 text-[15px] text-[#14502A]">
          <strong>{sentNote}</strong>
          <button type="button" onClick={() => setSentNote("")} className="min-h-9 px-1.5 font-bold underline underline-offset-[3px]">Dismiss</button>
        </div>
      ) : null}

      <section aria-label="Candidates list" className="mt-4 overflow-x-auto rounded-xl border border-[#E3E3DE] bg-white">
        {!shown.length ? (
          <div className="px-5 py-8 text-center text-[#3A3A40]">
            {candidates.length ? "No candidates match these filters." : "No candidates here yet. They appear as soon as someone applies for a role."}
            {hasFilters ? (
              <>
                {" "}
                <button type="button" onClick={clearAll} className="min-h-9 font-extrabold underline underline-offset-[3px]">Clear filters</button>
              </>
            ) : null}
          </div>
        ) : (
          <table className="w-full min-w-[980px] border-collapse text-left text-[15px]">
            <thead>
              <tr className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#5A5A60]">
                <th scope="col" className="w-7 border-b border-[#E3E3DE] py-3 pl-5">
                  <input type="checkbox" aria-label="Select all shown" checked={allPicked} onChange={toggleAllPicked} className="h-5 w-5 accent-[#0B2160]" />
                </th>
                {["Candidate", "Roles wanted and experience", "Pitch", "Profile", "Put forward to"].map((h) => (
                  <th key={h} scope="col" className="border-b border-[#E3E3DE] px-4 py-3 font-medium">{h}</th>
                ))}
                <th scope="col" className="border-b border-[#E3E3DE] py-3 pl-4 pr-5 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((c) => {
                const decision = decisionOf(admin.candidates, c.key);
                const dist = distributionsFor(c.key);
                const expanded = open === c.key;
                const isPicked = picked.includes(c.key);
                const prof = profileOf(c);
                const rating = ratingOf(admin.ratings, c.key);
                return (
                  <Fragment key={c.key}>
                    <tr className={isPicked ? "bg-[#EEF1F8]" : ""}>
                      <td className="border-b border-[#EFEFEA] py-3.5 pl-5 align-top">
                        <input type="checkbox" aria-label={`Select ${c.name}`} checked={isPicked} onChange={() => setPicked((p) => (p.includes(c.key) ? p.filter((k) => k !== c.key) : [...p, c.key]))} className="mt-0.5 h-5 w-5 accent-[#0B2160]" />
                      </td>
                      <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                        <div className="flex items-start gap-3">
                          <span aria-hidden className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full bg-[#0B2160] text-[13px] font-extrabold text-white">{initialsOf(c.name)}</span>
                          <div className="min-w-0">
                            <button type="button" onClick={() => openPage(c.key, "view")} className="text-left font-extrabold text-[#0B2160] hover:underline">{c.name}</button>
                            <div className="text-[14px] text-[#3A3A40]">{c.location || "—"}</div>
                            {createdByClosdd(c) ? <span className="mt-1 inline-flex rounded-full bg-[#DCE6FF] px-2 py-px text-[12px] font-extrabold text-[#0B2160]">Created by Closdd</span> : null}
                            <div className="mt-1 w-[96px]"><RatingSelect value={rating} onChange={(n) => onRate(c.key, c.name, n)} /></div>
                          </div>
                        </div>
                      </td>
                      <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                        {c.role ? (
                          <span className="inline-flex max-w-[240px] items-center rounded-full border border-[#BDBDB5] bg-white px-[9px] py-0.5 text-[12px] font-bold text-[#0B2160]">{c.role}</span>
                        ) : (
                          <div className="text-[14px] text-[#5A5A60]">Role not set</div>
                        )}
                        <div className="mt-1 text-[14px] text-[#3A3A40]">{c.years || "Experience not set"}</div>
                      </td>
                      <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                        {c.hasPitch ? <PitchPlayer has url={c.audioUrl} /> : <span className="text-[14px] text-[#5A5A60]">No pitch</span>}
                      </td>
                      <td className="min-w-[190px] border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                        <div className="flex items-center justify-between gap-2 text-[14px]">
                          <span className="font-extrabold">{prof.pct}%</span>
                          <span className="inline-flex items-center gap-1.5">
                            <Pill text={decision === "Pending" ? "Waiting" : decisionLabels[decision]} tone={decisionStyles[decision]} />
                            {decision !== "Approved" ? (
                              <button type="button" onClick={() => onDecide(c, "Approved")} aria-label={`Approve ${c.name}`} className="min-h-7 rounded-full bg-[#FF5A3C] px-2.5 text-[12px] font-extrabold text-[#0B2160]">Approve</button>
                            ) : (
                              <button type="button" onClick={() => onDecide(c, "Pending")} className="min-h-7 px-1 text-[12px] font-bold text-[#0B2160] underline underline-offset-[3px]">Undo</button>
                            )}
                          </span>
                        </div>
                        <div role="progressbar" aria-label="Profile complete" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prof.pct} className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EFEFEA]">
                          <div className="h-full rounded-full bg-[#FF5A3C]" style={{ width: `${prof.pct}%` }} />
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[13px] text-[#3A3A40]">
                          <span className={`rounded-full px-2 py-px text-[12px] font-extrabold ${c.voiceAnswers >= 3 ? "bg-[#DDF2E3] text-[#1F6B3A]" : "bg-[#EFEFEA] text-[#3A3A40]"}`}>Assessment {c.voiceAnswers}/3</span>
                          {prof.missing.length ? <span>Missing {prof.missing.slice(0, 3).join(", ")}</span> : null}
                        </div>
                      </td>
                      <td className="w-[210px] border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                        {dist.length ? (
                          <>
                            <span className="block truncate text-[14px] font-extrabold" title={dist[0]!.employer}>{dist[0]!.employer}</span>
                            <span className="block truncate text-[13px] text-[#3A3A40]" title={dist[0]!.jobTitle}>{dist[0]!.jobTitle}</span>
                            {dist.length > 1 ? (
                              <button type="button" onClick={() => setFwdKey(c.key)} className="mt-0.5 text-[13px] font-bold text-[#0B2160] underline underline-offset-[3px]">
                                Show all ({dist.length})
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-[14px] text-[#5A5A60]">Not put forward yet</span>
                        )}
                      </td>
                      <td className="border-b border-[#EFEFEA] py-3.5 pl-4 pr-5 align-top">
                        <div className="relative">
                          <button type="button" aria-haspopup="menu" aria-expanded={rowMenu === c.key} aria-label={`More for ${c.name}`} onClick={() => setRowMenu(rowMenu === c.key ? "" : c.key)} className="h-11 w-11 rounded-[4px] border border-[#BDBDB5] bg-white text-[18px] font-extrabold leading-none text-[#0B2160]">⋯</button>
                          {rowMenu === c.key ? (
                            <div role="menu" className="absolute right-0 top-full z-30 mt-1.5 flex min-w-[220px] flex-col rounded-xl border border-[#BDBDB5] bg-white p-2 shadow-[0_12px_28px_rgba(11,33,96,0.14)]">
                              {([
                                ["View profile", () => openPage(c.key, "view")],
                                ...(entryOf(c)
                                  ? ([
                                      ["Edit", () => openPage(c.key, "edit")],
                                      ["Reset password", () => openReset(c)],
                                    ] as [string, () => void][])
                                  : []),
                              ] as [string, () => void][]).map(([text, run]) => (
                                <button key={text} type="button" role="menuitem" onClick={() => { setRowMenu(""); run(); }} className="flex min-h-11 items-center rounded-md px-3.5 text-left text-[16px] font-extrabold text-[#0B2160] hover:bg-[#F3F3F0]">{text}</button>
                              ))}
                              <div role="separator" className="mx-3 my-1.5 h-px bg-[#E3E3DE]" />
                              <button type="button" role="menuitem" onClick={() => { setRowMenu(""); void onDelete(c); }} className="flex min-h-11 items-center rounded-md px-3.5 text-left text-[16px] font-extrabold text-[#A62B12] hover:bg-[#FCE3DD]">Delete</button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td colSpan={7} className="border-b border-[#EFEFEA] bg-[#FAFAF8] px-5 py-5">
                          <div className="grid gap-5 lg:grid-cols-2">
                            <div className="grid gap-3">
                              <p className={label}>Contact</p>
                              <Fact k="Email" v={c.email} />
                              <Fact k="Phone" v={c.phone} />
                              <Fact k="Location" v={c.location} />
                              <Fact k="Experience" v={c.years} />
                              <Fact k="Assessment" v={`${c.voiceAnswers}/3 questions answered`} />
                              <Fact k="CV" v={c.cvName || "Not uploaded"} />
                            </div>
                            <div className="grid content-start gap-3">
                              <p className={label}>Roles applied for</p>
                              <ul className="grid gap-1.5">
                                {c.appliedJobIds.map((id, i) => (
                                  <li key={`${id}-${i}`} className="text-[15px] text-[#3A3A40]">{jobTitle(id)}</li>
                                ))}
                              </ul>
                              <p className={`${label} mt-2`}>Put forward by CLOSDD</p>
                              {dist.length ? (
                                <ul className="grid gap-1.5">
                                  {dist.map((d) => (
                                    <li key={d.id} className="text-[15px] text-[#3A3A40]">{d.jobTitle} at {d.employer} · {d.status}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-[15px] text-[#5A5A60]">Not put forward yet.</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {shown.length ? (
          <PaginationBar total={shown.length} page={page} totalPages={totalPages} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} itemLabel="candidates" className="border-t border-[#E3E3DE] bg-[#FAFAF8]" />
        ) : null}
      </section>
    </>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <p className="text-[13px] text-ink/70">
      <span className="text-ink/45">{k}: </span>
      {v || "Not provided"}
    </p>
  );
}

/**
 * Job management: every role posted on CLOSDD in one place.
 *
 * The admin searches by company name, company email, role or reference,
 * narrows by sales category, status, review decision and posting date, then
 * approves, rejects, pauses, edits or removes the role and sees who applied.
 */
const seniorityOptions = ["Entry level / no experience", "1–2 years", "3–5 years", "5+ years", "Leadership"];
const contractOptions = ["Full-time", "Part-time", "Contract / freelance", "Internship"];
const settingOptions = ["On-site", "Hybrid", "Remote"];
const payStructureOptions = [
  "Base salary + commission",
  "Base salary only",
  "Commission only",
  "Hourly + bonus",
  "Day rate",
];

/**
 * Full role editor: every field an employer fills in on /post-job.
 *
 * Typing edits a local `draft` copy of the job so keystrokes never touch
 * the (large) admin state tree — that was making every character wait on
 * a full re-render of the whole admin page. Edits are pushed up to
 * `onEdit` on a short debounce once typing pauses, and flushed immediately
 * on unmount so nothing is lost if the editor is closed mid-type.
 */
function JobEditor({
  job,
  onEdit,
}: {
  job: EmployerJob;
  onEdit: (id: string, patch: Partial<EmployerJob>) => void;
}) {
  const [draft, setDraft] = useState(job);
  const pending = useRef<Partial<EmployerJob>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = () => {
    clearTimeout(timer.current);
    if (Object.keys(pending.current).length === 0) return;
    const patch = pending.current;
    pending.current = {};
    onEdit(job.id, patch);
  };

  useEffect(() => {
    setDraft(job);
    pending.current = {};
    clearTimeout(timer.current);
    // Cleanup runs (with this render's job/pending still in scope) right
    // before we reset for a newly-opened job, or on unmount — either way,
    // any edit still waiting on the debounce gets saved first.
    return flush;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const set = (patch: Partial<EmployerJob>) => {
    setDraft((d) => ({ ...d, ...patch }));
    pending.current = { ...pending.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 500);
  };

  const tags = draft.tags ?? [];
  const toggleTag = (id: string) =>
    set({ tags: tags.includes(id) ? tags.filter((t) => t !== id) : [...tags, id] });

  return (
    <div className="grid gap-4">
      <div>
        <p className={label}>The role</p>
        <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
          <FieldRow k="Job title">
            <input className={input} value={draft.title} onChange={(e) => set({ title: e.target.value })} />
          </FieldRow>
          <FieldRow k="Reference">
            <input className={input} value={draft.ref} onChange={(e) => set({ ref: e.target.value })} />
          </FieldRow>
          <FieldRow k="Seniority">
            <select className={input} value={draft.seniority} onChange={(e) => set({ seniority: e.target.value })}>
              {[draft.seniority, ...seniorityOptions.filter((o) => o !== draft.seniority)].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </FieldRow>
          <FieldRow k="Contract">
            <select className={input} value={draft.contract} onChange={(e) => set({ contract: e.target.value })}>
              {[draft.contract, ...contractOptions.filter((o) => o !== draft.contract)].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </FieldRow>
        </div>
      </div>

      <div>
        <p className={label}>Company & location</p>
        <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
          <FieldRow k="Company name">
            <input className={input} value={draft.company} onChange={(e) => set({ company: e.target.value })} />
          </FieldRow>
          <FieldRow k="City / area">
            <input className={input} value={draft.location} onChange={(e) => set({ location: e.target.value })} />
          </FieldRow>
          <FieldRow k="Setting">
            <select className={input} value={draft.setting} onChange={(e) => set({ setting: e.target.value })}>
              {[draft.setting, ...settingOptions.filter((o) => o !== draft.setting)].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </FieldRow>
          <FieldRow k="Status">
            <select
              className={input}
              value={draft.status}
              onChange={(e) => set({ status: e.target.value as EmployerJob["status"] })}
            >
              <option value="Live">Live</option>
              <option value="Paused">Paused</option>
              <option value="Closed">Closed</option>
            </select>
          </FieldRow>
        </div>
      </div>

      <div>
        <p className={label}>Pay & commission</p>
        <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
          <FieldRow k="Pay structure">
            <select className={input} value={draft.payStructure} onChange={(e) => set({ payStructure: e.target.value })}>
              {[draft.payStructure, ...payStructureOptions.filter((o) => o !== draft.payStructure)]
                .filter(Boolean)
                .map((o) => (
                  <option key={o}>{o}</option>
                ))}
            </select>
          </FieldRow>
          <FieldRow k="Pay details">
            <input
              className={input}
              value={draft.pay}
              onChange={(e) => set({ pay: e.target.value })}
              placeholder="£35,000 – £45,000 base + commission"
            />
          </FieldRow>
        </div>
      </div>

      <div>
        <p className={label}>Job description</p>
        <textarea
          className={`${input} mt-2 min-h-[220px]`}
          value={draft.advert}
          onChange={(e) => set({ advert: e.target.value })}
          placeholder="Advert copy"
        />
      </div>

      <div>
        <p className={label}>Tags</p>
        <div className="mt-2 grid gap-3">
          {tagGroups.map((group) => (
            <div key={group}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/35">{group}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {jobTags
                  .filter((t) => t.group === group)
                  .map((t) => {
                    const on = tags.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleTag(t.id)}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition ${
                          on
                            ? `${toneClass[t.tone]} ring-2 ring-purple-strong/25`
                            : "bg-white text-ink/50 ring-1 ring-ink/10 hover:ring-purple-strong/25"
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-ink/45">Changes save as you type and update the live advert.</p>
    </div>
  );
}

function FieldRow({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-ink/40">{k}</span>
      {children}
    </label>
  );
}

function JobsTab({
  showDemo = true,
  jobs,
  admin,
  crmEmployers,
  employerName,
  employerEmail,
  recruiter,
  applicationsFor,
  qualifiedFor,
  onDecide,
  onStatus,
  onHidden,
  onFullPage,
  onEdit,
  onRemove,
  onRemoveMany,
  onDecideMany,
  onFindCandidates,
  onCreate,
}: {
  showDemo?: boolean;
  jobs: EmployerJob[];
  admin: AdminStore;
  crmEmployers: EmployerRecord[];
  employerName: (j?: EmployerJob) => string;
  employerEmail: (j: EmployerJob) => string;
  recruiter: string;
  applicationsFor: (id: string) => Application[];
  qualifiedFor: (id: string) => number;
  onDecide: (j: EmployerJob, d: Decision, featured?: boolean, reason?: string) => void;
  onDecideMany: (jobsToDecide: EmployerJob[], d: Decision) => Promise<void>;
  onStatus: (id: string, status: EmployerJob["status"]) => void;
  onHidden: (ids: string[], hidden: boolean) => void;
  onFullPage: (on: boolean) => void;
  onEdit: (id: string, patch: Partial<EmployerJob>) => void;
  onRemove: (j: EmployerJob) => void;
  onRemoveMany: (jobsToRemove: EmployerJob[]) => Promise<Set<string>>;
  onFindCandidates: (id: string) => void;
  onCreate: (input: CreateJobInput) => Promise<EmployerJob>;
}) {
  const [menuFor, setMenuFor] = useState("");
  const [viewJob, setViewJob] = useState("");
  const [viewTab, setViewTab] = useState<"view" | "edit">("view");
  const [open, setOpen] = useState("");
  const [editing, setEditing] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  useEffect(() => {
    onFullPage(showCreate);
    return () => onFullPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [approving, setApproving] = useState("");
  const [rejecting, setRejecting] = useState<{ id: string; reason: string }>({ id: "", reason: "" });
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState("All");
  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState<"All" | EmployerJob["status"]>("All");
  const [review, setReview] = useState<"All" | Decision>("All");
  const [posted, setPosted] = useState<"All" | "7" | "30" | "90">("All");
  const [sort, setSort] = useState<"newest" | "oldest" | "applicants" | "company">("newest");
  const [account, setAccount] = useState<"All" | "client" | "prospect">("All");
  const [demoOnly, setDemoOnly] = useState(false);
  // Turning Show demo data off while on the Demo chip drops back to the real list.
  useEffect(() => {
    if (!showDemo) setDemoOnly(false);
  }, [showDemo]);
  const [payType, setPayType] = useState("All");
  const [onSite, setOnSite] = useState<"All" | "shown" | "hidden">("All");

  const selectCls =
    "min-h-12 min-w-[160px] flex-[1_1_180px] rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]";

  const accountOf = (j: EmployerJob) => employerAccountStatus(crmEmployers, employerName(j));
  const catsOf = (j: EmployerJob) => categoriesOf(`${j.title} ${j.tags.join(" ")} ${j.advert}`);
  const demoCompanySet = new Set(crmEmployers.filter((e) => e.demo).map((e) => e.company.trim().toLowerCase()));
  const demoEmployerJobIds = new Set(crmEmployers.filter((e) => e.demo).flatMap((e) => e.jobIds));
  const isDemoJob = (j: EmployerJob) =>
    Boolean(j.demo) || demoEmployerJobIds.has(j.id) || demoCompanySet.has((j.company || "").trim().toLowerCase());
  const realJobs = jobs.filter((j) => !isDemoJob(j));
  /** Demo companies/roles clutter this filter with sample data admins aren't managing day to day — the Demo chip below still finds them. */
  const companies = Array.from(new Set(realJobs.map((j) => employerName(j)))).sort();


  const payTypes = Array.from(new Set(jobs.map((j) => j.payStructure).filter(Boolean))).sort();
  const q = search.trim().toLowerCase();
  const rows = jobs
    .filter((j) => {
      if (payType !== "All" && j.payStructure !== payType) return false;
      if (onSite !== "All" && Boolean(j.hidden) !== (onSite === "hidden")) return false;
      if (q && !`${j.title} ${j.company} ${employerName(j)} ${employerEmail(j)} ${j.ref} ${j.location}`.toLowerCase().includes(q))
        return false;
      if (company !== "All" && employerName(j) !== company) return false;
      if (category !== "All" && !catsOf(j).includes(category as ReturnType<typeof categoriesOf>[number])) return false;
      if (status !== "All" && j.status !== status) return false;
      if (account !== "All" && (accountOf(j).hasAccount ? "client" : "prospect") !== account) return false;
      if (review !== "All" && decisionOf(admin.jobs, j.id) !== review) return false;
      // Demo roles only ever show under the Demo chip, like Employers.
      if (demoOnly !== isDemoJob(j)) return false;
      if (posted !== "All") {
        const t = new Date(j.createdAt).getTime();
        if (Number.isNaN(t) || Date.now() - t > Number(posted) * 86_400_000) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === "applicants") return applicationsFor(b.id).length - applicationsFor(a.id).length;
      if (sort === "company") return employerName(a).localeCompare(employerName(b));
      const ta = new Date(a.createdAt).getTime() || 0;
      const tb = new Date(b.createdAt).getTime() || 0;
      return sort === "oldest" ? ta - tb : tb - ta;
    });

  const awaiting = realJobs.filter((j) => decisionOf(admin.jobs, j.id) === "Pending").length;

  const { page: currentPage, setPage, pageSize, setPageSize, totalPages, resetPage } = usePagination(rows.length, 50);

  useEffect(() => {
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, company, category, status, review, posted, sort, account, demoOnly, payType, onSite, pageSize]);

  const pageRows = paginate(rows, pageSize, currentPage);

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const visibleSelectedCount = pageRows.filter((j) => selected.has(j.id)).length;
  const allVisibleSelected = pageRows.length > 0 && visibleSelectedCount === pageRows.length;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const j of pageRows) next.delete(j.id);
        return next;
      }
      const next = new Set(prev);
      for (const j of pageRows) next.add(j.id);
      return next;
    });
  };

  const selectedJobs = jobs.filter((j) => selected.has(j.id));

  const bulkDelete = async () => {
    if (!selectedJobs.length) return;
    const names = selectedJobs.map((j) => j.title).join(", ");
    if (
      !window.confirm(
        `Remove ${selectedJobs.length} role${selectedJobs.length === 1 ? "" : "s"} from CLOSDD (${names})? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      const removedIds = await onRemoveMany(selectedJobs);
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of removedIds) next.delete(id);
        return next;
      });
    } finally {
      setDeleting(false);
    }
  };

  const bulkDecide = async (d: Decision) => {
    if (!selectedJobs.length) return;
    setDeciding(true);
    try {
      await onDecideMany(selectedJobs, d);
    } finally {
      setDeciding(false);
    }
  };

  const pageJob = viewJob ? jobs.find((x) => x.id === viewJob) : undefined;
  if (pageJob) {
    return (
      <JobPage
        job={pageJob}
        decision={decisionOf(admin.jobs, pageJob.id)}
        apps={applicationsFor(pageJob.id)}
        qualified={qualifiedFor(pageJob.id)}
        employer={employerName(pageJob)}
        email={employerEmail(pageJob)}
        hasAccount={accountOf(pageJob).hasAccount}
        recruiter={recruiter}
        tab={viewTab}
        setTab={setViewTab}
        admin={admin}
        onBack={() => setViewJob("")}
        onDecide={(d) => onDecide(pageJob, d)}
        onStatus={(st) => onStatus(pageJob.id, st)}
        onEdit={onEdit}
        onFindCandidates={() => onFindCandidates(pageJob.id)}
        onRemove={() => {
          if (window.confirm(`Remove ${pageJob.title} from CLOSDD? This cannot be undone.`)) {
            onRemove(pageJob);
            setViewJob("");
          }
        }}
      />
    );
  }
  if (showCreate) {
    return (
      <div>
        <div className="mx-auto max-w-[880px]">
          <button type="button" onClick={() => setShowCreate(false)} className="inline-flex min-h-10 items-center text-[15px] font-bold text-[#0B2160] underline underline-offset-[3px]">
            ← Back to jobs
          </button>
          <p className="mt-3 font-mono text-[13px] uppercase tracking-[0.12em] text-[#D6361C]">Post on behalf</p>
          <h1 className="mt-2.5 text-[clamp(32px,3.6vw,46px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">Post a job for an employer.</h1>
          <p className="mt-2.5 max-w-[620px] text-[17px] text-[#3A3A40]">
            The role appears on the board under the employer's name, exactly as if they had posted it. Saving it creates the employer's account automatically if they don't have one.
          </p>
          <div className="mt-7">
            <JobCreateForm crmEmployers={crmEmployers} onCreate={onCreate} onDone={() => setShowCreate(false)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E3E3DE] px-5 py-4">
        <div className="flex flex-wrap items-center gap-2.5 sm:ml-auto">
          <button
            type="button"
            onClick={() => {
              setShowImport((v) => !v);
              setShowCreate(false);
            }}
            className={`${btnGhost} min-h-12 px-[18px] ${showImport ? "bg-[#0B2160]/[0.06]" : ""}`}
          >
            Import CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setShowCreate((v) => !v);
              setShowImport(false);
            }}
            className={`${btn} min-h-12 bg-[#FF5A3C] px-[22px] text-[#0B2160] hover:bg-[#FF5A3C]/90`}
          >
            + Create a job
          </button>
        </div>
      </div>

      {showCreate ? (
        <div className="border-b border-ink/10 bg-[#fbfcfe] px-3 py-4">
          <JobCreateForm
            crmEmployers={crmEmployers}
            onCreate={onCreate}
            onDone={() => setShowCreate(false)}
          />
        </div>
      ) : null}

      {showImport ? (
        <div className="border-b border-ink/10 bg-[#fbfcfe] px-3 py-4">
          <a href="/closdd-jobs-template.xlsx" download className={`${btnGhost} mb-3`}>
            Download the Excel template
          </a>
          <JobCsvImport crmEmployers={crmEmployers} onCreate={onCreate} onDone={() => setShowImport(false)} />
        </div>
      ) : null}

      <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-5 py-4">
        {(() => {
          const count = (fn: (j: EmployerJob) => boolean) => realJobs.filter(fn).length;
          const pick = (next: { status?: typeof status; review?: typeof review; demo?: boolean }) => () => {
            setStatus(next.status ?? "All");
            setReview(next.review ?? "All");
            setAccount("All");
            setDemoOnly(Boolean(next.demo));
          };
          const chips: { label: string; n: number; active: boolean; apply: () => void }[] = [
            { label: "Waiting", n: awaiting, active: review === "Pending", apply: pick({ review: "Pending" }) },
            { label: "Live", n: count((j) => j.status === "Live" && decisionOf(admin.jobs, j.id) === "Approved"), active: status === "Live" && review === "Approved", apply: pick({ status: "Live", review: "Approved" }) },
            { label: "Paused", n: count((j) => j.status === "Paused"), active: status === "Paused", apply: pick({ status: "Paused" }) },
            {
              label: "Rejected",
              n: count((j) => decisionOf(admin.jobs, j.id) === "Rejected"),
              active: review === "Rejected",
              apply: pick({ review: "Rejected" }),
            },
            { label: "Closed", n: count((j) => j.status === "Closed"), active: status === "Closed", apply: pick({ status: "Closed" }) },
            ...(showDemo
              ? [{ label: "Demo", n: jobs.filter(isDemoJob).length, active: demoOnly && status === "All" && review === "All", apply: pick({ demo: true }) }]
              : []),
            { label: "All", n: realJobs.length, active: !demoOnly && status === "All" && review === "All", apply: pick({}) },
          ];
          return chips.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={c.apply}
              aria-pressed={c.active}
              className={`min-h-10 rounded-full border px-3.5 text-[14px] font-bold transition ${
                c.active
                  ? "border-[#0B2160] bg-[#0B2160] text-white"
                  : "border-[#BDBDB5] bg-white text-[#0B2160] hover:border-[#0B2160]"
              }`}
            >
              {c.label} <span className="opacity-75">{c.n}</span>
            </button>
          ));
        })()}
      </div>
      <div className="flex flex-wrap items-center gap-2.5 border-b border-[#E3E3DE] px-5 py-4">
        <input
          type="search"
          aria-label="Search jobs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search role, company or reference"
          className="min-h-12 min-w-[280px] flex-[3_1_280px] rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none placeholder:text-[#7A7F99] focus:border-[#0B2160]"
        />
        <select value={account} onChange={(e) => setAccount(e.target.value as typeof account)} className={selectCls}>
          <option value="All">Client or not</option>
          <option value="client">Signed up ({realJobs.filter((j) => accountOf(j).hasAccount).length})</option>
          <option value="prospect">Not signed up ({realJobs.filter((j) => !accountOf(j).hasAccount).length})</option>
        </select>
        <select value={company} onChange={(e) => setCompany(e.target.value)} className={selectCls}>
          <option value="All">All employers</option>
          {companies.map((c) => (
            <option key={c} value={c}>
              {c} ({realJobs.filter((j) => employerName(j) === c).length})
            </option>
          ))}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}>
          <option value="All">All role types</option>
          {salesCategories.map((c) => (
            <option key={c} value={c}>
              {c} ({realJobs.filter((j) => catsOf(j).includes(c)).length})
            </option>
          ))}
        </select>
        <select value={payType} onChange={(e) => setPayType(e.target.value)} className={selectCls}>
          <option value="All">All pay types</option>
          {payTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select aria-label="On the website" value={onSite} onChange={(e) => setOnSite(e.target.value as typeof onSite)} className={selectCls}>
          <option value="All">Shown or hidden</option>
          <option value="hidden">Hidden from website</option>
          <option value="shown">Shown on website</option>
        </select>
        <span className="ml-auto text-[14px] text-[#3A3A40]">{rows.length} roles</span>
      </div>

      <PaginationBar
        total={rows.length}
        page={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="roles"
        className="border-b border-ink/10"
      />

      <div className="flex flex-wrap items-center gap-2.5 border-b border-[#E3E3DE] bg-[#FAFAF8] px-5 py-2">
        <label className="flex min-h-11 items-center gap-2.5 text-[15px] font-bold text-[#0B2160]">
          <input
            className="h-5 w-5 accent-[#0B2160]"
            type="checkbox"
            checked={allVisibleSelected}
            ref={(el) => {
              if (el) el.indeterminate = visibleSelectedCount > 0 && !allVisibleSelected;
            }}
            onChange={toggleSelectAll}
            disabled={!pageRows.length}
          />
          Select all on this page
        </label>
        {selected.size > 0 ? (
          <>
            <span className="text-[15px] font-bold text-[#0B2160]">{selected.size} selected</span>
            <button
              type="button"
              onClick={() => bulkDecide("Approved")}
              disabled={deciding || deleting}
              className={`${btn} bg-[#FF5A3C] text-[#0B2160] disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {deciding ? "Working…" : `Approve ${selected.size}`}
            </button>
            <button
              type="button"
              onClick={() => bulkDecide("Rejected")}
              disabled={deciding || deleting}
              className={`${btnGhost} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {deciding ? "Working…" : `Reject ${selected.size}`}
            </button>
            <button type="button" onClick={() => onHidden([...selected], true)} className={btnGhost}>
              Hide from website
            </button>
            <button type="button" onClick={() => onHidden([...selected], false)} className={btnGhost}>
              Show on website
            </button>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={deleting || deciding}
              className={`${btn} border-2 border-[#FFB3A3] text-[#A62B12] hover:bg-[#FCE3DD] disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {deleting ? "Removing…" : `Remove ${selected.size} role${selected.size === 1 ? "" : "s"}`}
            </button>
            <button type="button" onClick={() => setSelected(new Set())} className={btnGhost}>
              Clear selection
            </button>
          </>
        ) : null}
      </div>

      {!rows.length ? (
        <Empty>No roles match these filters. Roles appear here the moment an employer submits one.</Empty>
      ) : (
        <div>
          {pageRows.map((j) => {
            const decision = decisionOf(admin.jobs, j.id);
            const apps = applicationsFor(j.id);
            const expanded = open === j.id;
            const isEditing = editing === j.id;
            const acct = accountOf(j);
            return (
              <div
                key={j.id}
                className={`border-b border-[#EFEFEA] px-5 py-[18px] ${selected.has(j.id) ? "bg-[#EEF1F8]" : ""}`}
              >
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <input
                    type="checkbox"
                    checked={selected.has(j.id)}
                    onChange={() => toggleSelected(j.id)}
                    aria-label={`Select ${j.title}`}
                    className="h-5 w-5 shrink-0 accent-[#0B2160]"
                  />
                  <div className="min-w-0 flex-[999_1_360px]">
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setViewTab("view");
                          setViewJob(j.id);
                        }}
                        className="text-left text-[18px] font-extrabold tracking-[-0.01em] text-[#0B2160] hover:underline"
                      >
                        {j.title}
                      </button>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-[3px] text-[12px] font-extrabold ${
                          decision === "Approved"
                            ? "bg-[#DDF2E3] text-[#1F6B3A]"
                            : decision === "Rejected"
                              ? "bg-[#FCE3DD] text-[#A62B12]"
                              : "bg-[#FFE9A8] text-[#6B4A00]"
                        }`}
                      >
                        {decision === "Pending" ? "Waiting" : decisionLabels[decision]}
                      </span>
                      {j.status !== "Live" ? (
                        <span className="inline-flex items-center rounded-full bg-[#EFEFEA] px-2.5 py-[3px] text-[12px] font-extrabold text-[#3A3A40]">
                          {j.status}
                        </span>
                      ) : null}
                      {isDemoJob(j) ? (
                        <span className="inline-flex items-center rounded-full border-[1.5px] border-dashed border-[#3A3A40] bg-white px-2.5 py-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#3A3A40]">
                          Demo
                        </span>
                      ) : null}
                      {j.hidden ? (
                        <span className="inline-flex items-center rounded-full bg-[#EFEFEA] px-2.5 py-[3px] text-[12px] font-extrabold text-[#3A3A40]">
                          Hidden from website
                        </span>
                      ) : null}
                      {j.featured ? (
                        <span className="inline-flex items-center rounded-full bg-[#FF5A3C] px-2.5 py-[3px] text-[12px] font-extrabold text-[#0B2160]">
                          Featured
                        </span>
                      ) : null}
                      {!acct.hasAccount ? (
                        <span className="inline-flex items-center rounded-full bg-[#DCE6FF] px-2.5 py-[3px] text-[12px] font-extrabold text-[#0B2160]">
                          Posted by Closdd
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-[15px] text-[#3A3A40]">
                      {employerName(j)} · {j.location || "Location TBC"} · {j.pay || "Pay on application"}
                    </p>
                    <p className="text-[14px] text-[#5A5A60]">
                      <span className="font-mono text-[12px] font-medium text-[#0B2160]">REF {j.ref || "—"}</span> · Posted{" "}
                      {dayOf(j.createdAt) || "recently"}
                      {decision !== "Pending" ? ` · ${apps.length} applicants` : ""}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="max-w-full truncate rounded-md bg-[#F3F3F0] px-2.5 py-[3px] font-mono text-[12px] text-[#3A3A40]">
                        closdd.com/jobs/{(j.ref || j.id).toLowerCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-[1_1_260px] flex-wrap justify-end gap-2">
                    {decision === "Pending" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setRejecting({ id: "", reason: "" });
                            setApproving(approving === j.id ? "" : j.id);
                          }}
                          className="min-h-11 rounded-[4px] bg-[#FF5A3C] px-[18px] text-[15px] font-extrabold text-[#0B2160]"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setApproving("");
                            setRejecting(rejecting.id === j.id ? { id: "", reason: "" } : { id: j.id, reason: "" });
                          }}
                          className="min-h-11 rounded-[4px] border-2 border-[#0B2160] bg-white px-4 text-[15px] font-extrabold text-[#0B2160]"
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onDecide(j, "Pending")}
                        title="Move back to waiting"
                        className={`min-h-11 rounded-[4px] border-2 px-4 text-[15px] font-extrabold ${
                          decision === "Approved"
                            ? "border-[#1F6B3A] bg-[#DDF2E3] text-[#1F6B3A]"
                            : "border-[#A62B12] bg-[#FCE3DD] text-[#A62B12]"
                        }`}
                      >
                        {decision === "Approved" ? "Approved" : "Not approved"}
                      </button>
                    )}
                    <span className="relative inline-flex">
                      <button
                        type="button"
                        onClick={() => setMenuFor(menuFor === j.id ? "" : j.id)}
                        aria-expanded={menuFor === j.id}
                        aria-haspopup="menu"
                        aria-label={`More for ${j.title}`}
                        className="h-11 w-11 rounded-[4px] border border-[#BDBDB5] bg-white text-[18px] font-extrabold leading-none text-[#0B2160]"
                      >
                        ⋯
                      </button>
                      {menuFor === j.id ? (
                        <div
                          role="menu"
                          aria-label={`Actions for ${j.title}`}
                          className="absolute right-0 top-full z-30 mt-1.5 flex min-w-[210px] flex-col rounded-lg border border-[#BDBDB5] bg-white p-1.5 shadow-[0_12px_28px_rgba(11,33,96,0.14)]"
                        >
                          {(
                            [
                              ["View", () => { setViewTab("view"); setViewJob(j.id); }],
                              ["Edit", () => { setViewTab("edit"); setViewJob(j.id); }],
                              ...(decision === "Pending"
                                ? []
                                : [
                                    [`View candidates (${apps.length})`, () => { setViewTab("view"); setViewJob(j.id); }],
                                    ...(decision === "Approved"
                                      ? [[j.featured ? "Remove featured" : "Mark featured", () => onDecide(j, "Approved", !j.featured)] as [string, () => void]]
                                      : []),
                                    [j.hidden ? "Show on website" : "Hide from website", () => onHidden([j.id], !j.hidden)] as [string, () => void],
                                    [
                                      "Copy link",
                                      () => void navigator.clipboard?.writeText(`https://closdd.com/jobs/${(j.ref || j.id).toLowerCase()}`),
                                    ] as [string, () => void],
                                    ...(["Live", "Paused", "Closed"] as const)
                                      .filter((st) => st !== j.status)
                                      .map((st) => [`Mark ${st.toLowerCase()}`, () => onStatus(j.id, st)] as [string, () => void]),
                                    ["Remove", () => {
                                      if (window.confirm(`Remove ${j.title} from CLOSDD? This cannot be undone.`)) onRemove(j);
                                    }] as [string, () => void],
                                  ]),
                            ] as [string, () => void][]
                          ).map(([text, run]) => (
                            <button
                              key={text}
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setMenuFor("");
                                run();
                              }}
                              className={`flex min-h-10 items-center rounded-md px-3 text-left text-[14px] font-bold hover:bg-[#F3F3F0] ${
                                text === "Remove" ? "text-[#A62B12]" : "text-[#0B2160]"
                              }`}
                            >
                              {text}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </span>
                  </div>
                </div>

                {rejecting.id === j.id && decision === "Pending" ? (
                  <form
                    className="mt-3 rounded-[8px] border border-[#F5CFC4] bg-[#FDF0EC] px-4 py-3.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      onDecide(j, "Rejected", undefined, rejecting.reason.trim());
                      setRejecting({ id: "", reason: "" });
                    }}
                  >
                    <label htmlFor={`reject-${j.id}`} className="block text-[15px] font-extrabold text-[#0B2160]">
                      Why is it rejected?
                    </label>
                    <p className="mt-0.5 text-[13px] text-[#3A3A40]">
                      The employer sees this on My roles, fixes the advert and sends it back for review.
                    </p>
                    <textarea
                      id={`reject-${j.id}`}
                      required
                      rows={2}
                      value={rejecting.reason}
                      onChange={(e) => setRejecting({ id: j.id, reason: e.target.value })}
                      placeholder="e.g. The pay is missing. Add a base salary, or say it's commission only, then resubmit."
                      className="mt-2 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 py-2 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button type="submit" className="min-h-11 rounded-[4px] bg-[#A62B12] px-[18px] text-[15px] font-extrabold text-white">
                        Reject and tell the employer
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejecting({ id: "", reason: "" })}
                        className="px-2 text-[14px] font-bold text-[#0B2160] underline underline-offset-[3px]"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : null}

                {approving === j.id && decision === "Pending" ? (
                  <div className="mt-3 rounded-[8px] border border-[#BFE3CB] bg-[#E8F5EC] px-4 py-3.5">
                    <p className="text-[15px] font-extrabold text-[#0B2160]">Approve and put it live. Feature it too?</p>
                    <p className="mt-0.5 text-[13px] text-[#3A3A40]">
                      Featured roles sit at the top of Find jobs with a Featured tag. You can change this later from the ⋯ menu.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setApproving("");
                          onDecide(j, "Approved", true);
                        }}
                        className="min-h-11 rounded-[4px] bg-[#FF5A3C] px-[18px] text-[15px] font-extrabold text-[#0B2160]"
                      >
                        Approve and feature
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setApproving("");
                          onDecide(j, "Approved", false);
                        }}
                        className="min-h-11 rounded-[4px] border-2 border-[#0B2160] bg-white px-4 text-[15px] font-extrabold text-[#0B2160]"
                      >
                        Approve, don't feature
                      </button>
                      <button type="button" onClick={() => setApproving("")} className="px-2 text-[14px] font-bold text-[#0B2160] underline underline-offset-[3px]">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}

                {expanded ? (
                  <div className={`mt-4 grid gap-5 rounded-lg border border-[#E3E3DE] bg-[#FAFAF8] px-5 py-5 ${isEditing ? "" : "lg:grid-cols-2"}`}>
                    <div>

                      {isEditing ? (
                        <JobEditor job={j} onEdit={onEdit} />
                      ) : (

                        <>
                          <div className="rounded-xl border border-purple-strong/10 bg-purple-strong px-4 py-4">
                            <p className="font-serif text-[17px] leading-[1.15] tracking-[-0.01em] text-white">{j.title}</p>
                            <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70">
                              {employerName(j)} · {j.location || "Location TBC"} · {j.setting} · {j.contract}
                            </p>
                            <p className="mt-2 text-[11px] text-white/60">
                              Ref {j.ref || "—"} ·{" "}
                              {j.status === "Live" && decision === "Approved"
                                ? "Live on the job board"
                                : "Not live: hidden from the board, nobody can apply"}
                            </p>
                          </div>

                          {j.tags?.length ? <TagRow tags={j.tags} size="sm" className="mt-3" /> : null}

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-purple-strong/10 bg-purple-soft/40 px-4 py-3">
                            <div>
                              <p className={label}>Pay</p>
                              <p className="mt-1 text-[14px] font-semibold text-ink">{j.pay || "Pay on application"}</p>
                            </div>
                            <div className="sm:text-right">
                              <p className={label}>Pay structure</p>
                              <p className="mt-1 text-[12px] font-semibold text-purple-strong">{j.payStructure || "—"}</p>
                            </div>
                          </div>

                          <p className={`${label} mt-4`}>The advert</p>
                          <p className="mt-2.5 whitespace-pre-line text-[12px] leading-[1.7] text-ink/70">
                            {j.advert ? formatAdvert(j.advert) : "No advert copy provided."}
                          </p>
                          <div className="mt-4 grid gap-2">
                            <Fact k="Seniority" v={j.seniority} />
                            <Fact k="Contract" v={j.contract} />
                            <Fact k="Setting" v={j.setting} />
                            <Fact k="Expected salary" v={`${j.payStructure} ${j.pay}`.trim() || "On application"} />
                            <Fact k="Date posted" v={dayOf(j.createdAt) || "Not recorded"} />
                            <Fact k="Company email" v={employerEmail(j) || "Not recorded"} />
                            <Fact k="Posted by" v={`${recruiter} · ${employerName(j)}`} />
                          </div>
                          <Link to="/jobs/$jobId" params={{ jobId: j.id }} className={`${btnGhost} mt-4`}>
                            View live advert
                          </Link>
                        </>
                      )}
                    </div>
                    <div>
                      <p className={label}>Applications ({apps.length})</p>
                      {apps.length ? (
                        <ul className="mt-2.5 grid gap-2">
                          {apps.map((a) => {
                            const d = applicationDecision(admin, a.id, (a.email || a.name).toLowerCase());
                            return (
                              <li
                                key={a.id}
                                className="flex items-center justify-between gap-3 rounded-xl border border-purple-strong/10 bg-white px-3.5 py-2.5"
                              >
                                <span className="min-w-0 truncate text-[12px] font-semibold text-ink">{a.name}</span>
                                <span className="shrink-0 text-[11px] text-ink/50">{a.years}</span>
                                <Pill text={decisionLabels[d]} tone={decisionStyles[d]} />
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="mt-2.5 text-[12px] text-ink/45">No applications yet.</p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <PaginationBar
        total={rows.length}
        page={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="roles"
        className="border-t border-ink/10"
      />
    </div>
  );
}

/** Full job page (design: "Jobs, public view and edit, approve, reject"). */
function JobPage({
  job,
  decision,
  apps,
  qualified,
  employer,
  email,
  hasAccount,
  recruiter,
  tab,
  setTab,
  admin,
  onBack,
  onDecide,
  onStatus,
  onEdit,
  onFindCandidates,
  onRemove,
}: {
  job: EmployerJob;
  decision: Decision;
  apps: Application[];
  qualified: number;
  employer: string;
  email: string;
  hasAccount: boolean;
  recruiter: string;
  tab: "view" | "edit";
  setTab: (t: "view" | "edit") => void;
  admin: AdminStore;
  onBack: () => void;
  onDecide: (d: Decision) => void;
  onStatus: (s: EmployerJob["status"]) => void;
  onEdit: (id: string, patch: Partial<EmployerJob>) => void;
  onFindCandidates: () => void;
  onRemove: () => void;
}) {
  const live = job.status === "Live" && decision === "Approved";
  const chip =
    decision === "Approved"
      ? "bg-[#DDF2E3] text-[#1F6B3A]"
      : decision === "Rejected"
        ? "bg-[#FCE3DD] text-[#A62B12]"
        : "bg-[#FFE9A8] text-[#6B4A00]";
  const outline =
    "inline-flex min-h-12 items-center rounded-[4px] border-2 border-[#0B2160] bg-white px-5 text-[15px] font-extrabold text-[#0B2160]";
  return (
    <div className="mx-auto w-full max-w-[1200px]">
      <button type="button" onClick={onBack} className="inline-flex min-h-10 items-center text-[15px] font-bold text-[#0B2160] underline underline-offset-[3px]">
        ← Back to Jobs
      </button>

      <section aria-label="Review" className="mt-3 rounded-xl border border-[#E3E3DE] bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              <h1 className="text-[clamp(24px,2.6vw,32px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-[#0B2160]">{job.title}</h1>
              <span className={`inline-flex rounded-full px-2.5 py-[3px] text-[12px] font-extrabold ${chip}`}>
                {decision === "Pending" ? "Waiting" : decisionLabels[decision]}
              </span>
              {job.status !== "Live" ? (
                <span className="inline-flex rounded-full bg-[#EFEFEA] px-2.5 py-[3px] text-[12px] font-extrabold text-[#3A3A40]">{job.status}</span>
              ) : null}
              <span className="inline-flex rounded-full bg-[#DCE6FF] px-2.5 py-[3px] text-[12px] font-extrabold text-[#0B2160]">
                {recruiter === employer || !hasAccount ? "Posted by CLOSDD" : "Posted by employer"}
              </span>
            </div>
            <p className="mt-1 text-[15px] text-[#3A3A40]">
              {employer} · {hasAccount ? "employer has an account" : "no employer account yet"} · posted {dayOf(job.createdAt) || "recently"}
            </p>
          </div>
          <div role="group" aria-label="Actions" className="flex flex-wrap gap-2.5">
            {decision === "Pending" ? (
              <>
                <button type="button" onClick={() => onDecide("Approved")} className="min-h-12 rounded-[4px] bg-[#FF5A3C] px-[22px] text-[15px] font-extrabold text-[#0B2160]">
                  Approve and publish
                </button>
                <button type="button" onClick={() => onDecide("Rejected")} className={outline}>
                  Reject
                </button>
              </>
            ) : (
              <button type="button" onClick={() => onDecide("Pending")} className={outline}>
                {decision === "Approved" ? "Approved · move back to waiting" : "Not approved · move back to waiting"}
              </button>
            )}
            <button type="button" onClick={() => setTab("edit")} className={outline}>
              Edit
            </button>
            {live ? (
              <Link to="/jobs/$jobId" params={{ jobId: job.id }} target="_blank" className="inline-flex min-h-12 items-center gap-2 rounded-[4px] bg-[#0B2160] px-5 text-[15px] font-extrabold text-white">
                Open on website ↗
              </Link>
            ) : null}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2.5 border-t border-[#E3E3DE] pt-4">
          <label htmlFor="jp-status" className="text-[14px] font-semibold text-[#0B2160]">Status</label>
          <select id="jp-status" value={job.status} onChange={(e) => onStatus(e.target.value as EmployerJob["status"])} className="min-h-11 rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160]">
            <option value="Live">Live</option>
            <option value="Paused">Paused</option>
            <option value="Closed">Closed</option>
          </select>
          <button type="button" onClick={onFindCandidates} className={`${outline} !min-h-11`}>Find matching candidates</button>
          <button type="button" onClick={onRemove} className="inline-flex min-h-11 items-center rounded-[4px] border-2 border-[#A62B12] bg-white px-4 text-[15px] font-extrabold text-[#A62B12]">
            Remove job
          </button>
        </div>
      </section>

      <div role="tablist" aria-label="Job" className="mt-5 flex max-w-[360px] gap-1 rounded-full bg-[#E3E3DE] p-1">
        {(["view", "edit"] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`min-h-11 flex-1 rounded-full text-[15px] font-extrabold ${tab === k ? "bg-[#0B2160] text-white" : "text-[#0B2160]"}`}>
            {k === "view" ? "Public view" : "Edit"}
          </button>
        ))}
      </div>

      {tab === "edit" ? (
        <div className="mt-6 rounded-xl border border-[#E3E3DE] bg-white p-5 sm:p-8">
          <JobEditor job={job} onEdit={onEdit} />
        </div>
      ) : (
        <div className="mt-6 flex flex-wrap items-start gap-5">
          <div className="min-w-0 flex-[999_1_560px] rounded-xl border border-[#E3E3DE] bg-white p-5 sm:p-8">
            <p className="font-mono text-[12px] uppercase tracking-[0.12em] text-[#D6361C]">
              {live ? "Live on the board" : "Not live: hidden from the board"}
            </p>
            <h2 className="mt-1.5 text-[clamp(28px,3vw,40px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">{job.title}</h2>
            <p className="mt-2 text-[16px] text-[#3A3A40]">
              {employer} · {job.location || "Location TBC"} · {job.setting} · {job.contract}
            </p>
            {job.tags?.length ? <TagRow tags={job.tags} size="sm" className="mt-3" /> : null}
            <dl className="mt-5 grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr))]">
              {[
                ["Pay", job.pay || "Pay on application"],
                ["Pay structure", job.payStructure || "—"],
                ["Seniority", job.seniority || "—"],
                ["Reference", job.ref || "—"],
              ].map(([k, v]) => (
                <div key={k} className="rounded-[10px] bg-[#F3F3F0] px-3.5 py-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#5A5A60]">{k}</dt>
                  <dd className="mt-0.5 text-[15px] font-bold text-[#0B2160]">{v}</dd>
                </div>
              ))}
            </dl>
            <p className={`${label} mt-6`}>The advert</p>
            <p className="mt-2 whitespace-pre-line text-[17px] leading-[1.65] text-[#2A2A30]">{job.advert ? formatAdvert(job.advert) : "No advert copy provided."}</p>
            <div className="mt-6 grid gap-2">
              <Fact k="Date posted" v={dayOf(job.createdAt) || "Not recorded"} />
              <Fact k="Company email" v={email || "Not recorded"} />
              <Fact k="Posted by" v={`${recruiter} · ${employer}`} />
            </div>
          </div>
          <aside className="min-w-0 flex-[1_1_320px] rounded-xl border border-[#E3E3DE] bg-white p-5">
            <p className={label}>Applications ({apps.length})</p>
            <p className="mt-1 text-[14px] text-[#3A3A40]">{qualified} approved</p>
            {apps.length ? (
              <ul className="mt-3 grid gap-2">
                {apps.map((a) => {
                  const d = applicationDecision(admin, a.id, (a.email || a.name).toLowerCase());
                  return (
                    <li key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E3E3DE] bg-white px-3.5 py-2.5">
                      <span className="min-w-0 truncate text-[15px] font-bold text-[#0B2160]">{a.name}</span>
                      <span className="shrink-0 text-[13px] text-[#5A5A60]">{a.years}</span>
                      <Pill text={decisionLabels[d]} tone={decisionStyles[d]} />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-3 text-[15px] text-[#3A3A40]">No applications yet.</p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

/** Manual creation form: every field an employer fills in on /post-job, plus which employer it belongs to. */
/** Minimal RFC4180-ish CSV parser: handles quoted fields, embedded commas/newlines and "" escapes. */
function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      pushField();
    } else if (c === "\n") {
      pushField();
      pushRow();
    } else if (c === "\r") {
      // ignore: a following \n (if any) ends the row
    } else {
      field += c;
    }
  }
  if (field.length || row.length) {
    pushField();
    pushRow();
  }

  const cleaned = rows.filter((r) => r.some((cell) => cell.trim() !== ""));
  if (!cleaned.length) return [];
  const headers = cleaned[0]!.map((h) => h.trim().toLowerCase());
  return cleaned.slice(1).map((r) => {
    const record: Record<string, string> = {};
    headers.forEach((h, idx) => {
      record[h] = (r[idx] ?? "").trim();
    });
    return record;
  });
}

/** Drops empty-string/undefined fields so optional `CreateJobInput` keys are omitted rather than set to undefined. */
function resolveEmployerIdByCompany(crmEmployers: EmployerRecord[], company: string): string | undefined {
  const q = company.trim().toLowerCase();
  if (!q) return undefined;
  return crmEmployers.find((e) => e.company.trim().toLowerCase() === q)?.id;
}

/** Maps one parsed CSV row to a create-job payload, or reports why the row can't be imported. */
function csvRowToJobInput(
  record: Record<string, string>,
  crmEmployers: EmployerRecord[],
  demo: boolean,
  autoApprove: boolean,
): { input: CreateJobInput; warning?: string } | { error: string } {
  const isTemplate = "job_title" in record || "company_name" in record;
  const title = (record["job_title"] ?? record["title"] ?? "").trim();
  if (!title) return { error: "Missing job_title" };

  const company = (
    record["company_name"] ??
    record["company"] ??
    record["company name"] ??
    record["employer"] ??
    record["employer name"] ??
    record["organization"] ??
    record["organisation"] ??
    ""
  ).trim();
  if (isTemplate) {
    const missing = ["company_name", "contact_email", "role_type", "location", "setting", "contract", "pay_text", "the_role"].filter(
      (k) => !(record[k] ?? "").trim(),
    );
    if (missing.length) return { error: `Missing ${missing.join(", ")}` };
  }
  // Employer is linked by contact email first, then company name. If neither
  // matches, the API creates a bare employer record from the company text.
  const contactEmail = (record["contact_email"] ?? "").trim().toLowerCase();
  const matchedEmployerId =
    (contactEmail ? crmEmployers.find((e) => e.email.trim().toLowerCase() === contactEmail)?.id : undefined) ??
    resolveEmployerIdByCompany(crmEmployers, company);
  const objectives = (record["objectives"] ?? "")
    .split("|")
    .map((t) => t.trim())
    .filter(Boolean);
  const advert = isTemplate
    ? [
        record["who_they_are"],
        record["the_role"],
        objectives.length ? `Objectives:\n${objectives.map((o) => `- ${o}`).join("\n")}` : "",
        record["looking_for"] ? `What we're looking for: ${record["looking_for"]}` : "",
        record["what_you_get"] ? `What you get: ${record["what_you_get"]}` : "",
      ]
        .map((part) => (part ?? "").trim())
        .filter(Boolean)
        .join("\n\n")
    : (record["advert"] ?? "");
  const tags = isTemplate
    ? [record["role_type"], record["sector"]].map((t) => (t ?? "").trim()).filter(Boolean)
    : (record["tags"] ?? "")
        .split(/[;|]/)
        .map((t) => t.trim())
        .filter(Boolean);
  const rowStatus = (record["status"] ?? "").trim().toLowerCase();

  const input = compactJobInput({
    title,
    employerId: matchedEmployerId ?? "",
    company,
    ref: record["ref"] ?? "",
    location: record["location"] ?? "",
    seniority: record["seniority"] ?? "",
    contract: record["contract"] ?? "",
    setting: record["setting"] ?? "",
    payStructure: record["paystructure"] ?? "",
    pay: record["pay_text"] ?? record["pay"] ?? "",
    advert,
    anonymous: /^y(es)?$/i.test(record["anonymous"] ?? ""),
    tags,
    demo: demo || /^y(es)?$/i.test(record["demo"] ?? ""),
    autoApprove: autoApprove || rowStatus === "live",
  });

  if (rowStatus === "draft") {
    return { input, warning: "Status is draft: imported as awaiting review" };
  }
  if (company && !matchedEmployerId) {
    return { input, warning: `New employer record will be created for "${company}"` };
  }
  return { input };
}

type CsvRowResult = {
  row: number;
  title: string;
  input?: CreateJobInput;
  error?: string;
  warning?: string;
  status: "pending" | "created" | "failed";
};

/** Bulk job import: pick a CSV, preview what will be created, then post each row in turn. */
function JobCsvImport({
  crmEmployers,
  onCreate,
  onDone,
}: {
  crmEmployers: EmployerRecord[];
  onCreate: (input: CreateJobInput) => Promise<EmployerJob>;
  onDone: () => void;
}) {
  const [fileName, setFileName] = useState("");
  const [records, setRecords] = useState<Record<string, string>[]>([]);
  const [demo, setDemo] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [rows, setRows] = useState<CsvRowResult[]>([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);

  const buildRows = (recs: Record<string, string>[], demoFlag: boolean, autoApproveFlag: boolean): CsvRowResult[] =>
    recs.map((record, i) => {
      const title = (record["job_title"] ?? record["title"] ?? "").trim();
      const result = csvRowToJobInput(record, crmEmployers, demoFlag, autoApproveFlag);
      if ("error" in result) {
        return { row: i + 2, title: title || "(no title)", error: result.error, status: "failed" };
      }
      return {
        row: i + 2,
        title,
        input: result.input,
        status: "pending" as const,
        ...(result.warning !== undefined ? { warning: result.warning } : {}),
      };
    });

  const onFile = async (file: File) => {
    setFileName(file.name);
    setDone(false);
    const text = await file.text();
    const recs = parseCsv(text);
    setRecords(recs);
    setRows(buildRows(recs, demo, autoApprove));
  };

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    if (records.length) setRows(buildRows(records, next, autoApprove));
  };

  const toggleAutoApprove = () => {
    const next = !autoApprove;
    setAutoApprove(next);
    if (records.length) setRows(buildRows(records, demo, next));
  };

  const importable = rows.filter((r) => r.input);

  const runImport = async () => {
    setImporting(true);
    const next = [...rows];
    for (let i = 0; i < next.length; i++) {
      const r = next[i]!;
      if (!r.input) continue;
      try {
        await onCreate(r.input);
        next[i] = { ...r, status: "created" };
      } catch (e) {
        next[i] = { ...r, status: "failed", error: e instanceof Error ? e.message : "Could not create this role." };
      }
      setRows([...next]);
    }
    setImporting(false);
    setDone(true);
  };

  const createdCount = rows.filter((r) => r.status === "created").length;
  const failedCount = rows.filter((r) => r.status === "failed").length;
  const code = "rounded bg-ink/5 px-1 py-0.5";

  return (
    <div className="grid gap-4">
      <div className="grid gap-2 rounded-xl border border-dashed border-purple-strong/25 bg-white px-4 py-4 text-[12px] text-ink/60">
        <p>
          Fill in the Excel template (one row per job), save it as CSV UTF-8 and upload it here. Required columns:{" "}
          <code className={code}>company_name</code>, <code className={code}>contact_email</code>,{" "}
          <code className={code}>job_title</code>, <code className={code}>role_type</code>, <code className={code}>location</code>,{" "}
          <code className={code}>setting</code>, <code className={code}>contract</code>, <code className={code}>pay_text</code> and{" "}
          <code className={code}>the_role</code>. Employers are matched by contact email, then company name; any not found are created
          automatically. Status <code className={code}>live</code> posts a role as approved, <code className={code}>review</code> (or blank) sends it for review.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
              e.target.value = "";
            }}
            className="cursor-pointer text-[12px] text-ink/70 file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-purple-strong/25 file:bg-white file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-ink/70 file:transition hover:file:border-purple-strong/45 hover:file:text-ink"
          />
          {fileName ? <span className="text-[11px] text-ink/45">{fileName}</span> : null}
        </div>
        <label className="flex w-fit items-center gap-1.5 text-[12px] font-semibold text-ink/70">
          <input type="checkbox" checked={demo} onChange={toggleDemo} />
          Demo
          <span className="font-normal text-ink/45">— tag this whole batch as demo data (shows a "Demo" badge in the jobs table)</span>
        </label>
        <label className="flex w-fit items-center gap-1.5 text-[12px] font-semibold text-ink/70">
          <input type="checkbox" checked={autoApprove} onChange={toggleAutoApprove} />
          Auto approve
          <span className="font-normal text-ink/45">— post this whole batch as already approved, skipping manual review</span>
        </label>
      </div>

      {rows.length ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-ink/55">
              {rows.length} row{rows.length === 1 ? "" : "s"} found · {importable.length} ready to import
              {rows.length - importable.length > 0 ? ` · ${rows.length - importable.length} skipped (no title)` : ""}
            </p>
            {done ? (
              <p className="text-[11px] font-semibold text-ink/70">
                {createdCount} created{failedCount ? `, ${failedCount} failed` : ""}
              </p>
            ) : null}
          </div>
          <div className="mt-2 max-h-[320px] overflow-y-auto rounded-xl border border-purple-strong/10">
            <table className="w-full text-left text-[12px]">
              <thead className="sticky top-0 bg-[#fbfcfe] text-[10px] font-semibold uppercase tracking-[0.1em] text-ink/40">
                <tr>
                  <th className="px-3 py-2">Row</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {rows.map((r) => (
                  <tr key={r.row}>
                    <td className="px-3 py-2 text-ink/45">{r.row}</td>
                    <td className="px-3 py-2 text-ink">{r.title || "—"}</td>
                    <td className="px-3 py-2">
                      {r.status === "created" ? (
                        <span className="text-tag-green">Created</span>
                      ) : r.status === "failed" ? (
                        <span className="text-tag-red">{r.error || "Failed"}</span>
                      ) : r.warning ? (
                        <span className="text-tag-amber">{r.warning}</span>
                      ) : (
                        <span className="text-ink/45">Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={runImport}
              disabled={importing || done || !importable.length}
              className={`${btnSolid} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {importing
                ? "Importing…"
                : done
                  ? "Import complete"
                  : `Import ${importable.length} job${importable.length === 1 ? "" : "s"}`}
            </button>
            <button type="button" onClick={onDone} className={btnGhost}>
              {done ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** 1 to 10 score control. Rating is per candidate, so it follows them everywhere. */
function RatingSelect({
  value,
  onChange,
  compact = true,
}: {
  value: number;
  onChange: (n: number) => void;
  compact?: boolean;
}) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      aria-label="Rate candidate out of 10"
      className={`w-full rounded-md border font-bold outline-none transition ${ratingStyle(value)} ${
        compact ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-1 text-[12px]"
      }`}
    >
      <option value="">Rate</option>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>
          {n}/10
        </option>
      ))}
    </select>
  );
}

function appKeyOf(a: Application) {
  return (a.email || a.name).toLowerCase();
}

type AppFilter = "All" | "Awaiting Review" | "History" | "Approved" | "Not Approved";

const appFilters: AppFilter[] = ["Awaiting Review", "History"];
const appFilterLabel: Partial<Record<AppFilter, string>> = { "Awaiting Review": "Waiting" };

const filterDecision: Record<Exclude<AppFilter, "All" | "History">, Decision> = {
  "Awaiting Review": "Pending",
  Approved: "Approved",
  "Not Approved": "Rejected",
};

/**
 * Every application ever made, in one reviewable list.
 *
 * The admin reviews the candidate before an employer ever sees them: open the
 * profile, listen to the pitch, check the CV, then approve or not approve.
 */
/** "Applied within" windows shared by the applications list and Distribution. */
const appliedWindows: { label: string; days: number }[] = [
  { label: "All time", days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 2 weeks", days: 14 },
  { label: "Last 3 weeks", days: 21 },
  { label: "Last month", days: 30 },
  { label: "Last 2 months", days: 60 },
  { label: "Last 3 months", days: 90 },
];

/** Short date plus how long ago, e.g. "12 Aug 2026 · 5 days ago". */
function appliedDate(iso?: string | null) {
  if (!iso) return { date: "—", ago: "" };
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { date: String(iso), ago: "" };
  const date = new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const days = Math.floor((Date.now() - t) / 86_400_000);
  const ago = days <= 0 ? "Today" : days === 1 ? "Yesterday" : days < 14 ? `${days} days ago` : days < 60 ? `${Math.round(days / 7)} weeks ago` : `${Math.round(days / 30)} months ago`;
  return { date, ago };
}

/** Dropdown of checkboxes: pick any number of options; none picked means no filter. */
function MultiFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <details className="relative">
      <summary className="cursor-pointer list-none rounded-lg border border-purple-strong/15 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-ink/70">
        {value.length ? `${label} (${value.length})` : `All ${label.toLowerCase()}`}
      </summary>
      <div className="absolute z-20 mt-1 grid min-w-[180px] gap-1 rounded-lg border border-purple-strong/15 bg-white p-2 shadow-lg">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-2 text-[11px] text-ink/80">
            <input
              type="checkbox"
              checked={value.includes(o)}
              onChange={() => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o])}
            />
            {o}
          </label>
        ))}
        {value.length ? (
          <button type="button" className="text-left text-[11px] font-semibold text-purple-strong" onClick={() => onChange([])}>
            Clear
          </button>
        ) : null}
      </div>
    </details>
  );
}

function ApplicationsTab({
  apps,
  directory,
  admin,
  crmEmployers,
  jobTitle,
  jobById,
  employerName,
  assignJobs,
  distributions,
  onAssign,
  onUnassign,
  onUnassignMany,
  onRemoveApplications,
  onDecide,
  onDecideMany,
  onRate,
  onSalary,
}: {
  apps: Application[];
  /** Every candidate with a profile, including admin-created ones who haven't applied to anything yet — lets Distribution assign them their first role. */
  directory: CandidateDirectoryEntry[];
  admin: AdminStore;
  crmEmployers: EmployerRecord[];
  jobTitle: (id: string) => string;
  jobById: Map<string, EmployerJob>;
  employerName: (j?: EmployerJob) => string;
  assignJobs: EmployerJob[];
  distributions: Distribution[];
  onAssign: (people: { key: string; name: string }[], jobIds: string[]) => void;
  onUnassign: (d: Distribution) => void;
  onUnassignMany: (candidateKeys: string[], company?: string) => void;
  onRemoveApplications: (applicationIds: string[]) => void;
  onDecide: (a: Application, d: Decision) => void;
  onDecideMany: (apps: Application[], d: Decision) => void;
  onRate: (key: string, name: string, n: number) => void;
  onSalary: (key: string, name: string, min: number, max: number) => void;

}) {
  const [filter, setFilter] = useState<AppFilter>("Awaiting Review");
  /** "Applied within" window, used to grab the freshest candidates. */
  const [within, setWithin] = useState("All time");
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [jobFilter, setJobFilter] = useState("All");
  /** All-applications view: narrow the list to one employer. */
  const [empFilter, setEmpFilter] = useState("All");
  const [sort, setSort] = useState<"Newest" | "Oldest" | "Candidate A-Z" | "Job A-Z">("Newest");
  const [groupByJob, setGroupByJob] = useState(false);
  const [pitchOnly, setPitchOnly] = useState(false);
  const [locFilter, setLocFilter] = useState("All");
  const [expFilter, setExpFilter] = useState("All");
  const [ratingFilter, setRatingFilter] = useState("All");
  const [salaryFilter, setSalaryFilter] = useState("All");
  const [moreApp, setMoreApp] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [view, setView] = useState<"companies" | "roles" | "list" | "candidates" | "distribution">("list");
  const [companyQuery, setCompanyQuery] = useState("");
  const [candQuery, setCandQuery] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [candSort, setCandSort] = useState<"Newest" | "Highest rated" | "Lowest rated" | "Name A-Z">("Newest");
  const [distQuery, setDistQuery] = useState("");
  const [distTitle, setDistTitle] = useState("");
  const [distCategory, setDistCategory] = useState("All");
  /** Salary expectation filter: a band label, "Fits role pay", or "All". */
  const [distSalary, setDistSalary] = useState("All");

  const [distCompany, setDistCompany] = useState("All");
  const [distLocation, setDistLocation] = useState("All");
  const [distRoleTag, setDistRoleTag] = useState("All");
  /** Admin-only candidate attributes; multi-select, empty means no filter. */
  const [distExperience, setDistExperience] = useState<string[]>([]);
  const [distSalesTypes, setDistSalesTypes] = useState<string[]>([]);

  const [distMinRating, setDistMinRating] = useState(0);
  /** Exact applied-date range for shortlisting (inclusive, empty means open). */
  const [distFrom, setDistFrom] = useState("");
  const [distTo, setDistTo] = useState("");
  const [distSort, setDistSort] = useState<
    "Highest rated" | "Lowest rated" | "Applied: newest" | "Applied: oldest" | "Name A-Z" | "Company A-Z"
  >("Highest rated");
  const [distPicked, setDistPicked] = useState<string[]>([]);
  const [assignJobIds, setAssignJobIds] = useState<string[]>([]);
  /** Narrows the "assign to role" picker to roles whose employer has this sales category. */
  const [assignCategory, setAssignCategory] = useState("All");
  /** Narrows the "assign to role" picker to roles with or without an existing assignment. */
  const [assignStatus, setAssignStatus] = useState<"all" | "unassigned" | "assigned">("all");
  const [roleQuery, setRoleQuery] = useState("");
  const [roleEmp, setRoleEmp] = useState("All");
  const [roleDateFilter, setRoleDateFilter] = useState<"All time" | "Last 7 days" | "Last 30 days" | "Last 90 days">(
    "All time",
  );
  const [roleSort, setRoleSort] = useState<
    "Latest posted" | "Oldest posted" | "Needs review" | "Most applications" | "Employer A-Z"
  >("Latest posted");



  const statusOf = (a: Application) => applicationDecision(admin, a.id, appKeyOf(a));
  const order = new Map(apps.map((a, i) => [a.id, i]));

  const demoCompanies = new Set(crmEmployers.filter((e) => e.demo).map((e) => e.company.trim().toLowerCase()));
  /** Guards the Candidates browser against a name that's actually an employer's company name (e.g. test data entered on the wrong form). */
  const employerCompanyNames = new Set(crmEmployers.map((e) => e.company.trim().toLowerCase()));
  /** Demo companies/roles clutter filter dropdowns with sample data — the underlying row content stays untouched. */
  const isDemoJobId = (id: string) => {
    const j = jobById.get(id);
    if (!j) return false;
    return Boolean(j.demo) || demoCompanies.has((j.company || "").trim().toLowerCase());
  };
  /** Employer sales category (High Ticket Sales / Appointment Setter), keyed by company name for matching against a job's `company`. */
  const categoryByCompany = new Map(
    crmEmployers.filter((e) => e.category).map((e) => [e.company.trim().toLowerCase(), e.category as string] as const),
  );

  const withinDays = appliedWindows.find((w) => w.label === within)?.days ?? 0;
  const inWindow = (a: Application) => {
    if (!withinDays) return true;
    const t = new Date(a.appliedAt).getTime();
    if (Number.isNaN(t)) return true;
    return Date.now() - t <= withinDays * 24 * 60 * 60 * 1000;
  };

  /** History is everything already decided: approved and not approved together. */
  const inFilter = (f: AppFilter, a: Application) =>
    f === "All" ? true : f === "History" ? statusOf(a) !== "Pending" : statusOf(a) === filterDecision[f];
  const byStatus = apps
    .filter((a) => inFilter(filter, a))
    .filter(inWindow);
  const q = query.trim().toLowerCase();
  const rows = byStatus
    .filter((a) => empFilter === "All" || employerName(jobById.get(a.jobId)) === empFilter)
    .filter((a) => jobFilter === "All" || a.jobId === jobFilter)
    .filter((a) => !pitchOnly || a.hasPitch)
    .filter((a) => locFilter === "All" || a.location === locFilter)
    .filter((a) => expFilter === "All" || a.years === expFilter)
    .filter((a) => salaryFilter === "All" || a.salaryExpectation === salaryFilter)
    .filter((a) => {
      if (ratingFilter === "All") return true;
      const r = ratingOf(admin.ratings, appKeyOf(a));
      return ratingFilter === "0" ? r === 0 : r >= Number(ratingFilter);
    })
    .filter((a) =>
      !q
        ? true
        : [a.name, a.email, jobTitle(a.jobId), employerName(jobById.get(a.jobId))]
            .join(" ")
            .toLowerCase()
            .includes(q),
    )
    .sort((x, y) => {
      if (sort === "Candidate A-Z") return x.name.localeCompare(y.name);
      if (sort === "Job A-Z") return jobTitle(x.jobId).localeCompare(jobTitle(y.jobId));
      const dx = (order.get(x.id) ?? 0) - (order.get(y.id) ?? 0);
      return sort === "Oldest" ? -dx : dx;
    });

  const count = (f: AppFilter) => apps.filter((a) => inFilter(f, a)).length;
  const open = apps.find((a) => a.id === openId) ?? null;

  const jobOptions = Array.from(new Set(apps.map((a) => a.jobId)));
  const distinctOf = (pick: (a: Application) => string | undefined) =>
    Array.from(new Set(apps.map((a) => (pick(a) ?? "").trim()).filter(Boolean))).sort();
  const appLocations = distinctOf((a) => a.location);
  const appExperience = distinctOf((a) => a.years);
  const appSalaries = distinctOf((a) => a.salaryExpectation);
  /** Employers that actually have applications, for the list-view filter. Demo companies are excluded — the Demo chip elsewhere still finds them. */
  const appEmployers = Array.from(
    new Set(apps.filter((a) => !isDemoJobId(a.jobId)).map((a) => employerName(jobById.get(a.jobId)))),
  ).sort((a, b) => a.localeCompare(b));
  /** Job dropdown follows the chosen employer so the two filters stay in sync. */
  const jobFilterOptions = jobOptions.filter(
    (id) => !isDemoJobId(id) && (empFilter === "All" || employerName(jobById.get(id)) === empFilter),
  );
  // Pagination only applies to the flat (ungrouped) list — grouping by job
  // shows every group in full, since paging groups themselves isn't supported.
  const {
    page: listPage,
    setPage: setListPage,
    pageSize: listPageSize,
    setPageSize: setListPageSize,
    totalPages: listTotalPages,
    resetPage: resetListPage,
  } = usePagination(rows.length, 50);

  useEffect(() => {
    resetListPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, within, q, jobFilter, empFilter, sort, groupByJob, view, listPageSize, locFilter, expFilter, ratingFilter, salaryFilter, pitchOnly]);

  const groups = groupByJob
    ? jobOptions
        .map((id) => ({ id, rows: rows.filter((a) => a.jobId === id) }))
        .filter((g) => g.rows.length)
        .sort((a, b) => jobTitle(a.id).localeCompare(jobTitle(b.id)))
    : [{ id: "", rows: paginate(rows, listPageSize, listPage) }];

  const visibleIds = rows.map((a) => a.id);
  const chosen = rows.filter((a) => picked.includes(a.id));
  const allPicked = Boolean(visibleIds.length) && visibleIds.every((id) => picked.includes(id));
  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setPicked(allPicked ? [] : visibleIds);
  const bulk = (d: Decision) => {
    onDecideMany(chosen, d);
    setPicked([]);
  };

  const selectCls =
    "min-h-12 min-w-[160px] flex-[1_1_180px] rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]";

  /** Role browser: a dense, searchable, paginated index — one line per role. */
  const rq = roleQuery.trim().toLowerCase();
  const allRoleRows = jobOptions.map((id) => {
    const job = jobById.get(id);
    const mine = apps.filter((a) => a.jobId === id);
    const by = (d: Decision) => mine.filter((a) => statusOf(a) === d).length;
    return {
      id,
      title: jobTitle(id),
      employer: employerName(job),
      postedAt: job?.createdAt ? new Date(job.createdAt) : null,
      total: mine.length,
      approved: by("Approved"),
      pending: by("Pending"),
      rejected: by("Rejected"),
    };
  });

  const roleEmployers = Array.from(
    new Set(allRoleRows.filter((r) => !isDemoJobId(r.id)).map((r) => r.employer)),
  ).sort((a, b) => a.localeCompare(b));

  const roleRows = allRoleRows
    .filter((r) => roleEmp === "All" || r.employer === roleEmp)
    .filter((r) => (!rq ? true : `${r.title} ${r.employer}`.toLowerCase().includes(rq)))
    .filter((r) => {
      if (roleDateFilter === "All time" || !r.postedAt) return true;
      const now = Date.now();
      const days = roleDateFilter === "Last 7 days" ? 7 : roleDateFilter === "Last 30 days" ? 30 : 90;
      return now - r.postedAt.getTime() <= days * 24 * 60 * 60 * 1000;
    })
    .sort((a, b) => {
      if (roleSort === "Latest posted") {
        const da = a.postedAt?.getTime() ?? 0;
        const db = b.postedAt?.getTime() ?? 0;
        return db - da || b.pending - a.pending || b.total - a.total;
      }
      if (roleSort === "Oldest posted") {
        const da = a.postedAt?.getTime() ?? 0;
        const db = b.postedAt?.getTime() ?? 0;
        return da - db || b.pending - a.pending || b.total - a.total;
      }
      if (roleSort === "Most applications") return b.total - a.total;
      if (roleSort === "Employer A-Z") return a.employer.localeCompare(b.employer) || a.title.localeCompare(b.title);
      return b.pending - a.pending || b.total - a.total;
    });

  const {
    page: rolePage,
    setPage: setRolePage,
    pageSize: rolePageSize,
    setPageSize: setRolePageSize,
    totalPages: rolePages,
    resetPage: resetRolePage,
  } = usePagination(roleRows.length, 50);

  useEffect(() => {
    resetRolePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleQuery, roleEmp, roleDateFilter, roleSort, rolePageSize]);

  const rolePageRows = paginate(roleRows, rolePageSize, rolePage);
  const totalPending = allRoleRows.reduce((n, r) => n + r.pending, 0);

  const openRole = (id: string, f: AppFilter = "All") => {
    setJobFilter(id);
    setFilter(f);
    setPicked([]);
    setView("list");
  };

  /**
   * Company browser: one line per employer on file, then drill into their
   * roles. Built from crmEmployers (the live CRM list) rather than from
   * applications' snapshotted job.company text — that older approach both
   * kept showing employers that had since been deleted (their orphaned
   * jobs still carried the company name) and skipped any employer with no
   * applications yet.
   */
  const cq = companyQuery.trim().toLowerCase();
  const companyRows = crmEmployers
    .filter((e) => !e.demo)
    .map((e) => {
      const myJobIds = e.jobIds.filter((id) => !jobById.get(id)?.demo);
      const mine = apps.filter((a) => myJobIds.includes(a.jobId));
      const count = (d: Decision) => mine.filter((a) => statusOf(a) === d).length;
      return {
        name: e.company,
        roles: myJobIds.length,
        total: mine.length,
        pending: count("Pending"),
        approved: count("Approved"),
        rejected: count("Rejected"),
      };
    })
    .filter((c) => (!cq ? true : c.name.toLowerCase().includes(cq)))
    .sort((a, b) => b.pending - a.pending || b.total - a.total || a.name.localeCompare(b.name));

  const {
    page: companyPage,
    setPage: setCompanyPage,
    pageSize: companyPageSize,
    setPageSize: setCompanyPageSize,
    totalPages: companyPages,
    resetPage: resetCompanyPage,
  } = usePagination(companyRows.length, 50);

  useEffect(() => {
    resetCompanyPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyQuery, companyPageSize]);

  const companyPageRows = paginate(companyRows, companyPageSize, companyPage);

  const openCompany = (name: string) => {
    setRoleEmp(name);
    setRoleQuery("");
    setView("roles");
  };

  /** Candidate browser: one line per person, with their pitch and every role they applied for. */
  const dq = candQuery.trim().toLowerCase();
  const directoryByKey = new Map(directory.map((d) => [(d.email || d.name).toLowerCase(), d]));
  const people = (() => {
    const map = new Map<
      string,
      {
        key: string;
        name: string;
        email: string;
        phone: string;
        location: string;
        years: string;
        cvName: string;
        hasPitch: boolean;
        voiceAnswers: number;
        apps: Application[];
      }
    >();
    apps.filter(inWindow).forEach((a) => {
      const key = (a.email || a.name).toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.apps.push(a);
        existing.hasPitch = existing.hasPitch || a.hasPitch;
        existing.voiceAnswers = Math.max(existing.voiceAnswers, a.voiceAnswers);
        if (!existing.cvName && a.cvName) existing.cvName = a.cvName;
      } else {
        map.set(key, {
          key,
          name: a.name,
          email: a.email,
          phone: a.phone,
          location: a.location,
          years: a.years,
          cvName: a.cvName,
          hasPitch: a.hasPitch,
          voiceAnswers: a.voiceAnswers,
          apps: [a],
        });
      }
    });
    return Array.from(map.values()).map((c) => {
      const by = (d: Decision) => c.apps.filter((a) => statusOf(a) === d).length;
      const titles = Array.from(new Set(c.apps.map((a) => jobTitle(a.jobId))));
      const tagText = c.apps
        .map((a) => (jobById.get(a.jobId)?.tags ?? []).join(" "))
        .join(" ");
      const tag = { ...emptyPoolTag, ...admin.pool[c.key] };
      // Falls back to the band the candidate picked on their own profile.
      const ownBand = salaryBands.find((b) =>
        c.apps.some((a) => a.salaryExpectation === b.label),
      );
      const dir = directoryByKey.get(c.key);
      return {
        ...c,
        rating: ratingOf(admin.ratings, c.key),
        salaryMin: tag.max ? tag.min : (ownBand?.min ?? 0),
        salaryMax: tag.max ? tag.max : (ownBand?.max ?? 0),
        companies: Array.from(new Set(c.apps.map((a) => employerName(jobById.get(a.jobId))))),
        titles,
        // Also folds in their showcase targetRole/headline (same signal the directory-only
        // branch below categorizes from) so a category picked up from their profile isn't
        // lost, and they don't drop out of a category filter, once they have an application.
        categories: categoriesOf(
          [...titles, tagText, c.years, dir?.showcase.targetRole, dir?.showcase.headline].filter(Boolean).join(" "),
        ),
        approved: by("Approved"),
        pending: by("Pending"),
        rejected: by("Rejected"),
        lastApplied: Math.max(...c.apps.map((a) => new Date(a.appliedAt).getTime() || 0)),
        firstApplied: Math.min(...c.apps.map((a) => new Date(a.appliedAt).getTime() || Infinity)),
        audioUrl: c.apps.find((a) => a.audioUrl)?.audioUrl ?? null,
        // Applications carry no Closer/Setter tag themselves — only ever set by an
        // admin on the New Candidate form — so fall back to their directory profile
        // for it. Otherwise a candidate an admin tagged "Setter" would lose that tag
        // (and drop out of a role-tag filter) the moment they got their first
        // application, e.g. from being assigned to a client role via Distribution.
        role: dir?.role ?? null,
      };

    });

  })();

  /**
   * Distribution's candidate pool: everyone in `people` (who applied to at
   * least one role) plus admin-created candidates who haven't applied to
   * anything yet — otherwise they'd never be assignable to a client role and
   * could never reach an employer's dashboard.
   */
  const distPeople = (() => {
    const existingKeys = new Set(people.map((c) => c.key));
    const extra = directory
      .filter((d) => !existingKeys.has((d.email || d.name).toLowerCase()))
      .map((d) => {
        const key = (d.email || d.name).toLowerCase();
        const tag = { ...emptyPoolTag, ...admin.pool[key] };
        return {
          key,
          name: d.name,
          email: d.email,
          phone: d.phone,
          location: d.location || d.showcase.location,
          role: d.role,
          years: d.showcase.yearsSelling,
          cvName: d.cvUrl ? "CV on file" : "",
          hasPitch: Boolean(d.showcase.audioUrl),
          audioUrl: d.showcase.audioUrl || null,
          // Only recorded answers to the 5 questions — not the intro prompt or unanswered custom wording.
          voiceAnswers: assessmentDone(d.showcase),
          apps: [] as Application[],
          rating: ratingOf(admin.ratings, key),
          salaryMin: tag.max ? tag.min : 0,
          salaryMax: tag.max ? tag.max : 0,
          companies: [] as string[],
          titles: [] as string[],
          categories: categoriesOf([d.showcase.targetRole, d.showcase.headline].filter(Boolean).join(" ")),
          approved: 0,
          pending: 0,
          rejected: 0,
          lastApplied: -Infinity,
          firstApplied: Infinity,
        };
      });
    return [...people, ...extra];
  })();

  // distPeople (people who applied + admin-created/profile-only candidates who
  // haven't applied yet) so every candidate on file shows up here, not just
  // ones with an application — same reasoning as Distribution's pool below.
  const candidateRows = distPeople
    .filter((c) => !employerCompanyNames.has(c.name.trim().toLowerCase()))
    .filter((c) => (!minRating ? true : c.rating >= minRating))
    .filter((c) =>
      !dq
        ? true
        : [c.name, c.email, c.location, c.years, ...c.titles].join(" ").toLowerCase().includes(dq),
    )
    .sort((a, b) => {
      if (candSort === "Highest rated") return b.rating - a.rating || a.name.localeCompare(b.name);
      if (candSort === "Lowest rated") {
        return (a.rating || 99) - (b.rating || 99) || a.name.localeCompare(b.name);
      }
      if (candSort === "Name A-Z") return a.name.localeCompare(b.name);
      // No application yet (an admin-created candidate) sorts as newest.
      const aOrder = a.apps[0] ? (order.get(a.apps[0].id) ?? 0) : -1;
      const bOrder = b.apps[0] ? (order.get(b.apps[0].id) ?? 0) : -1;
      return aOrder - bOrder;
    });

  const {
    page: candPage,
    setPage: setCandPage,
    pageSize: candPageSize,
    setPageSize: setCandPageSize,
    totalPages: candPages,
    resetPage: resetCandPage,
  } = usePagination(candidateRows.length, 50);

  useEffect(() => {
    resetCandPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candQuery, minRating, candSort, candPageSize]);

  const candPageRows = paginate(candidateRows, candPageSize, candPage);

  /** Distribution: pick qualified people and assign them to any client's role. */
  const distQ = distQuery.trim().toLowerCase();
  /** The first role chosen in the assign toolbar — used to match pay when several are picked. */
  const targetJob = assignJobs.find((j) => j.id === assignJobIds[0]) ?? null;
  const targetPay = `${targetJob?.payStructure ?? ""} ${targetJob?.pay ?? ""}`.trim();
  /** Years of experience / sales types live on the candidate record, matched to a row by email. */
  const adminAttrs = new Map(
    directory.map((d) => [(d.email || d.name).toLowerCase(), { experienceLevel: d.experienceLevel, salesTypes: d.salesTypes }] as const),
  );
  const distRows = distPeople
    .filter((c) => (!distMinRating ? true : c.rating >= distMinRating))
    .filter((c) => distCategory === "All" || c.categories.includes(distCategory as never))
    .filter((c) => {
      if (distSalary === "All") return true;
      if (distSalary === "Fits role pay") {
        if (!targetPay) return true;
        return moneyFits({ ...emptyPoolTag, min: c.salaryMin, max: c.salaryMax }, targetPay);
      }
      if (distSalary === "Not set") return !c.salaryMax;
      const band = salaryBands.find((b) => b.label === distSalary);
      if (!band || !c.salaryMax) return false;
      return c.salaryMin <= band.max && c.salaryMax >= band.min;
    })
    .filter((c) => distCompany === "All" || c.companies.includes(distCompany))
    .filter((c) => distLocation === "All" || c.location === distLocation)
    .filter((c) => distRoleTag === "All" || c.role === distRoleTag)
    .filter((c) => !distExperience.length || distExperience.includes(adminAttrs.get(c.key)?.experienceLevel ?? ""))
    .filter((c) => {
      if (!distSalesTypes.length) return true;
      const types = adminAttrs.get(c.key)?.salesTypes ?? [];
      return distSalesTypes.some((t) => types.includes(t));
    })
    .filter((c) => {
      if (!distFrom && !distTo) return true;
      const from = distFrom ? new Date(`${distFrom}T00:00:00`).getTime() : 0;
      const to = distTo ? new Date(`${distTo}T23:59:59`).getTime() : Infinity;
      return c.apps.some((a) => {
        const t = new Date(a.appliedAt).getTime();
        return Number.isNaN(t) ? false : t >= from && t <= to;
      });
    })

    .filter((c) => (!distTitle ? true : c.titles.join(" ").toLowerCase().includes(distTitle.toLowerCase())))
    .filter((c) =>
      !distQ
        ? true
        : [c.name, c.email, c.location, c.years, ...c.titles, ...c.companies]
            .join(" ")
            .toLowerCase()
            .includes(distQ),
    )
    .sort((a, b) => {
      // Distribution exists to find who to assign next, so unassigned and
      // lightly-assigned candidates always float to the top — the chosen
      // sort only breaks ties within the same assignment count.
      const byAssignments = a.companies.length - b.companies.length;
      if (byAssignments !== 0) return byAssignments;
      if (distSort === "Lowest rated") return (a.rating || 99) - (b.rating || 99) || a.name.localeCompare(b.name);
      if (distSort === "Name A-Z") return a.name.localeCompare(b.name);
      if (distSort === "Company A-Z") {
        return (a.companies[0] ?? "").localeCompare(b.companies[0] ?? "") || a.name.localeCompare(b.name);
      }
      if (distSort === "Applied: newest") return b.lastApplied - a.lastApplied || a.name.localeCompare(b.name);
      if (distSort === "Applied: oldest") return a.firstApplied - b.firstApplied || a.name.localeCompare(b.name);
      return b.rating - a.rating || a.name.localeCompare(b.name);
    });

  const {
    page: distPage,
    setPage: setDistPage,
    pageSize: distPageSize,
    setPageSize: setDistPageSize,
    totalPages: distPages,
    resetPage: resetDistPage,
  } = usePagination(distRows.length, 50);

  useEffect(() => {
    resetDistPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    distQ,
    distMinRating,
    distCategory,
    distSalary,
    distCompany,
    distLocation,
    distRoleTag,
    distExperience,
    distSalesTypes,
    distFrom,
    distTo,
    distTitle,
    distSort,
    distPageSize,
  ]);

  const distPageRows = paginate(distRows, distPageSize, distPage);

  const distCompanies = Array.from(new Set(people.flatMap((c) => c.companies)))
    .filter((name) => !demoCompanies.has(name.trim().toLowerCase()))
    .sort((a, b) => a.localeCompare(b));
  const distLocations = Array.from(new Set(distPeople.map((c) => c.location).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );
  const distRoleTags = Array.from(
    new Set(distPeople.map((c) => c.role).filter((r): r is string => Boolean(r))),
  ).sort((a, b) => a.localeCompare(b));
  const distChosen = distRows.filter((c) => distPicked.includes(c.key));
  // Scoped to the current page, not every filtered candidate — the "select
  // all" checkbox should only ever pick up what's actually on screen.
  // Selections on other pages are left alone (added/removed additively) so
  // paging through and checking a few pages in a row accumulates as expected.
  const distAllPicked =
    Boolean(distPageRows.length) && distPageRows.every((c) => distPicked.includes(c.key));
  const toggleDistPageAll = () =>
    setDistPicked((prev) =>
      distAllPicked
        ? prev.filter((k) => !distPageRows.some((c) => c.key === k))
        : Array.from(new Set([...prev, ...distPageRows.map((c) => c.key)])),
    );
  // Count distinct candidates per job, not raw distribution rows — a
  // candidate can end up with more than one row for the same job (e.g. a
  // stale local-only row surviving alongside its server-backed replacement
  // after an unassign/reassign), which would otherwise double-count them in
  // the "N candidates assigned" hint below even though every other view
  // (Distribution table, Employers page) already reports the unique count.
  const distCandidatesByJob = new Map<string, Set<string>>();
  for (const d of distributions) {
    let set = distCandidatesByJob.get(d.jobId);
    if (!set) {
      set = new Set();
      distCandidatesByJob.set(d.jobId, set);
    }
    set.add(d.candidateKey);
  }
  const distCountByJob = new Map<string, number>();
  for (const [jobId, keys] of distCandidatesByJob) distCountByJob.set(jobId, keys.size);
  const assignTargets = assignJobs
    .filter((j) => !j.demo && !demoCompanies.has((j.company || "").trim().toLowerCase()))
    .filter((j) => assignCategory === "All" || categoryByCompany.get((j.company || "").trim().toLowerCase()) === assignCategory)
    .filter((j) => {
      if (assignStatus === "unassigned") return !(distCountByJob.get(j.id) ?? 0);
      if (assignStatus === "assigned") return (distCountByJob.get(j.id) ?? 0) > 0;
      return true;
    })
    .sort((a, b) => employerName(a).localeCompare(employerName(b)) || a.title.localeCompare(b.title));
  const assignTargetOptions = assignTargets.map((j) => {
    const assignedCount = distCountByJob.get(j.id) ?? 0;
    return {
      value: j.id,
      label: `${employerName(j)} — ${j.title} — ${j.location || "Location TBC"}`,
      hint: assignedCount ? `${assignedCount} candidate${assignedCount === 1 ? "" : "s"} assigned` : "No candidates assigned yet",
      highlighted: assignedCount > 0,
    };
  });

  const runAssign = () => {
    if (!assignJobIds.length || !distChosen.length) return;
    onAssign(
      distChosen.map((c) => ({ key: c.key, name: c.name })),
      assignJobIds,
    );
    setDistPicked([]);
  };


  return (
    <>
      <div className="m-5 mb-0 flex max-w-[760px] items-start gap-2.5 rounded-lg bg-[#DCE6FF] px-4 py-3">
        <span aria-hidden className="font-extrabold text-[#0B2160]">i</span>
        <p className="text-[15px] text-[#0B2160]">
          <strong>Only direct applications, and they always need your review.</strong> People Closdd puts forward are
          approved automatically and show on the employer page under each role.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5">
        {view === "list" && jobFilter !== "All" ? (
          <button
            type="button"
            onClick={() => {
              setJobFilter("All");
              setView("roles");
            }}
            className="ml-auto text-[15px] font-semibold text-purple-strong underline"
          >
            ← Back to all roles
          </button>
        ) : null}
        {view === "roles" && roleEmp !== "All" ? (
          <button
            type="button"
            onClick={() => {
              setRoleEmp("All");
              setView("companies");
            }}
            className="ml-auto text-[15px] font-semibold text-purple-strong underline"
          >
            ← Back to companies
          </button>
        ) : null}
      </div>

      {view === "companies" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-3 py-2">
            <input
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              placeholder="Search company or brand"
              className="min-w-[160px] flex-1 min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[14px] text-ink outline-none placeholder:text-ink/35 focus:border-purple-strong/40"
            />
            <p className="ml-auto text-[15px] text-[#5A5A60]">
              {companyRows.length} compan{companyRows.length === 1 ? "y" : "ies"} · {totalPending} awaiting review
            </p>
          </div>

          {!companyRows.length ? (
            <Empty>No companies match that search.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <PaginationBar
                total={companyRows.length}
                page={companyPage}
                totalPages={companyPages}
                pageSize={companyPageSize}
                onPageChange={setCompanyPage}
                onPageSizeChange={setCompanyPageSize}
                itemLabel="companies"
                className="border-b border-[#E3E3DE]"
              />
              <table className="w-full border-collapse text-left" style={{ tableLayout: "fixed", minWidth: 760 }}>
                <thead>
                  <tr className="bg-[#FAFAF8]">
                    {[
                      { label: "Company", width: "28%" },
                      { label: "Account", width: "70px" },
                      { label: "Active", width: "70px" },
                      { label: "Roles", width: "70px" },
                      { label: "Applications", width: "90px" },
                      { label: "Awaiting", width: "90px" },
                      { label: "Approved", width: "90px" },
                      { label: "Not approved", width: "105px" },
                      { label: "", width: "70px" },
                    ].map((h) => (
                      <th key={h.label} className="px-2 py-1.5 text-[15px] font-bold uppercase tracking-[0.12em] text-ink/40" style={{ width: h.width }}>
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EFEFEA]">
                  {companyPageRows.map((c) => {
                    const status = employerAccountStatus(crmEmployers, c.name);
                    return (
                      <tr key={c.name} className="align-middle transition hover:bg-purple-soft/30">
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            onClick={() => openCompany(c.name)}
                            className="block w-full truncate text-left text-[14px] font-semibold text-ink hover:text-purple-strong"
                          >
                            {c.name}
                          </button>
                        </td>
                        <td className="px-2 py-1.5">
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                              status.hasAccount
                                ? "border-tag-green/20 bg-tag-green-soft text-tag-green"
                                : "border-ink/15 bg-ink/[0.04] text-[#5A5A60]"
                            }`}
                          >
                            {status.hasAccount ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                              status.isActive
                                ? "border-tag-green/20 bg-tag-green text-white"
                                : "border-ink/15 bg-ink/[0.04] text-[#5A5A60]"
                            }`}
                          >
                            {status.isActive ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-[14px] text-[#3A3A40]">{c.roles}</td>
                        <td className="px-2 py-1.5 text-[14px] font-semibold text-[#3A3A40]">{c.total}</td>
                        <td className={`px-2 py-1.5 text-[14px] font-semibold ${c.pending ? "text-tag-amber" : "text-ink/30"}`}>
                          {c.pending}
                        </td>
                        <td className={`px-2 py-1.5 text-[14px] font-semibold ${c.approved ? "text-tag-green" : "text-ink/30"}`}>
                          {c.approved}
                        </td>
                        <td className={`px-2 py-1.5 text-[14px] font-semibold ${c.rejected ? "text-tag-red" : "text-ink/30"}`}>
                          {c.rejected}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button type="button" onClick={() => openCompany(c.name)} className={btnGhost}>
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginationBar
                total={companyRows.length}
                page={companyPage}
                totalPages={companyPages}
                pageSize={companyPageSize}
                onPageChange={setCompanyPage}
                onPageSizeChange={setCompanyPageSize}
                itemLabel="companies"
                className="border-t border-[#E3E3DE]"
              />
            </div>
          )}
        </>
      ) : view === "roles" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-3 py-2">
            <input
              value={roleQuery}
              onChange={(e) => setRoleQuery(e.target.value)}
              placeholder="Search role or employer"
              className="min-w-[160px] flex-1 min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[14px] text-ink outline-none placeholder:text-ink/35 focus:border-purple-strong/40"
            />
            <select value={roleEmp} onChange={(e) => setRoleEmp(e.target.value)} className={selectCls}>
              <option value="All">All employers ({roleEmployers.length})</option>
              {roleEmployers.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
            <select
              value={roleSort}
              onChange={(e) => setRoleSort(e.target.value as typeof roleSort)}
              className={selectCls}
            >
              {["Latest posted", "Oldest posted", "Needs review", "Most applications", "Employer A-Z"].map((s) => (
                <option key={s} value={s}>
                  Sort: {s}
                </option>
              ))}
            </select>
            <select
              value={roleDateFilter}
              onChange={(e) => setRoleDateFilter(e.target.value as typeof roleDateFilter)}
              className={selectCls}
            >
              {["All time", "Last 7 days", "Last 30 days", "Last 90 days"].map((d) => (
                <option key={d} value={d}>
                  Posted: {d}
                </option>
              ))}
            </select>
            <p className="ml-auto text-[15px] text-[#5A5A60]">
              {roleRows.length} role{roleRows.length === 1 ? "" : "s"} · {totalPending} awaiting review
            </p>
          </div>

          {!roleRows.length ? (
            <Empty>No roles match that search.</Empty>
          ) : (
              <div className="overflow-x-auto">
                <PaginationBar
                  total={roleRows.length}
                  page={rolePage}
                  totalPages={rolePages}
                  pageSize={rolePageSize}
                  onPageChange={setRolePage}
                  onPageSizeChange={setRolePageSize}
                  itemLabel="roles"
                  className="border-b border-[#E3E3DE]"
                />
                <table className="w-full border-collapse text-left" style={{ tableLayout: "fixed", minWidth: 980 }}>
                  <thead>
                    <tr className="bg-[#FAFAF8]">
                      {[
                        { label: "Role", width: "22%" },
                        { label: "Employer", width: "18%" },
                        { label: "Posted", width: "90px" },
                        { label: "Account", width: "70px" },
                        { label: "Active", width: "70px" },
                        { label: "Applications", width: "85px" },
                        { label: "Awaiting", width: "90px" },
                        { label: "Approved", width: "90px" },
                        { label: "Not approved", width: "105px" },
                        { label: "", width: "70px" },
                      ].map((h) => (
                        <th
                          key={h.label}
                          className="px-2 py-1.5 text-[15px] font-bold uppercase tracking-[0.12em] text-ink/40"
                          style={{ width: h.width }}
                        >
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EFEFEA]">
                    {rolePageRows.map((r) => {
                      const status = employerAccountStatus(crmEmployers, r.employer);
                      return (
                        <tr key={r.id} className="align-middle transition hover:bg-purple-soft/30">
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => openRole(r.id)}
                              className="block w-full truncate text-left text-[14px] font-semibold text-ink hover:text-purple-strong"
                            >
                              {r.title}
                            </button>
                          </td>
                          <td className="truncate px-2 py-1.5 text-[14px] text-[#3A3A40]">{r.employer}</td>
                          <td className="px-2 py-1.5 text-[15px] text-[#5A5A60]">{formatDate(r.postedAt)}</td>
                          <td className="px-2 py-1.5">
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                                status.hasAccount
                                  ? "border-tag-green/20 bg-tag-green-soft text-tag-green"
                                  : "border-ink/15 bg-ink/[0.04] text-[#5A5A60]"
                              }`}
                            >
                              {status.hasAccount ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                                status.isActive
                                  ? "border-tag-green/20 bg-tag-green text-white"
                                  : "border-ink/15 bg-ink/[0.04] text-[#5A5A60]"
                              }`}
                            >
                              {status.isActive ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-[14px] font-semibold text-[#3A3A40]">{r.total}</td>
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => openRole(r.id, "Awaiting Review")}
                              className={`text-[14px] font-semibold ${r.pending ? "text-tag-amber underline" : "text-ink/30"}`}
                            >
                              {r.pending}
                            </button>
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => openRole(r.id, "Approved")}
                              className={`text-[14px] font-semibold ${r.approved ? "text-tag-green underline" : "text-ink/30"}`}
                            >
                              {r.approved}
                            </button>
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => openRole(r.id, "Not Approved")}
                              className={`text-[14px] font-semibold ${r.rejected ? "text-tag-red underline" : "text-ink/30"}`}
                            >
                              {r.rejected}
                            </button>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button type="button" onClick={() => openRole(r.id)} className={btnGhost}>
                              Open
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <PaginationBar
                  total={roleRows.length}
                  page={rolePage}
                  totalPages={rolePages}
                  pageSize={rolePageSize}
                  onPageChange={setRolePage}
                  onPageSizeChange={setRolePageSize}
                  itemLabel="roles"
                  className="border-t border-[#E3E3DE]"
                />
              </div>
          )}
        </>
      ) : view === "distribution" ? (
        <>
          <div className="border-b border-[#E3E3DE] bg-purple-soft/50 px-3 py-2">
            <p className="text-[14px] text-purple-strong">
              Pick the people you rate, then assign them straight onto any client role — including a brand new client
              with no applications yet.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-3 py-2">
            <input
              value={distQuery}
              onChange={(e) => setDistQuery(e.target.value)}
              placeholder="Search candidate, role, company"
              className="w-[190px] min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[14px] text-ink outline-none placeholder:text-ink/35 focus:border-purple-strong/40"
            />
            <input
              value={distTitle}
              onChange={(e) => setDistTitle(e.target.value)}
              placeholder="Job title, e.g. SDR"
              className="w-[140px] min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[14px] text-ink outline-none placeholder:text-ink/35 focus:border-purple-strong/40"
            />
            <select value={within} onChange={(e) => setWithin(e.target.value)} className={selectCls} aria-label="Applied within">
              {appliedWindows.map((w) => (
                <option key={w.label} value={w.label}>
                  Applied: {w.label}
                </option>
              ))}
            </select>
            <select value={distCompany} onChange={(e) => setDistCompany(e.target.value)} className={selectCls}>
              <option value="All">All companies applied to</option>
              {distCompanies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select value={distLocation} onChange={(e) => setDistLocation(e.target.value)} className={selectCls}>
              <option value="All">All locations</option>
              {distLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
            <select value={distRoleTag} onChange={(e) => setDistRoleTag(e.target.value)} className={selectCls}>
              <option value="All">All roles</option>
              {distRoleTags.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <MultiFilter label="Experience" options={experienceLevelOptions} value={distExperience} onChange={setDistExperience} />
            <MultiFilter label="Sales type" options={salesTypeOptions} value={distSalesTypes} onChange={setDistSalesTypes} />
            <select
              value={distMinRating}
              onChange={(e) => setDistMinRating(Number(e.target.value))}
              className={selectCls}
            >
              {[
                { v: 0, l: "All ratings" },
                { v: 9, l: "Rated 9+" },
                { v: 8, l: "Rated 8+" },
                { v: 7, l: "Rated 7+" },
                { v: 5, l: "Rated 5+" },
                { v: 1, l: "Rated (any score)" },
              ].map((o) => (
                <option key={o.v} value={o.v}>
                  {o.l}
                </option>
              ))}
            </select>
            <select
              value={distSort}
              onChange={(e) => setDistSort(e.target.value as typeof distSort)}
              className={selectCls}
            >
              {["Highest rated", "Lowest rated", "Applied: newest", "Applied: oldest", "Name A-Z", "Company A-Z"].map((s) => (
                <option key={s} value={s}>
                  Sort: {s}
                </option>
              ))}
            </select>
            <select
              value={distCategory}
              onChange={(e) => {
                setDistCategory(e.target.value);
                setDistPicked([]);
              }}
              className={selectCls}
            >
              <option value="All">All categories ({distPeople.length})</option>
              {salesCategories.map((cat) => {
                const count = distPeople.filter((p) => p.categories.includes(cat as never)).length;
                if (!count) return null;
                return (
                  <option key={cat} value={cat}>
                    {cat} ({count})
                  </option>
                );
              })}
            </select>
            <select
              value={distSalary}
              onChange={(e) => {
                setDistSalary(e.target.value);
                setDistPicked([]);
              }}
              className={selectCls}
            >
              <option value="All">All salary expectations</option>
              <option value="Fits role pay">Fits the selected role's pay</option>
              {salaryBands.map((b) => {
                const count = people.filter(
                  (p) => p.salaryMax && p.salaryMin <= b.max && p.salaryMax >= b.min,
                ).length;
                return (
                  <option key={b.label} value={b.label}>
                    Expects {b.label} ({count})
                  </option>
                );
              })}
              <option value="Not set">Salary not set</option>
            </select>
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] font-bold uppercase tracking-[0.12em] text-ink/40">Applied</span>
              <input
                type="date"
                value={distFrom}
                onChange={(e) => setDistFrom(e.target.value)}
                aria-label="Applied from"
                className="rounded-lg border border-[#BDBDB5] bg-white px-2 py-1.5 text-[14px] text-ink outline-none focus:border-purple-strong/40"
              />
              <span className="text-[15px] text-ink/40">to</span>
              <input
                type="date"
                value={distTo}
                onChange={(e) => setDistTo(e.target.value)}
                aria-label="Applied to"
                className="rounded-lg border border-[#BDBDB5] bg-white px-2 py-1.5 text-[14px] text-ink outline-none focus:border-purple-strong/40"
              />
              {distFrom || distTo ? (
                <button
                  type="button"
                  onClick={() => {
                    setDistFrom("");
                    setDistTo("");
                  }}
                  className="rounded-full border border-[#BDBDB5] px-2 py-1 text-[15px] font-semibold text-[#3A3A40] hover:border-purple-strong/40 hover:text-ink"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <p className="text-[15px] text-[#5A5A60]">{distRows.length} candidates</p>

          </div>





          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] bg-[#FAFAF8] px-3 pt-2">
            <span className="text-[15px] font-bold uppercase tracking-[0.12em] text-ink/40">Employer category</span>
            <select
              value={assignCategory}
              onChange={(e) => setAssignCategory(e.target.value)}
              className={selectCls}
              aria-label="Filter roles by category"
            >
              <option value="All">All categories</option>
              {employerCategoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="ml-2 text-[15px] font-bold uppercase tracking-[0.12em] text-ink/40">Assignment</span>
            <select
              value={assignStatus}
              onChange={(e) => setAssignStatus(e.target.value as typeof assignStatus)}
              className={selectCls}
              aria-label="Filter roles by assignment status"
            >
              <option value="all">All roles</option>
              <option value="unassigned">Not yet assigned</option>
              <option value="assigned">Already assigned</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] bg-[#FAFAF8] px-3 py-2">
            <p className="text-[14px] font-semibold text-[#3A3A40]">
              {distChosen.length} selected
            </p>
            <div className="w-[420px] max-w-full">
              <MultiCombobox
                options={assignTargetOptions}
                values={assignJobIds}
                onChange={setAssignJobIds}
                placeholder="Assign to client role(s)…"
                empty="No matching roles"
              />
            </div>
            <button
              type="button"
              onClick={runAssign}
              disabled={!distChosen.length || !assignJobIds.length}
              className="rounded-lg bg-purple-strong px-3 py-1.5 text-[14px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              Assign {distChosen.length || ""} to {assignJobIds.length > 1 ? `${assignJobIds.length} roles` : "role"}
            </button>
            <button
              type="button"
              onClick={() => {
                const inScope = (d: Distribution) => distCompany === "All" || d.employer === distCompany;
                const withAssignments = distChosen.filter((c) =>
                  distributions.some((d) => d.candidateKey === c.key && inScope(d)),
                );
                if (!withAssignments.length) return;
                const scopeLabel = distCompany === "All" ? "every role they're currently assigned to" : `${distCompany}`;
                if (
                  window.confirm(
                    `Unassign ${withAssignments.length} candidate${withAssignments.length === 1 ? "" : "s"} from ${scopeLabel}?`,
                  )
                ) {
                  onUnassignMany(withAssignments.map((c) => c.key), distCompany);
                }
              }}
              disabled={
                !distChosen.some((c) =>
                  distributions.some((d) => d.candidateKey === c.key && (distCompany === "All" || d.employer === distCompany)),
                )
              }
              className="rounded-lg border border-tag-red/25 bg-tag-red/10 px-3 py-1.5 text-[14px] font-semibold text-tag-red transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              Unassign {distChosen.length || ""}
              {distCompany !== "All" ? ` from ${distCompany}` : ""}
            </button>
            {distPicked.length ? (
              <button type="button" onClick={() => setDistPicked([])} className={btnGhost}>
                Clear selection
              </button>
            ) : null}
          </div>

          {!distRows.length ? (
            <Empty>No candidates match these filters yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <PaginationBar
                total={distRows.length}
                page={distPage}
                totalPages={distPages}
                pageSize={distPageSize}
                onPageChange={setDistPage}
                onPageSizeChange={setDistPageSize}
                itemLabel="candidates"
                className="border-b border-[#E3E3DE]"
              />
              <table className="w-full border-collapse text-left" style={{ tableLayout: "fixed", minWidth: 940 }}>
                <thead>
                  <tr className="bg-[#FAFAF8]">
                    <th className="px-2 py-1.5" style={{ width: "30px" }}>
                      <input
                        type="checkbox"
                        checked={distAllPicked}
                        onChange={toggleDistPageAll}
                      />
                    </th>
                    {[
                      { label: "Candidate", width: "16%" },
                      { label: "Rating", width: "80px" },
                      { label: "Salary expected", width: "120px" },
                      { label: "Category", width: "14%" },
                      { label: "Pitch", width: "130px" },
                      { label: "Applied", width: "110px" },
                      { label: "Applied for", width: "16%" },
                      { label: "Company", width: "14%" },
                      { label: "Already sent to", width: "16%" },
                    ].map((h) => (

                      <th
                        key={h.label}
                        className="px-2 py-1.5 text-[15px] font-bold uppercase tracking-[0.14em] text-ink/40"
                        style={{ width: h.width }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EFEFEA]">
                  {distPageRows.map((c) => {
                    const sent = distributions.filter((d) => d.candidateKey === c.key);
                    const applied = appliedDate(Number.isFinite(c.lastApplied) ? new Date(c.lastApplied).toISOString() : null);
                    return (
                      <tr key={c.key} className="align-middle">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={distPicked.includes(c.key)}
                            onChange={() =>
                              setDistPicked((p) =>
                                p.includes(c.key) ? p.filter((x) => x !== c.key) : [...p, c.key],
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2">
                            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-purple-soft text-[15px] font-bold text-purple-strong">
                              {initialsOf(c.name)}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-[15px] font-semibold text-ink">{c.name}</p>
                              <p className="truncate text-[15px] text-[#5A5A60]">
                                {c.years || "—"} · {c.location || "—"}
                                {c.role ? ` · ${c.role}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <RatingSelect value={c.rating} onChange={(n) => onRate(c.key, c.name, n)} />
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={c.salaryMax ? `${c.salaryMin}-${c.salaryMax}` : ""}
                            onChange={(e) => {
                              const [min, max] = e.target.value.split("-");
                              onSalary(c.key, c.name, Number(min || 0), Number(max || 0));
                            }}
                            className="w-full rounded-md border border-[#BDBDB5] bg-white px-1.5 py-1 text-[15px] font-semibold text-[#3A3A40] outline-none focus:border-purple-strong/40"
                          >
                            <option value="">Not set</option>
                            {salaryBands.map((b) => (
                              <option key={b.label} value={`${b.min}-${b.max}`}>
                                {b.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-2">
                          <div className="flex flex-wrap gap-1">
                            {c.categories.map((cat) => (
                              <span
                                key={cat}
                                className="rounded-full border border-purple-strong/20 bg-purple-soft px-1.5 py-0.5 text-[15px] font-semibold text-purple-strong"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-2 overflow-hidden">
                          <PitchPlayer has={c.hasPitch} url={c.audioUrl ?? undefined} />
                        </td>
                        <td className="px-2 py-2">
                          <p className="text-[14px] font-semibold text-ink/80">{applied.date}</p>
                          <p className="text-[15px] text-[#5A5A60]">{applied.ago}</p>
                        </td>
                        <td className="px-2 py-2">
                          <p className="truncate text-[14px] font-semibold text-ink/80">{c.titles.join(", ") || "—"}</p>
                          <p className="text-[15px] text-[#5A5A60]">
                            {c.apps.length ? (
                              <>
                                {c.apps.length} application{c.apps.length === 1 ? "" : "s"} · {c.approved} approved
                              </>
                            ) : (
                              "Not applied yet"
                            )}
                          </p>
                        </td>
                        <td className="px-2 py-2">
                          {c.companies.length ? (
                            <div className="flex flex-col gap-1">
                              {c.companies.map((company) => (
                                <span
                                  key={company}
                                  className="flex items-center gap-1 rounded-md bg-ink/[0.04] px-1.5 py-0.5 text-[15px] text-[#3A3A40]"
                                  title={company}
                                >
                                  <span className="truncate">{company}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const appsAtCompany = c.apps.filter(
                                        (a) => employerName(jobById.get(a.jobId)) === company,
                                      );
                                      if (!appsAtCompany.length) return;
                                      const plural = appsAtCompany.length > 1 ? "s" : "";
                                      if (
                                        !window.confirm(
                                          `Remove ${c.name} from ${company}? This deletes their application${plural} to ${company}'s role${plural}.`,
                                        )
                                      ) {
                                        return;
                                      }
                                      onRemoveApplications(appsAtCompany.map((a) => a.id));
                                    }}
                                    className="shrink-0 font-bold text-tag-red/70 hover:text-tag-red"
                                    aria-label={`Remove ${c.name} from ${company}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[15px] text-[#5A5A60]">—</p>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {sent.length ? (
                            <div className="flex flex-col gap-1">
                              {sent.map((d) => (
                                <span
                                  key={d.id}
                                  className="flex items-center gap-1 rounded-md bg-ink/[0.04] px-1.5 py-0.5 text-[15px] text-[#3A3A40]"
                                  title={`${d.jobTitle} (${d.employer})`}
                                >
                                  <span className="truncate">
                                    {d.jobTitle} ({d.employer})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm(`Unassign ${c.name} from ${d.jobTitle} at ${d.employer}?`)) {
                                        onUnassign(d);
                                      }
                                    }}
                                    className="shrink-0 font-bold text-tag-red/70 hover:text-tag-red"
                                    aria-label={`Unassign from ${d.jobTitle} at ${d.employer}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[15px] text-[#3A3A40]">—</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginationBar
                total={distRows.length}
                page={distPage}
                totalPages={distPages}
                pageSize={distPageSize}
                onPageChange={setDistPage}
                onPageSizeChange={setDistPageSize}
                itemLabel="candidates"
                className="border-t border-[#E3E3DE]"
              />
            </div>
          )}
        </>
      ) : view === "candidates" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-3 py-2">
            <input
              value={candQuery}
              onChange={(e) => setCandQuery(e.target.value)}
              placeholder="Search candidate, email, location or role"
              className="min-w-[160px] flex-1 min-h-12 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[14px] text-ink outline-none placeholder:text-ink/35 focus:border-purple-strong/40"
            />
            <select value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} className={selectCls}>
              {[
                { v: 0, l: "All ratings" },
                { v: 10, l: "Rated 10" },
                { v: 9, l: "Rated 9+" },
                { v: 8, l: "Rated 8+" },
                { v: 7, l: "Rated 7+" },
                { v: 5, l: "Rated 5+" },
                { v: 1, l: "Rated (any score)" },
              ].map((o) => (
                <option key={o.v} value={o.v}>
                  {o.l}
                </option>
              ))}
            </select>
            <select
              value={candSort}
              onChange={(e) => setCandSort(e.target.value as typeof candSort)}
              className={selectCls}
            >
              {["Newest", "Highest rated", "Lowest rated", "Name A-Z"].map((s) => (
                <option key={s} value={s}>
                  Sort: {s}
                </option>
              ))}
            </select>
            <p className="text-[15px] text-[#5A5A60]">{candidateRows.length} candidates</p>
          </div>

          {!candidateRows.length ? (
            <Empty>
              No candidates yet. People appear here the moment they apply for a role or an admin
              creates their account.
            </Empty>
          ) : (
            <div className="overflow-x-auto">
              <PaginationBar
                total={candidateRows.length}
                page={candPage}
                totalPages={candPages}
                pageSize={candPageSize}
                onPageChange={setCandPage}
                onPageSizeChange={setCandPageSize}
                itemLabel="candidates"
                className="border-b border-[#E3E3DE]"
              />
              <table className="w-full border-collapse text-left" style={{ tableLayout: "fixed", minWidth: 900 }}>
                <thead>
                  <tr className="bg-[#FAFAF8]">
                    {[
                      { label: "Candidate", width: "22%" },
                      { label: "Rating", width: "80px" },
                      { label: "Pitch", width: "130px" },
                      { label: "Contact", width: "22%" },
                      { label: "Voice", width: "70px" },
                      { label: "CV", width: "16%" },
                      { label: "Roles applied for", width: "22%" },
                      { label: "", width: "90px" },
                    ].map((h) => (
                      <th
                        key={h.label}
                        className="px-2 py-1.5 text-[15px] font-bold uppercase tracking-[0.14em] text-ink/40"
                        style={{ width: h.width }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EFEFEA]">
                  {candPageRows.map((c) => (
                    <tr key={c.key} className="align-middle">
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2">
                          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-purple-soft text-[15px] font-bold text-purple-strong">
                            {initialsOf(c.name)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[15px] font-semibold text-ink">{c.name}</p>
                            <p className="truncate text-[15px] text-[#5A5A60]">
                              {c.years || "—"} · {c.location || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <RatingSelect value={c.rating} onChange={(n) => onRate(c.key, c.name, n)} />
                      </td>
                      <td className="px-2 py-2 overflow-hidden">
                        <PitchPlayer has={c.hasPitch} url={c.audioUrl ?? undefined} />
                      </td>
                      <td className="px-2 py-2">
                        <p className="truncate text-[14px] text-[#3A3A40]">{c.email || "—"}</p>
                        <p className="truncate text-[15px] text-[#5A5A60]">{c.phone || "—"}</p>
                      </td>
                      <td className="px-2 py-2">
                        <p className="text-[14px] font-semibold text-[#3A3A40]">{c.voiceAnswers}/3</p>
                      </td>
                      <td className="px-2 py-2">
                        <p className="truncate text-[15px] text-[#3A3A40]">{c.cvName || "No CV"}</p>
                      </td>
                      <td className="px-2 py-2">
                        {c.apps.length ? (
                          <>
                            <p className="truncate text-[14px] font-semibold text-ink/80">
                              {c.apps.map((a) => jobTitle(a.jobId)).join(", ")}
                            </p>
                            <p className="text-[15px] text-[#5A5A60]">
                              {c.apps.length} application{c.apps.length === 1 ? "" : "s"} · {c.approved} approved ·{" "}
                              {c.pending} awaiting
                            </p>
                          </>
                        ) : (
                          <p className="text-[15px] text-[#5A5A60]">Hasn't applied to a role yet</p>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {c.apps[0] ? (
                          <button
                            type="button"
                            onClick={() => setOpenId(c.apps[0]!.id)}
                            className={btnGhost}
                          >
                            Open
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationBar
                total={candidateRows.length}
                page={candPage}
                totalPages={candPages}
                pageSize={candPageSize}
                onPageChange={setCandPage}
                onPageSizeChange={setCandPageSize}
                itemLabel="candidates"
                className="border-t border-[#E3E3DE]"
              />
            </div>
          )}
        </>
      ) : (



        <>
          {jobFilter !== "All" ? (
            <div className="border-b border-[#E3E3DE] bg-purple-soft/40 px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[15px] font-semibold uppercase tracking-[0.14em] text-purple-strong">Role applications</p>
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: jobFilter }}
                    target="_blank"
                    className="mt-0.5 inline-flex items-center gap-1 text-[15px] font-semibold text-ink underline decoration-purple-strong/30 underline-offset-4 transition hover:text-purple-strong"
                  >
                    {jobTitle(jobFilter)}
                    <span aria-hidden className="text-[15px]">↗</span>
                  </Link>
                  <p className="text-[14px] text-[#3A3A40]">{employerName(jobById.get(jobFilter))}</p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const roleApps = apps.filter((a) => a.jobId === jobFilter);
                    return [
                      { label: "Awaiting", count: roleApps.filter((a) => statusOf(a) === "Pending").length, f: "Awaiting Review" },
                      { label: "Approved", count: roleApps.filter((a) => statusOf(a) === "Approved").length, f: "Approved" },
                      { label: "Not approved", count: roleApps.filter((a) => statusOf(a) === "Rejected").length, f: "Not Approved" },
                    ];
                  })().map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => setFilter(s.f as AppFilter)}
                      className={`rounded-md border px-2.5 py-1 text-left transition ${
                        filter === s.f
                          ? "border-purple-strong bg-purple-strong text-white"
                          : "border-[#BDBDB5] bg-white text-ink hover:border-purple-strong/30"
                      }`}
                    >
                      <p className="text-[9px] font-semibold uppercase tracking-[0.1em] opacity-80">{s.label}</p>
                      <p className="text-[15px] font-bold leading-tight">{s.count}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] px-5 py-4">
            {appFilters.map((f) => {
              const inHistory = f === "History" && (filter === "History" || filter === "Approved" || filter === "Not Approved");
              const on = f === "History" ? inHistory : filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  aria-pressed={on}
                  className={`min-h-10 rounded-full border px-3.5 text-[14px] font-bold transition ${
                    on
                      ? "border-[#0B2160] bg-[#0B2160] text-white"
                      : "border-[#BDBDB5] bg-white text-[#0B2160] hover:border-[#0B2160]"
                  }`}
                >
                  {appFilterLabel[f] ?? f} <span className="opacity-75">{count(f)}</span>
                </button>
              );
            })}
            {filter === "History" || filter === "Approved" || filter === "Not Approved" ? (
              <>
                <span aria-hidden="true" className="mx-1 h-6 w-px bg-[#E3E3DE]" />
                {(
                  [
                    ["Approved", "Approved"],
                    ["Not Approved", "Rejected"],
                  ] as const
                ).map(([f, label]) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(filter === f ? "History" : f)}
                    aria-pressed={filter === f}
                    className={`min-h-10 rounded-full border px-3.5 text-[14px] font-bold transition ${
                      filter === f
                        ? "border-[#0B2160] bg-[#0B2160] text-white"
                        : "border-[#BDBDB5] bg-white text-[#0B2160] hover:border-[#0B2160]"
                    }`}
                  >
                    {label} <span className="opacity-75">{count(f)}</span>
                  </button>
                ))}
              </>
            ) : null}
          </div>

      <section aria-label="Filters" className="m-5 rounded-xl border border-[#E3E3DE] bg-white px-5 py-[18px]">
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(170px,100%),1fr))]">
          <div className="[grid-column:1/-1]">
            <label htmlFor="ap-q" className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Search</label>
            <input
              id="ap-q"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email, role or company applied to"
              className="min-h-11 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[16px] text-[#0B2160] outline-none placeholder:text-[#7A7F99] focus:border-[#0B2160]"
            />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Role</label>
            <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
              <option value="All">Any</option>
              {jobFilterOptions.map((id) => (
                <option key={id} value={id}>
                  {jobTitle(id)} ({byStatus.filter((a) => a.jobId === id).length})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Location</label>
            <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
              <option value="All">Any</option>
              {appLocations.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Experience</label>
            <select value={expFilter} onChange={(e) => setExpFilter(e.target.value)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
              <option value="All">Any</option>
              {appExperience.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Your rating</label>
            <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
              <option value="All">Any</option>
              <option value="4">4+ stars</option>
              <option value="3">3+ stars</option>
              <option value="0">Not rated</option>
            </select>
          </div>
{moreApp ? (
            <>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Salary expected</label>
            <select value={salaryFilter} onChange={(e) => setSalaryFilter(e.target.value)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
              <option value="All">Any</option>
              {appSalaries.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-[#0B2160]">Sort</label>
                <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="min-h-12 w-full rounded-[4px] border border-[#BDBDB5] bg-white px-3 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]">
                  {["Newest", "Oldest", "Candidate A-Z", "Job A-Z"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </>
          ) : null}
          <div className="flex min-w-0 items-end">
            <button
              type="button"
              onClick={() => setMoreApp((v) => !v)}
              aria-expanded={moreApp}
              className="min-h-11 w-full rounded-[4px] border border-dashed border-[#0B2160] bg-white px-3 text-[14px] font-extrabold text-[#0B2160]"
            >
              {moreApp ? "Fewer filters" : "More filters"}
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex min-h-10 cursor-pointer items-center gap-2.5 text-[15px] font-semibold text-[#0B2160]">
            <input type="checkbox" checked={pitchOnly} onChange={(e) => setPitchOnly(e.target.checked)} className="h-5 w-5 accent-[#0B2160]" />
            Pitch recorded
          </label>
          <label className="flex min-h-10 cursor-pointer items-center gap-2.5 text-[15px] font-semibold text-[#0B2160]">
            <input type="checkbox" checked={groupByJob} onChange={(e) => setGroupByJob(e.target.checked)} className="h-5 w-5 accent-[#0B2160]" />
            Group by job
          </label>
          <span className="ml-auto text-[14px] text-[#3A3A40]">
            <strong className="text-[#0B2160]">{rows.length}</strong> candidates match
          </span>
        </div>
      </section>

      {chosen.length ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-[#E3E3DE] bg-purple-soft/60 px-3 py-1.5">
          <p className="text-[14px] font-semibold text-purple-strong">
            {chosen.length} selected
          </p>
          <button
            type="button"
            onClick={() => bulk("Approved")}
            className="rounded-md bg-purple-strong px-2.5 py-1 text-[15px] font-bold uppercase tracking-[0.12em] text-white hover:bg-purple-mid"
          >
            Approve all
          </button>
          <button
            type="button"
            onClick={() => bulk("Rejected")}
            className="rounded-md border border-tag-red/30 bg-white px-2.5 py-1 text-[15px] font-bold uppercase tracking-[0.12em] text-tag-red hover:bg-tag-red-soft"
          >
            Not approve all
          </button>
          <button
            type="button"
            onClick={() => setPicked([])}
            className="text-[15px] font-semibold text-[#5A5A60] underline hover:text-ink"
          >
            Clear
          </button>
        </div>
      ) : null}

      {!rows.length ? (
        <Empty>No applications here.</Empty>
      ) : (
        <div className="overflow-x-auto">
          {!groupByJob ? (
            <PaginationBar
              total={rows.length}
              page={listPage}
              totalPages={listTotalPages}
              pageSize={listPageSize}
              onPageChange={setListPage}
              onPageSizeChange={setListPageSize}
              itemLabel="applications"
              className="border-b border-[#E3E3DE]"
            />
          ) : null}
          <table className="w-full min-w-[900px] border-collapse text-left text-[15px]">
            <thead>
              <tr className="text-left font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#5A5A60]">
                <th scope="col" className="w-7 border-b border-[#E3E3DE] py-3 pl-5">
                  <input
                    type="checkbox"
                    checked={allPicked}
                    onChange={toggleAll}
                    aria-label="Select all applications"
                    className="h-5 w-5 accent-[#0B2160]"
                  />
                </th>
                {["Candidate", "Roles wanted and experience", "Pitch", "Rating", "Decision", "Role"].map((h) => (
                  <th key={h} scope="col" className="border-b border-[#E3E3DE] px-4 py-3 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.id || "all"}>
                  {groupByJob ? (
                    <tr>
                      <td colSpan={7} className="bg-[#FAFAF8] px-5 py-2.5">
                        <p className="font-mono text-[12px] font-medium uppercase tracking-[0.12em] text-[#0B2160]">
                          {jobTitle(g.id)} · {employerName(jobById.get(g.id))} · {g.rows.length}
                        </p>
                      </td>
                    </tr>
                  ) : null}
                  {g.rows.map((a) => {
                    const d = statusOf(a);
                    const job = jobById.get(a.jobId);
                    const picked_ = picked.includes(a.id);
                    return (
                      <tr key={a.id} className={picked_ ? "bg-[#EEF1F8]" : ""}>
                        <td className="border-b border-[#EFEFEA] py-3.5 pl-5 align-top">
                          <input
                            type="checkbox"
                            checked={picked_}
                            onChange={() => toggle(a.id)}
                            aria-label={`Select ${a.name}`}
                            className="mt-0.5 h-5 w-5 accent-[#0B2160]"
                          />
                        </td>
                        <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                          <div className="flex items-center gap-3">
                            <span
                              aria-hidden
                              className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full bg-[#0B2160] text-[13px] font-extrabold text-white"
                            >
                              {initialsOf(a.name)}
                            </span>
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => setOpenId(a.id)}
                                className="text-left font-extrabold text-[#0B2160] hover:underline"
                              >
                                {a.name}
                              </button>
                              <p className="text-[14px] text-[#3A3A40]">{a.location || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                          <p className="text-[14px] text-[#3A3A40]">{a.years || "—"}</p>
                          <p className="text-[14px] text-[#5A5A60]">{a.email || ""}</p>
                        </td>
                        <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                          <PitchPlayer has={a.hasPitch} url={a.audioUrl} />
                        </td>
                        <td className="whitespace-nowrap border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                          <RatingSelect
                            value={ratingOf(admin.ratings, appKeyOf(a))}
                            onChange={(n) => onRate(appKeyOf(a), a.name, n)}
                          />
                        </td>
                        <td className="whitespace-nowrap border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                          {d === "Pending" ? (
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => onDecide(a, "Approved")}
                                className="min-h-10 rounded-[4px] bg-[#FF5A3C] px-3.5 text-[14px] font-extrabold text-[#0B2160]"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => onDecide(a, "Rejected")}
                                className="min-h-10 rounded-[4px] border-2 border-[#0B2160] bg-white px-3 text-[14px] font-extrabold text-[#0B2160]"
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Pill text={d === "Approved" ? "Approved" : "Rejected"} tone={decisionStyles[d]} />
                              <button
                                type="button"
                                onClick={() => onDecide(a, "Pending")}
                                className="min-h-8 px-1 text-[13px] font-bold text-[#0B2160] underline underline-offset-[3px]"
                              >
                                Undo
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="max-w-[240px] border-b border-[#EFEFEA] px-4 py-3.5 pr-5 align-top text-[14px] text-[#3A3A40]">
                          <p className="font-bold text-[#0B2160]">{jobTitle(a.jobId)}</p>
                          <p>
                            {employerName(job)}
                            {job?.ref ? ` · ${job.ref}` : ""} · Applied {a.appliedAt}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
          {!groupByJob ? (
            <PaginationBar
              total={rows.length}
              page={listPage}
              totalPages={listTotalPages}
              pageSize={listPageSize}
              onPageChange={setListPage}
              onPageSizeChange={setListPageSize}
              itemLabel="applications"
              className="border-t border-[#E3E3DE]"
            />
          ) : null}
        </div>
      )}
        </>
      )}




      {open ? (
        <ApplicationReview
          a={open}
          decision={statusOf(open)}
          jobTitle={jobTitle(open.jobId)}
          employer={employerName(jobById.get(open.jobId))}
          onClose={() => setOpenId(null)}
          onDecide={(d) => onDecide(open, d)}
          rating={ratingOf(admin.ratings, appKeyOf(open))}
          onRate={(n) => onRate(appKeyOf(open), open.name, n)}
        />
      ) : null}
    </>
  );
}

/** Review panel: the profile, the voice assessment and the CV in one place. */
function ApplicationReview({
  a,
  decision,
  jobTitle,
  employer,
  onClose,
  onDecide,
  rating,
  onRate,
}: {
  a: Application;
  decision: Decision;
  jobTitle: string;
  employer: string;
  onClose: () => void;
  onDecide: (d: Decision) => void;
  rating: number;
  onRate: (n: number) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/45 p-0 sm:p-6">
      <div className="flex h-full w-full max-w-[560px] flex-col overflow-y-auto bg-white sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-purple-strong/10 px-6 py-5">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/40">Application review</p>
            <p className="mt-1.5 truncate text-[18px] font-semibold text-ink">{a.name}</p>
            <p className="mt-1 truncate text-[13px] text-ink/55">
              {jobTitle} · {employer} · Applied {a.appliedAt}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={btnGhost}>
            Close
          </button>
        </div>

        <div className="grid gap-5 px-6 py-6">
          <div className="flex items-center gap-2.5">
            <Pill text={decisionLabels[decision]} tone={decisionStyles[decision]} />
            <div className="ml-auto flex items-center gap-2">
              <p className="text-[11px] font-semibold text-ink/50">Rating</p>
              <div className="w-[110px]">
                <RatingSelect value={rating} onChange={onRate} compact={false} />
              </div>
            </div>
          </div>

          <div className={`${panel} px-5 py-4`}>
            <p className={label}>Profile</p>
            <dl className="mt-3 grid gap-2 text-[13px]">
              {[
                ["Email", a.email],
                ["Phone", a.phone],
                ["Location", a.location],
                ["Experience", a.years],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="text-ink/45">{k}</dt>
                  <dd className="truncate font-semibold text-ink/80">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className={`${panel} px-5 py-4`}>
            <p className={label}>Voice assessment</p>
            <p className="mt-1.5 text-[12.5px] text-ink/50">{a.voiceAnswers}/3 answers recorded</p>
            <div className="mt-3">
              <PitchPlayer has={a.hasPitch} url={a.audioUrl} />
            </div>
          </div>

          <div className={`${panel} px-5 py-4`}>
            <p className={label}>CV</p>
            <p className="mt-2 truncate text-[14px] font-semibold text-ink">{a.cvName || "No CV uploaded"}</p>
          </div>
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-purple-strong/10 px-6 py-5">
          <button
            type="button"
            onClick={() => onDecide("Approved")}
            className={decision === "Approved" ? `${btn} bg-tag-green-soft text-tag-green` : btnSolid}
          >
            {decision === "Approved" ? "Approved" : "Approve"}
          </button>
          <button
            type="button"
            onClick={() => onDecide("Rejected")}
            className={
              decision === "Rejected" ? `${btn} border border-tag-red/25 bg-tag-red-soft text-tag-red` : btnGhost
            }
          >
            {decision === "Rejected" ? "Not approved" : "Not approve"}
          </button>
        </div>
      </div>
    </div>
  );
}


function MatchingTab({
  candidates,
  approved,
  jobs,
  candidateKey,
  jobId,
  setCandidateKey,
  setJobId,
  employerName,
  distributions,
  onPutForward,
  candidateByKey,
}: {
  candidates: CandidateRecord[];
  approved: CandidateRecord[];
  jobs: EmployerJob[];
  candidateKey: string;
  jobId: string;
  setCandidateKey: (k: string) => void;
  setJobId: (id: string) => void;
  employerName: (j?: EmployerJob) => string;
  distributions: Distribution[];
  onPutForward: (c: CandidateRecord, jobIds: string[]) => void;
  candidateByKey: Map<string, CandidateRecord>;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const candidate = candidateByKey.get(candidateKey);
  const job = jobs.find((j) => j.id === jobId);

  const togglePick = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const jobMatches = candidate
    ? jobs
        .map((j) => ({
          job: j,
          score: matchScore(
            { years: candidate.years, location: candidate.location, role: candidate.role, tags: candidate.tags },
            { years: j.seniority, location: j.location, role: j.title, tags: j.tags ?? [] },
          ),
        }))
        .sort((a, b) => b.score - a.score)
    : [];

  const candidateMatches = job
    ? approved
        .map((c) => ({
          candidate: c,
          score: matchScore(
            { years: c.years, location: c.location, role: c.role, tags: c.tags },
            { years: job.seniority, location: job.location, role: job.title, tags: job.tags ?? [] },
          ),
        }))
        .sort((a, b) => b.score - a.score)
    : [];

  return (
    <div className="grid gap-6 px-6 py-6">
      <div className="flex flex-wrap gap-2">
        {(["auto", "manual"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded-full border px-4 py-1.5 text-[12px] font-semibold transition ${
              mode === m
                ? "border-purple-strong bg-purple-strong text-white"
                : "border-purple-strong/15 text-ink/60 hover:border-purple-strong/30 hover:text-ink"
            }`}
          >
            {m === "auto" ? "Auto match everyone" : "Match one by one"}
          </button>
        ))}
      </div>

      {mode === "auto" ? (
        <AutoMatch
          approved={approved}
          jobs={jobs}
          distributions={distributions}
          employerName={employerName}
          onPutForward={onPutForward}
        />
      ) : (
        <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className={label}>Find matches for a candidate</p>
          <select
            value={candidateKey}
            onChange={(e) => {
              setCandidateKey(e.target.value);
              setPicked([]);
            }}
            className={`${input} mt-2`}
          >
            <option value="">Select a candidate</option>
            {candidates.map((c) => (
              <option key={c.key} value={c.key}>
                {c.name} · {c.role}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p className={label}>Find candidates for a role</p>
          <select
            value={jobId}
            onChange={(e) => {
              setJobId(e.target.value);
              setPicked([]);
            }}
            className={`${input} mt-2`}
          >
            <option value="">Select a role</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title} · {employerName(j)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {candidate ? (
        <div className="rounded-xl border border-purple-strong/10">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-strong/10 bg-[#fbfcfe] px-5 py-4">
            <div>
              <p className="text-[15px] font-semibold text-ink">Matches for {candidate.name}</p>
              <p className="mt-1 text-[13px] text-ink/55">
                {candidate.role} · {candidate.years} · {candidate.location}
              </p>
            </div>
            <button
              type="button"
              disabled={!picked.length}
              onClick={() => {
                onPutForward(candidate, picked);
                setPicked([]);
              }}
              className={`${btnSolid} disabled:cursor-not-allowed disabled:bg-ink/15`}
            >
              Put forward{picked.length ? ` (${picked.length})` : ""}
            </button>
          </div>
          {jobMatches.length ? (
            <ul className="divide-y divide-ink/10">
              {jobMatches.map(({ job: j, score }) => {
                const already = distributions.some((d) => d.candidateKey === candidate.key && d.jobId === j.id);
                return (
                  <li key={j.id} className="flex items-center gap-4 px-5 py-4">
                    <input
                      type="checkbox"
                      disabled={already}
                      checked={picked.includes(j.id)}
                      onChange={() => togglePick(j.id)}
                      aria-label={`Select ${j.title}`}
                      className="h-4 w-4 accent-[#10306b]"
                    />
                    <Score value={score} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-semibold text-ink">{j.title}</p>
                      <p className="mt-0.5 truncate text-[13px] text-ink/55">
                        {employerName(j)} · {j.location || "Location TBC"} · {j.status}
                      </p>
                    </div>
                    {already ? <Pill text="Already sent" tone={stageStyles["Put forward"]} /> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty>No roles to match against yet.</Empty>
          )}
        </div>
      ) : null}

      {job ? (
        <div className="rounded-xl border border-purple-strong/10">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-strong/10 bg-[#fbfcfe] px-5 py-4">
            <div>
              <p className="text-[15px] font-semibold text-ink">Candidates for {job.title}</p>
              <p className="mt-1 text-[13px] text-ink/55">
                {employerName(job)} · {job.location || "Location TBC"} · approved candidates only
              </p>
            </div>
            <button
              type="button"
              disabled={!picked.length}
              onClick={() => {
                for (const key of picked) {
                  const c = candidateByKey.get(key);
                  if (c) onPutForward(c, [job.id]);
                }
                setPicked([]);
              }}
              className={`${btnSolid} disabled:cursor-not-allowed disabled:bg-ink/15`}
            >
              Put forward{picked.length ? ` (${picked.length})` : ""}
            </button>
          </div>
          {candidateMatches.length ? (
            <ul className="divide-y divide-ink/10">
              {candidateMatches.map(({ candidate: c, score }) => {
                const already = distributions.some((d) => d.candidateKey === c.key && d.jobId === job.id);
                return (
                  <li key={c.key} className="flex items-center gap-4 px-5 py-4">
                    <input
                      type="checkbox"
                      disabled={already}
                      checked={picked.includes(c.key)}
                      onChange={() => togglePick(c.key)}
                      aria-label={`Select ${c.name}`}
                      className="h-4 w-4 accent-[#10306b]"
                    />
                    <Score value={score} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-semibold text-ink">{c.name}</p>
                      <p className="mt-0.5 truncate text-[13px] text-ink/55">
                        {c.role} · {c.years} · {c.location}
                      </p>
                    </div>
                    <PitchPlayer has={c.hasPitch} url={c.audioUrl} />
                    {already ? <Pill text="Already sent" tone={stageStyles["Put forward"]} /> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty>No approved candidates yet. Approve candidates first, then they show up here.</Empty>
          )}
        </div>
      ) : null}

      {!candidate && !job ? (
        <p className="rounded-lg border border-dashed border-purple-strong/15 px-5 py-10 text-center text-[14px] text-ink/50">
          Pick a candidate to find roles, or pick a role to find candidates.
        </p>
      ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Bulk matcher: scores every approved candidate against every live role, then
 * spreads them out so no single role hoovers up the whole batch. Tick the ones
 * you want and send them all in one press.
 */
function AutoMatch({
  approved,
  jobs,
  distributions,
  employerName,
  onPutForward,
}: {
  approved: CandidateRecord[];
  jobs: EmployerJob[];
  distributions: Distribution[];
  employerName: (j?: EmployerJob) => string;
  onPutForward: (c: CandidateRecord, jobIds: string[]) => void;
}) {
  const [minScore, setMinScore] = useState(70);
  const [perCandidate, setPerCandidate] = useState(1);
  const [spread, setSpread] = useState(true);
  const [skipped, setSkipped] = useState<string[]>([]);

  const liveJobs = useMemo(() => jobs.filter((j) => j.status === "Live"), [jobs]);

  const suggestions = useMemo(() => {
    if (!liveJobs.length || !approved.length) return [];
    const cap = spread ? Math.max(1, Math.ceil((approved.length * perCandidate) / liveJobs.length)) : Infinity;
    const load = new Map<string, number>();

    const scored = approved
      .map((c) => ({
        candidate: c,
        options: liveJobs
          .map((j) => ({
            job: j,
            score: matchScore(
              { years: c.years, location: c.location, role: c.role, tags: c.tags },
              { years: j.seniority, location: j.location, role: j.title, tags: j.tags ?? [] },
            ),
          }))
          .filter(
            (o) =>
              o.score >= minScore &&
              !distributions.some((d) => d.candidateKey === c.key && d.jobId === o.job.id),
          )
          .sort((a, b) => b.score - a.score),
      }))
      .filter((s) => s.options.length)
      .sort((a, b) => (b.options[0]?.score ?? 0) - (a.options[0]?.score ?? 0));

    const out: { key: string; candidate: CandidateRecord; job: EmployerJob; score: number }[] = [];
    for (const s of scored) {
      let taken = 0;
      for (const o of s.options) {
        if (taken >= perCandidate) break;
        if ((load.get(o.job.id) ?? 0) >= cap) continue;
        load.set(o.job.id, (load.get(o.job.id) ?? 0) + 1);
        out.push({ key: `${s.candidate.key}::${o.job.id}`, candidate: s.candidate, job: o.job, score: o.score });
        taken += 1;
      }
    }
    return out;
  }, [approved, liveJobs, distributions, minScore, perCandidate, spread]);

  const selected = suggestions.filter((s) => !skipped.includes(s.key));
  const roleCounts = new Map<string, number>();
  for (const s of selected) roleCounts.set(s.job.id, (roleCounts.get(s.job.id) ?? 0) + 1);

  const sendAll = () => {
    const byCandidate = new Map<string, { c: CandidateRecord; jobIds: string[] }>();
    for (const s of selected) {
      const entry = byCandidate.get(s.candidate.key) ?? { c: s.candidate, jobIds: [] };
      entry.jobIds.push(s.job.id);
      byCandidate.set(s.candidate.key, entry);
    }
    for (const { c, jobIds } of byCandidate.values()) onPutForward(c, jobIds);
    setSkipped([]);
  };

  return (
    <div className="grid gap-5">
      <div className="grid gap-4 rounded-xl border border-purple-strong/10 bg-[#fbfcfe] px-5 py-4 sm:grid-cols-3">
        <div>
          <p className={label}>Minimum match score</p>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={55}
              max={95}
              step={5}
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="w-full accent-[#10306b]"
            />
            <span className="w-9 text-[13px] font-semibold text-ink">{minScore}</span>
          </div>
        </div>
        <div>
          <p className={label}>Roles per candidate</p>
          <div className="mt-2 flex gap-2">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPerCandidate(n)}
                className={`rounded-md border px-3 py-1.5 text-[12px] font-semibold transition ${
                  perCandidate === n
                    ? "border-purple-strong bg-purple-strong text-white"
                    : "border-purple-strong/15 text-ink/60 hover:border-purple-strong/30 hover:text-ink"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className={label}>Spread evenly across roles</p>
          <button
            type="button"
            onClick={() => setSpread((s) => !s)}
            className={`mt-2 rounded-md border px-3 py-1.5 text-[12px] font-semibold transition ${
              spread
                ? "border-purple-strong bg-purple-strong text-white"
                : "border-purple-strong/15 text-ink/60 hover:border-purple-strong/30 hover:text-ink"
            }`}
          >
            {spread ? "On" : "Off"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-purple-strong/10">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-strong/10 bg-[#fbfcfe] px-5 py-4">
          <div>
            <p className="text-[15px] font-semibold text-ink">
              {selected.length} match{selected.length === 1 ? "" : "es"} ready
            </p>
            <p className="mt-1 text-[13px] text-ink/55">
              {approved.length} approved candidates · {liveJobs.length} live roles · untick anything you do not want
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setSkipped([])} className={btnGhost}>
              Select all
            </button>
            <button
              type="button"
              onClick={() => setSkipped(suggestions.map((s) => s.key))}
              className={btnGhost}
            >
              Clear
            </button>
            <button
              type="button"
              disabled={!selected.length}
              onClick={sendAll}
              className={`${btnSolid} disabled:cursor-not-allowed disabled:bg-ink/15`}
            >
              Put forward {selected.length || ""}
            </button>
          </div>
        </div>

        {suggestions.length ? (
          <ul className="divide-y divide-ink/10">
            {suggestions.map((s) => (
              <li key={s.key} className="grid grid-cols-[24px_56px_1fr_1fr] items-center gap-4 px-5 py-4">
                <input
                  type="checkbox"
                  checked={!skipped.includes(s.key)}
                  onChange={() =>
                    setSkipped((p) => (p.includes(s.key) ? p.filter((x) => x !== s.key) : [...p, s.key]))
                  }
                  aria-label={`Match ${s.candidate.name} to ${s.job.title}`}
                  className="h-4 w-4 accent-[#10306b]"
                />
                <Score value={s.score} />
                <div className="min-w-0">
                  <p className="truncate text-[14px] font-semibold text-ink">{s.candidate.name}</p>
                  <p className="mt-0.5 truncate text-[13px] text-ink/55">
                    {s.candidate.years} · {s.candidate.location || "Location TBC"}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[14px] font-semibold text-ink">{s.job.title}</p>
                  <p className="mt-0.5 truncate text-[13px] text-ink/55">
                    {employerName(s.job)} · {s.job.location || "Location TBC"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>
            Nothing to match yet. Approve candidates, keep roles live, or drop the minimum score.
          </Empty>
        )}
      </div>

      {roleCounts.size ? (
        <div className="rounded-xl border border-purple-strong/10 px-5 py-4">
          <p className={label}>How the batch spreads</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[...roleCounts.entries()].map(([id, n]) => {
              const j = liveJobs.find((x) => x.id === id);
              return (
                <span
                  key={id}
                  className="rounded-full border border-purple-strong/12 bg-[#fbfcfe] px-3 py-1.5 text-[12px] font-semibold text-ink/70"
                >
                  {j?.title ?? "Role"} · {n}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}


function DistributionTab({
  rows,
  onStatus,
  q,
}: {
  rows: Distribution[];
  onStatus: (id: string, status: DistributionStatus) => void;
  q: string;
}) {
  const { page, setPage, pageSize, setPageSize, totalPages, resetPage } = usePagination(rows.length, 20);

  useEffect(() => {
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, pageSize]);

  if (!rows.length)
    return <Empty>Nobody has been put forward yet. Use Matching to send candidates to employers.</Empty>;

  const pageRows = paginate(rows, pageSize, page);

  return (
    <div>
      <div className="divide-y divide-ink/10">
        {pageRows.map((d) => (
          <div key={d.id} className="grid gap-3 px-6 py-4 lg:grid-cols-[1fr_1fr_1fr_auto] lg:items-center">
            <p className="text-[14px] font-semibold text-ink">{d.candidateName}</p>
            <p className="text-[13px] text-ink/60">{d.jobTitle}</p>
            <p className="text-[13px] text-ink/60">
              {d.employer} · sent {dayOf(d.sentAt)}
            </p>
            <div className="flex items-center gap-3">
              <Pill text={d.status} tone={distributionStyles[d.status]} />
              <select
                value={d.status}
                onChange={(e) => onStatus(d.id, e.target.value as DistributionStatus)}
                aria-label={`Update status for ${d.candidateName}`}
                className="h-10 rounded-xl border border-purple-strong/12 bg-white px-3 text-[13px] font-semibold text-ink/70"
              >
                {distributionStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
      <PaginationBar
        total={rows.length}
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="candidates"
        className="border-t border-ink/10"
      />
    </div>
  );
}

const auditAreas = ["Applications", "Candidates", "Employers", "Jobs", "Leads", "Payments"] as const;
type AuditArea = (typeof auditAreas)[number];
const auditAreaChip: Record<AuditArea, string> = {
  Applications: "bg-[#FFE1D6] text-[#8A2A12]",
  Candidates: "bg-[#DCE6FF] text-[#0B2160]",
  Employers: "bg-[#0B2160] text-white",
  Jobs: "bg-[#DDF2E3] text-[#1F6B3A]",
  Leads: "bg-[#EFE8FF] text-[#4B2A8A]",
  Payments: "bg-[#FFE9A8] text-[#6B4A00]",
};

/** Log entries only carry free text, so the area is read off the wording. */
function auditAreaOf(message: string): AuditArea {
  const m = message.toLowerCase();
  if (/refund|payment|paid|unlock/.test(m)) return "Payments";
  if (/\blead\b|leads|booking/.test(m)) return "Leads";
  if (/invit|employer|company|account created/.test(m)) return "Employers";
  if (/application|^(approved|marked not approved|reset): .+ for /.test(m)) return "Applications";
  if (/candidate|profile|rated|rating|badge|put .* forward|assigned|unassigned|distributed|person|people/.test(m))
    return "Candidates";
  return "Jobs";
}

function ActivityTab({ entries }: { entries: AdminStore["activity"] }) {
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const needle = q.trim().toLowerCase();
  const rows = entries
    .map((e) => ({ e, area: auditAreaOf(e.message) }))
    .filter((r) => (!area || r.area === area) && (!needle || r.e.message.toLowerCase().includes(needle)));
  const field =
    "min-h-11 rounded-[4px] border border-[#BDBDB5] bg-white px-3.5 text-[15px] text-[#0B2160] outline-none focus:border-[#0B2160]";
  return (
    <>
      <div className="mt-5 flex flex-wrap items-center gap-2.5">
        <label htmlFor="al-q" className="sr-only">
          Search the log
        </label>
        <input
          id="al-q"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a role, candidate or action"
          className={`${field} min-w-[200px] flex-[1_1_260px]`}
        />
        <label htmlFor="al-area" className="sr-only">
          Area
        </label>
        <select id="al-area" value={area} onChange={(e) => setArea(e.target.value)} className={`${field} px-2.5`}>
          <option value="">All areas</option>
          {auditAreas.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
      </div>
      <div className="mt-3 overflow-x-auto rounded-xl border border-[#E3E3DE] bg-white">
        <table className="w-full min-w-[820px] border-collapse text-left text-[15px]">
          <thead>
            <tr className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[#5A5A60]">
              {["When", "Area", "What happened"].map((h) => (
                <th key={h} scope="col" className="border-b border-[#E3E3DE] px-4 py-3 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ e, area: a }) => (
              <tr key={e.id}>
                <td className="whitespace-nowrap border-b border-[#EFEFEA] px-4 py-3.5 align-top font-mono text-[13px] text-[#3A3A40]">
                  {dayOf(e.at)} {timeOf(e.at)}
                </td>
                <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                  <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-[3px] text-[12px] font-extrabold ${auditAreaChip[a]}`}>
                    {a}
                  </span>
                </td>
                <td className="border-b border-[#EFEFEA] px-4 py-3.5 align-top">
                  {e.actor === "System" ? `Automatic: ${e.message}` : e.message}
                  {e.actor && e.actor !== "System" && e.actor !== "Admin" ? (
                    <div className="mt-0.5 text-[13px] text-[#5A5A60]">By {e.actor}</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="px-4 py-6 text-center text-[#3A3A40]">
            {entries.length ? "Nothing matches these filters." : "Nothing logged yet. Every approval, match and put forward lands here."}
          </div>
        ) : null}
      </div>
      <p className="mt-2.5 text-[13px] text-[#5A5A60]">Showing the latest {rows.length} entries.</p>
    </>
  );
}
